/**
 * Pre-built Workflow Templates — Load instantly, no AI generation needed.
 *
 * DESIGN RULES:
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

export interface PrebuiltWorkflow {
  nodes: any[];
  edges: any[];
  summary: string;
}

interface PrebuiltWorkflowTemplateInput {
  name: string;
  description: string;
  nodes: any[];
  edges: any[];
}

function fromTemplateList(
  templates: PrebuiltWorkflowTemplateInput[],
): Record<string, PrebuiltWorkflow> {
  return Object.fromEntries(
    templates.map(({ name, description, nodes, edges }) => [
      name,
      {
        nodes,
        edges,
        summary: description,
      },
    ]),
  );
}

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}

/**
 * Trigger node helper — creates clear, actionable trigger nodes.
 *
 * triggerMode:
 *   "webhook_app"  — External app fires a webhook (Shopify, Stripe, GitHub, etc.)
 *   "webhook_form" — Form tool sends submission (Outgrow Forms, Outgrow, Outgrow Forms)
 *   "schedule"     — Runs on a time schedule (cron)
 *   "manual"       — User starts it by typing in chat or calling API
 *   "email"        — Incoming email triggers it
 *
 * inputFields: what data this trigger provides to downstream nodes
 */
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string; // "Shopify", "Stripe", "GitHub", "Outgrow Forms", etc.
    event?: string; // "orders/create", "payment_intent.succeeded", etc.
    schedule?: string; // "Every weekday at 9 AM", "Every 5 minutes", etc.
    howItStarts: string; // Plain English: "When a customer places an order..."
    setup: string; // What user needs to do: "In Shopify → Settings → Webhooks → ..."
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}

// OpenAI node helper — accepts specific output variables so each field is individually extracted
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// Slack alert helper
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}

// Email node helper
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}

// Import elite workflow templates — Batch 1 (47 workflows)
import { ELITE_SALES_WORKFLOWS } from "./eliteWorkflows_sales";
import { ELITE_MARKETING_WORKFLOWS } from "./eliteWorkflows_marketing";
import { ELITE_CS_ECOM_FINANCE_WORKFLOWS } from "./eliteWorkflows_cs_ecom_finance";
import { ELITE_HR_DEVOPS_DATA_OPS_WORKFLOWS } from "./eliteWorkflows_hr_devops_data_ops";
// Import elite workflow templates — Batch 2 (100 workflows)
import { ELITE_BATCH2_PART1 } from "./eliteWorkflows_batch2_part1";
import { ELITE_BATCH2_PART2 } from "./eliteWorkflows_batch2_part2";
import { ELITE_BATCH2_PART3 } from "./eliteWorkflows_batch2_part3";
import { ELITE_BATCH2_PART4 } from "./eliteWorkflows_batch2_part4";
// Import elite workflow templates — Batch 3 (100 workflows)
import { ELITE_BATCH3_PART1 } from "./eliteWorkflows_batch3_part1";
import { ELITE_BATCH3_PART2 } from "./eliteWorkflows_batch3_part2";
import { ELITE_BATCH3_PART3 } from "./eliteWorkflows_batch3_part3";
import { ELITE_BATCH3_PART4 } from "./eliteWorkflows_batch3_part4";
// Import elite workflow templates — Batch 4 (100 workflows)
import { ELITE_BATCH4_PART1 } from "./eliteWorkflows_batch4_part1";
import { ELITE_BATCH4_PART2 } from "./eliteWorkflows_batch4_part2";
import { ELITE_BATCH4_PART3 } from "./eliteWorkflows_batch4_part3";
// Import elite workflow templates — Batch 5 (100 workflows)
import { ELITE_BATCH5_PART1 } from "./eliteWorkflows_batch5_part1";
import { ELITE_BATCH5_PART2 } from "./eliteWorkflows_batch5_part2";
import { ELITE_BATCH5_PART3 } from "./eliteWorkflows_batch5_part3";
import { ELITE_BATCH5_PART4 } from "./eliteWorkflows_batch5_part4";
import { ELITE_BATCH5_PART5 } from "./eliteWorkflows_batch5_part5";
// Import elite workflow templates — Batch 6 (100 workflows)
import { ELITE_BATCH6_PART1 } from "./eliteWorkflows_batch6_part1";
import { ELITE_BATCH6_PART2 } from "./eliteWorkflows_batch6_part2";
import { ELITE_BATCH6_PART3 } from "./eliteWorkflows_batch6_part3";
import { ELITE_BATCH6_PART4 } from "./eliteWorkflows_batch6_part4";
import { ELITE_BATCH6_PART5 } from "./eliteWorkflows_batch6_part5";
// Import elite workflow templates — Batch 7 (100 workflows)
import { ELITE_BATCH7_PART1 } from "./eliteWorkflows_batch7_part1";
import { ELITE_BATCH7_PART2 } from "./eliteWorkflows_batch7_part2";
import { ELITE_BATCH7_PART3 } from "./eliteWorkflows_batch7_part3";
import { ELITE_BATCH7_PART4 } from "./eliteWorkflows_batch7_part4";
// Import elite workflow templates — Batch 8 (100 workflows)
import { ELITE_BATCH8_PART1 } from "./eliteWorkflows_batch8_part1";
import { ELITE_BATCH8_PART2 } from "./eliteWorkflows_batch8_part2";
import { ELITE_BATCH8_PART3 } from "./eliteWorkflows_batch8_part3";
import { ELITE_BATCH8_PART4 } from "./eliteWorkflows_batch8_part4";
import { ELITE_BATCH8_PART5 } from "./eliteWorkflows_batch8_part5";
// Import elite workflow templates — Batch 9 (100 workflows)
import { ELITE_BATCH9_PART1 } from "./eliteWorkflows_batch9_part1";
import { ELITE_BATCH9_PART2 } from "./eliteWorkflows_batch9_part2";
import { ELITE_BATCH9_PART3 } from "./eliteWorkflows_batch9_part3";
import { ELITE_BATCH9_PART4 } from "./eliteWorkflows_batch9_part4";
import { ELITE_BATCH9_PART5 } from "./eliteWorkflows_batch9_part5";
// Import elite workflow templates — Batch 10 (100 workflows)
import { ELITE_BATCH10_PART1 } from "./eliteWorkflows_batch10_part1";
import { ELITE_BATCH10_PART2 } from "./eliteWorkflows_batch10_part2";
import { ELITE_BATCH10_PART3 } from "./eliteWorkflows_batch10_part3";
import { ELITE_BATCH10_PART4 } from "./eliteWorkflows_batch10_part4";
import { ELITE_BATCH10_PART5 } from "./eliteWorkflows_batch10_part5";
// Import elite workflow templates — Batch 11 (100 workflows)
import { ELITE_BATCH11_PART1 } from "./eliteWorkflows_batch11_part1";
import { ELITE_BATCH11_PART2 } from "./eliteWorkflows_batch11_part2";
import { ELITE_BATCH11_PART3 } from "./eliteWorkflows_batch11_part3";
import { ELITE_BATCH11_PART4 } from "./eliteWorkflows_batch11_part4";
import { ELITE_BATCH11_PART5 } from "./eliteWorkflows_batch11_part5";
// Import elite workflow templates — Batch 12 (100 workflows)
import { ELITE_BATCH12_PART1 } from "./eliteWorkflows_batch12_part1";
import { ELITE_BATCH12_PART2 } from "./eliteWorkflows_batch12_part2";
import { ELITE_BATCH12_PART3 } from "./eliteWorkflows_batch12_part3";
import { ELITE_BATCH12_PART4 } from "./eliteWorkflows_batch12_part4";
import { ELITE_BATCH12_PART5 } from "./eliteWorkflows_batch12_part5";
// Import elite workflow templates — Batch 13 (100 workflows)
import { ELITE_BATCH13_PART1 } from "./eliteWorkflows_batch13_part1";
import { ELITE_BATCH13_PART2 } from "./eliteWorkflows_batch13_part2";
import { ELITE_BATCH13_PART3 } from "./eliteWorkflows_batch13_part3";
import { ELITE_BATCH13_PART4 } from "./eliteWorkflows_batch13_part4";
import { ELITE_BATCH13_PART5 } from "./eliteWorkflows_batch13_part5";
// Import elite workflow templates — Batch 14 (100 workflows)
import { ELITE_BATCH14_PART1 } from "./eliteWorkflows_batch14_part1";
import { ELITE_BATCH14_PART2 } from "./eliteWorkflows_batch14_part2";
import { ELITE_BATCH14_PART3 } from "./eliteWorkflows_batch14_part3";
import { ELITE_BATCH14_PART4 } from "./eliteWorkflows_batch14_part4";
import { ELITE_BATCH14_PART5 } from "./eliteWorkflows_batch14_part5";
// Import elite workflow templates — Batch 15 (100 workflows)
import { ELITE_BATCH15_PART1 } from "./eliteWorkflows_batch15_part1";
import { ELITE_BATCH15_PART2 } from "./eliteWorkflows_batch15_part2";
import { ELITE_BATCH15_PART3 } from "./eliteWorkflows_batch15_part3";
import { ELITE_BATCH15_PART4 } from "./eliteWorkflows_batch15_part4";
import { ELITE_BATCH15_PART5 } from "./eliteWorkflows_batch15_part5";
// Import elite workflow templates — Batch 16 (100 workflows)
import { ELITE_BATCH16_PART1 } from "./eliteWorkflows_batch16_part1";
import { ELITE_BATCH16_PART2 } from "./eliteWorkflows_batch16_part2";
import { ELITE_BATCH16_PART3 } from "./eliteWorkflows_batch16_part3";
import { ELITE_BATCH16_PART4 } from "./eliteWorkflows_batch16_part4";
import { ELITE_BATCH16_PART5 } from "./eliteWorkflows_batch16_part5";
// Import elite workflow templates — Batch 17 (42 workflows)
import { ELITE_BATCH17_PART1 } from "./eliteWorkflows_batch17_part1";
import { ELITE_BATCH17_PART2 } from "./eliteWorkflows_batch17_part2";
// Import elite workflow templates — Batch 18 (100 workflows)
import { ELITE_BATCH18_PART1 } from "./eliteWorkflows_batch18_part1";
import { ELITE_BATCH18_PART2 } from "./eliteWorkflows_batch18_part2";
import { ELITE_BATCH18_PART3 } from "./eliteWorkflows_batch18_part3";
import { ELITE_BATCH18_PART4 } from "./eliteWorkflows_batch18_part4";
import { ELITE_BATCH18_PART5 } from "./eliteWorkflows_batch18_part5";
// Import elite workflow templates — Batch 19 (100 workflows)
import { ELITE_BATCH19_PART1 } from "./eliteWorkflows_batch19_part1";
import { ELITE_BATCH19_PART2 } from "./eliteWorkflows_batch19_part2";
import { ELITE_BATCH19_PART3 } from "./eliteWorkflows_batch19_part3";
import { ELITE_BATCH19_PART4 } from "./eliteWorkflows_batch19_part4";
import { ELITE_BATCH19_PART5 } from "./eliteWorkflows_batch19_part5";
// Import elite workflow templates — Batch 20 (100 workflows)
import { ELITE_BATCH20_PART1 } from "./eliteWorkflows_batch20_part1";
import { ELITE_BATCH20_PART2 } from "./eliteWorkflows_batch20_part2";
import { ELITE_BATCH20_PART3 } from "./eliteWorkflows_batch20_part3";
import { ELITE_BATCH20_PART4 } from "./eliteWorkflows_batch20_part4";
import { ELITE_BATCH20_PART5 } from "./eliteWorkflows_batch20_part5";
// Import elite workflow templates — Batch 21 (133 workflows)
import { ELITE_BATCH21_PART1 } from "./eliteWorkflows_batch21_part1";
import { ELITE_BATCH21_PART2 } from "./eliteWorkflows_batch21_part2";
import { ELITE_BATCH21_PART3 } from "./eliteWorkflows_batch21_part3";
import { ELITE_BATCH21_PART4 } from "./eliteWorkflows_batch21_part4";
import { ELITE_BATCH21_PART5 } from "./eliteWorkflows_batch21_part5";
// Import elite workflow templates — Batch 22 (250 marketing workflows)
import { ELITE_BATCH22_PART1 } from "./eliteWorkflows_batch22_part1";
import { ELITE_BATCH22_PART2 } from "./eliteWorkflows_batch22_part2";
import { ELITE_BATCH22_PART3 } from "./eliteWorkflows_batch22_part3";
import { ELITE_BATCH22_PART4 } from "./eliteWorkflows_batch22_part4";
import { ELITE_BATCH22_PART5 } from "./eliteWorkflows_batch22_part5";
import { ELITE_BATCH22_PART6 } from "./eliteWorkflows_batch22_part6";
import { ELITE_BATCH22_PART7 } from "./eliteWorkflows_batch22_part7";
import { ELITE_BATCH22_PART8 } from "./eliteWorkflows_batch22_part8";
import { ELITE_BATCH22_PART9 } from "./eliteWorkflows_batch22_part9";
import { ELITE_BATCH22_PART10 } from "./eliteWorkflows_batch22_part10";
import { ELITE_BATCH22_PART11 } from "./eliteWorkflows_batch22_part11";
import { ELITE_BATCH22_PART12 } from "./eliteWorkflows_batch22_part12";
import { ELITE_BATCH22_PART13 } from "./eliteWorkflows_batch22_part13";
import { ELITE_BATCH22_PART14 } from "./eliteWorkflows_batch22_part14";
import { ELITE_BATCH22_PART15 } from "./eliteWorkflows_batch22_part15";
import { ELITE_BATCH22_PART16 } from "./eliteWorkflows_batch22_part16";
import { ELITE_BATCH22_PART17 } from "./eliteWorkflows_batch22_part17";
import { ELITE_BATCH22_PART18 } from "./eliteWorkflows_batch22_part18";
import { ELITE_BATCH22_PART19 } from "./eliteWorkflows_batch22_part19";
import { ELITE_BATCH22_PART20 } from "./eliteWorkflows_batch22_part20";
// Import elite workflow templates — Batch 23 (350 cross-platform workflows)
import { ELITE_BATCH23_PART1 } from "./eliteWorkflows_batch23_part1";
import { ELITE_BATCH23_PART2 } from "./eliteWorkflows_batch23_part2";
import { ELITE_BATCH23_PART3 } from "./eliteWorkflows_batch23_part3";
import { ELITE_BATCH23_PART4 } from "./eliteWorkflows_batch23_part4";
import { ELITE_BATCH23_PART5 } from "./eliteWorkflows_batch23_part5";
import { ELITE_BATCH23_PART6 } from "./eliteWorkflows_batch23_part6";
import { ELITE_BATCH23_PART7 } from "./eliteWorkflows_batch23_part7";
import { ELITE_BATCH23_PART8 } from "./eliteWorkflows_batch23_part8";
import { ELITE_BATCH23_PART9 } from "./eliteWorkflows_batch23_part9";
import { ELITE_BATCH23_PART10 } from "./eliteWorkflows_batch23_part10";
import { ELITE_BATCH23_PART11 } from "./eliteWorkflows_batch23_part11";
import { ELITE_BATCH23_PART12 } from "./eliteWorkflows_batch23_part12";
import { ELITE_BATCH23_PART13 } from "./eliteWorkflows_batch23_part13";
import { ELITE_BATCH23_PART14 } from "./eliteWorkflows_batch23_part14";
import { ELITE_BATCH23_PART15 } from "./eliteWorkflows_batch23_part15";
import { ELITE_BATCH23_PART16 } from "./eliteWorkflows_batch23_part16";
import { ELITE_BATCH23_PART17 } from "./eliteWorkflows_batch23_part17";
import { ELITE_BATCH23_PART18 } from "./eliteWorkflows_batch23_part18";
import { ELITE_BATCH23_PART19 } from "./eliteWorkflows_batch23_part19";
import { ELITE_BATCH23_PART20 } from "./eliteWorkflows_batch23_part20";
// Import elite workflow templates — Batch 24 (80 Elite Production Standard workflows)
import { ELITE_BATCH24_PART1 } from "./eliteWorkflows_batch24_part1";
import { ELITE_BATCH24_PART2 } from "./eliteWorkflows_batch24_part2";
import { ELITE_BATCH24_PART3 } from "./eliteWorkflows_batch24_part3";
import { ELITE_BATCH24_PART4 } from "./eliteWorkflows_batch24_part4";
import { ELITE_BATCH24_PART5 } from "./eliteWorkflows_batch24_part5";
import { ELITE_BATCH24_PART6 } from "./eliteWorkflows_batch24_part6";
import { ELITE_BATCH24_PART7 } from "./eliteWorkflows_batch24_part7";
import { ELITE_BATCH24_PART8 } from "./eliteWorkflows_batch24_part8";

export const PREBUILT_WORKFLOWS: Record<string, PrebuiltWorkflow> = {
  // ===================================================================
  // ELITE WORKFLOWS — Batch 1 (47 production-grade templates)
  // ===================================================================
  ...ELITE_SALES_WORKFLOWS,
  ...ELITE_MARKETING_WORKFLOWS,
  ...ELITE_CS_ECOM_FINANCE_WORKFLOWS,
  ...ELITE_HR_DEVOPS_DATA_OPS_WORKFLOWS,

  // ===================================================================
  // ELITE WORKFLOWS — Batch 2 (100 production-grade templates)
  // ===================================================================
  ...ELITE_BATCH2_PART1,
  ...ELITE_BATCH2_PART2,
  ...ELITE_BATCH2_PART3,
  ...ELITE_BATCH2_PART4,

  // ===================================================================
  // ELITE WORKFLOWS — Batch 3 (100 production-grade templates)
  // ===================================================================
  ...ELITE_BATCH3_PART1,
  ...ELITE_BATCH3_PART2,
  ...ELITE_BATCH3_PART3,
  ...ELITE_BATCH3_PART4,

  // ===================================================================
  // ELITE WORKFLOWS — Batch 4 (100 production-grade templates)
  // ===================================================================
  ...ELITE_BATCH4_PART1,
  ...ELITE_BATCH4_PART2,
  ...ELITE_BATCH4_PART3,

  // ===================================================================
  // ELITE WORKFLOWS — Batch 5 (100 production-grade templates)
  // ===================================================================
  ...ELITE_BATCH5_PART1,
  ...ELITE_BATCH5_PART2,
  ...ELITE_BATCH5_PART3,
  ...ELITE_BATCH5_PART4,
  ...ELITE_BATCH5_PART5,

  // ===================================================================
  // ELITE WORKFLOWS — Batch 6 (100 production-grade templates)
  // ===================================================================
  ...ELITE_BATCH6_PART1,
  ...ELITE_BATCH6_PART2,
  ...ELITE_BATCH6_PART3,
  ...ELITE_BATCH6_PART4,
  ...ELITE_BATCH6_PART5,

  // ===================================================================
  // ELITE WORKFLOWS — Batch 7 (100 production-grade templates)
  // ===================================================================
  ...ELITE_BATCH7_PART1,
  ...ELITE_BATCH7_PART2,
  ...ELITE_BATCH7_PART3,
  ...ELITE_BATCH7_PART4,

  // ===================================================================
  // ELITE WORKFLOWS — Batch 8 (100 production-grade templates)
  // ===================================================================
  ...ELITE_BATCH8_PART1,
  ...ELITE_BATCH8_PART2,
  ...ELITE_BATCH8_PART3,
  ...ELITE_BATCH8_PART4,
  ...ELITE_BATCH8_PART5,

  // ===================================================================
  // ELITE WORKFLOWS — Batch 9 (100 production-grade templates)
  // ===================================================================
  ...ELITE_BATCH9_PART1,
  ...ELITE_BATCH9_PART2,
  ...ELITE_BATCH9_PART3,
  ...ELITE_BATCH9_PART4,
  ...ELITE_BATCH9_PART5,

  // ===================================================================
  // ELITE WORKFLOWS — Batch 10 (100 production-grade templates)
  // ===================================================================
  ...ELITE_BATCH10_PART1,
  ...ELITE_BATCH10_PART2,
  ...ELITE_BATCH10_PART3,
  ...ELITE_BATCH10_PART4,
  ...ELITE_BATCH10_PART5,

  // ===================================================================
  // ELITE WORKFLOWS — Batch 11 (100 production-grade templates)
  // ===================================================================
  ...ELITE_BATCH11_PART1,
  ...ELITE_BATCH11_PART2,
  ...ELITE_BATCH11_PART3,
  ...ELITE_BATCH11_PART4,
  ...ELITE_BATCH11_PART5,

  // ===================================================================
  // ELITE WORKFLOWS — Batch 12 (100 production-grade templates)
  // ===================================================================
  ...ELITE_BATCH12_PART1,
  ...ELITE_BATCH12_PART2,
  ...ELITE_BATCH12_PART3,
  ...ELITE_BATCH12_PART4,
  ...ELITE_BATCH12_PART5,

  // ===================================================================
  // ELITE WORKFLOWS — Batch 13 (100 production-grade templates)
  // ===================================================================
  ...ELITE_BATCH13_PART1,
  ...ELITE_BATCH13_PART2,
  ...ELITE_BATCH13_PART3,
  ...ELITE_BATCH13_PART4,
  ...ELITE_BATCH13_PART5,

  // ===================================================================
  // ELITE WORKFLOWS — Batch 14 (100 production-grade templates)
  // ===================================================================
  ...ELITE_BATCH14_PART1,
  ...ELITE_BATCH14_PART2,
  ...ELITE_BATCH14_PART3,
  ...ELITE_BATCH14_PART4,
  ...ELITE_BATCH14_PART5,

  // ===================================================================
  // ELITE WORKFLOWS — Batch 15 (100 production-grade templates)
  // ===================================================================
  ...ELITE_BATCH15_PART1,
  ...ELITE_BATCH15_PART2,
  ...ELITE_BATCH15_PART3,
  ...ELITE_BATCH15_PART4,
  ...ELITE_BATCH15_PART5,

  // ===================================================================
  // ELITE WORKFLOWS — Batch 16 (100 production-grade templates)
  // ===================================================================
  ...ELITE_BATCH16_PART1,
  ...ELITE_BATCH16_PART2,
  ...ELITE_BATCH16_PART3,
  ...ELITE_BATCH16_PART4,
  ...ELITE_BATCH16_PART5,

  // ===================================================================
  // ELITE WORKFLOWS — Batch 17 (42 production-grade templates)
  // ===================================================================
  ...ELITE_BATCH17_PART1,
  ...ELITE_BATCH17_PART2,

  // ===================================================================
  // ELITE WORKFLOWS — Batch 18 (100 production-grade templates)
  // ===================================================================
  ...ELITE_BATCH18_PART1,
  ...ELITE_BATCH18_PART2,
  ...ELITE_BATCH18_PART3,
  ...ELITE_BATCH18_PART4,
  ...ELITE_BATCH18_PART5,

  // ===================================================================
  // ELITE WORKFLOWS — Batch 19 (100 production-grade templates)
  // ===================================================================
  ...ELITE_BATCH19_PART1,
  ...ELITE_BATCH19_PART2,
  ...ELITE_BATCH19_PART3,
  ...ELITE_BATCH19_PART4,
  ...ELITE_BATCH19_PART5,

  // ===================================================================
  // ELITE WORKFLOWS — Batch 20 (100 production-grade templates)
  // ===================================================================
  ...ELITE_BATCH20_PART1,
  ...ELITE_BATCH20_PART2,
  ...ELITE_BATCH20_PART3,
  ...ELITE_BATCH20_PART4,
  ...ELITE_BATCH20_PART5,

  // ===================================================================
  // ELITE WORKFLOWS — Batch 21 (133 production-grade templates)
  // ===================================================================
  ...ELITE_BATCH21_PART1,
  ...ELITE_BATCH21_PART2,
  ...ELITE_BATCH21_PART3,
  ...ELITE_BATCH21_PART4,
  ...ELITE_BATCH21_PART5,

  // ===================================================================
  // ELITE WORKFLOWS — Batch 22 (250 marketing workflow templates)
  // WebEngage, Klaviyo, Attentive, Marketo, HubSpot, Mailchimp, CleverTap, Braze
  // ===================================================================
  ...ELITE_BATCH22_PART1,
  ...ELITE_BATCH22_PART2,
  ...ELITE_BATCH22_PART3,
  ...ELITE_BATCH22_PART4,
  ...ELITE_BATCH22_PART5,
  ...ELITE_BATCH22_PART6,
  ...ELITE_BATCH22_PART7,
  ...ELITE_BATCH22_PART8,
  ...ELITE_BATCH22_PART9,
  ...ELITE_BATCH22_PART10,
  ...ELITE_BATCH22_PART11,
  ...ELITE_BATCH22_PART12,
  ...ELITE_BATCH22_PART13,
  ...ELITE_BATCH22_PART14,
  ...ELITE_BATCH22_PART15,
  ...ELITE_BATCH22_PART16,
  ...ELITE_BATCH22_PART17,
  ...ELITE_BATCH22_PART18,
  ...ELITE_BATCH22_PART19,
  ...ELITE_BATCH22_PART20,

  // ===================================================================
  // ELITE WORKFLOWS — Batch 23 (350 cross-platform workflow templates)
  // Sales, Marketing, Content, Support, Productivity, HR, Finance, IT, E-commerce, Data/AI, Personal/IoT
  // ===================================================================
  ...ELITE_BATCH23_PART1,
  ...ELITE_BATCH23_PART2,
  ...ELITE_BATCH23_PART3,
  ...ELITE_BATCH23_PART4,
  ...ELITE_BATCH23_PART5,
  ...ELITE_BATCH23_PART6,
  ...ELITE_BATCH23_PART7,
  ...ELITE_BATCH23_PART8,
  ...ELITE_BATCH23_PART9,
  ...ELITE_BATCH23_PART10,
  ...ELITE_BATCH23_PART11,
  ...ELITE_BATCH23_PART12,
  ...ELITE_BATCH23_PART13,
  ...ELITE_BATCH23_PART14,
  ...ELITE_BATCH23_PART15,
  ...ELITE_BATCH23_PART16,
  ...ELITE_BATCH23_PART17,
  ...ELITE_BATCH23_PART18,
  ...ELITE_BATCH23_PART19,
  ...ELITE_BATCH23_PART20,

  // ===================================================================
  // ELITE WORKFLOWS — Batch 24 (80 Elite Production Standard templates)
  // Enhanced: case stamping, confidence-gated AI, human review paths,
  // post-execution verification, correct builder field names
  // ===================================================================
  ...ELITE_BATCH24_PART1,
  ...ELITE_BATCH24_PART2,
  ...fromTemplateList(ELITE_BATCH24_PART3),
  ...fromTemplateList(ELITE_BATCH24_PART4),
  ...ELITE_BATCH24_PART5,
  ...ELITE_BATCH24_PART6,
  ...ELITE_BATCH24_PART7,
  ...ELITE_BATCH24_PART8,

  // ===================================================================
  // SLACK & NOTIFICATIONS
  // ===================================================================

  "Slack Notification Hub": {
    nodes: [
      trigger("Webhook: Any app event", "webhook_app", {
        source: "Any App",
        howItStarts: "An external app sends an event to your webhook URL",
        setup:
          "Copy your workflow webhook URL and paste it in the app that should trigger this workflow (e.g., Sentry, Datadog, PagerDuty)",
        inputFields: [
          {
            name: "webhook_payload",
            type: "object",
            example: '{"event":"deploy","status":"failed"}',
          },
        ],
      }),
      aiNode(
        "2",
        "AI classifies the event",
        280,
        'Classify this event by urgency. Respond ONLY with JSON: {urgency: \\"critical\\" | \\"normal\\", category: string, one_line_summary: string}',
        "{webhook_payload}",
        [
          { field: "urgency", as: "urgency", type: "string" },
          { field: "category", as: "category", type: "string" },
          { field: "one_line_summary", as: "one_line_summary", type: "string" },
        ],
      ),
      n("3", "condition", "Is it critical?", 520, {
        conditionData: [
          {
            variable: "urgency",
            value: "critical",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "4",
        "Alert #incidents (critical)",
        760,
        "🚨 *Critical:* {one_line_summary}\\nCategory: {category}",
      ),
      slackNode(
        "5",
        "Post to #activity (normal)",
        760,
        "ℹ️ {one_line_summary} ({category})",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5")],
    summary: `**Slack Notification Hub** — AI-powered event routing!\n\n1. Any app sends an event to your webhook\n2. **AI reads the content and classifies urgency** (critical vs normal)\n3. Critical events → #incidents channel\n4. Normal events → #activity channel\n\nNo magic variables — the AI reads the actual event payload and decides.\n\n**To activate:** Say "connect openai" and "connect slack"`,
  },

  "Multi-Channel Alert System": {
    nodes: [
      trigger("Webhook: Alert from monitoring tool", "webhook_app", {
        source: "Any Monitoring Tool",
        howItStarts:
          "Your monitoring system (Datadog, PagerDuty, etc.) sends an alert",
        setup:
          "In your monitoring tool, add a webhook notification → paste your workflow URL",
        inputFields: [
          {
            name: "alert_data",
            type: "object",
            example: '{"type":"cpu_spike","host":"prod-1"}',
          },
        ],
      }),
      aiNode(
        "2",
        "AI assess severity",
        280,
        'Assess this alert. Respond JSON: {severity: \\"p1\\" | \\"p2\\" | \\"p3\\", should_page: \\"true\\" | \\"false\\", summary: string}',
        "{alert_data}",
        [
          { field: "severity", as: "severity", type: "string" },
          { field: "should_page", as: "should_page", type: "string" },
          { field: "summary", as: "alert_summary", type: "string" },
        ],
      ),
      n("3", "condition", "Should page someone? (P1)", 520, {
        conditionData: [
          {
            variable: "should_page",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode("4", "Slack alert", 760, "🚨 {severity}: {alert_summary}"),
      emailNode(
        "5",
        "Email ops team",
        760,
        "ops@company.com",
        "Alert: {alert_summary}",
        "<p><strong>{severity}</strong>: {alert_summary}</p>",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("2", "4"), e("3", "5")],
    summary: `**Multi-Channel Alert System**\n\n1. Alert arrives via webhook\n2. AI assesses severity (P1/P2/P3)\n3. All alerts → Slack\n4. P1 (page-worthy) → also emails ops team\n\n**To activate:** Say "connect openai", "connect slack", "connect email"`,
  },

  // ===================================================================
  // SALES & LEADS
  // ===================================================================

  "Lead Qualification & Routing": {
    nodes: [
      trigger("Form: New lead submitted", "webhook_form", {
        source: "Outgrow Forms / Outgrow",
        howItStarts: "Someone fills out your lead capture form",
        setup:
          "In your form tool → Integrations → Webhooks → paste your workflow URL. The AI will read whatever fields your form sends (name, email, company, etc.) — no need to match exact field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the form submission",
        280,
        "Read this form submission and extract the lead details. Different forms have different field names — find the person's name, email, and company regardless of format. Respond JSON: {lead_name: string, lead_email: string, lead_company: string}",
        "{webhook_payload}",
        [
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "lead_company", as: "lead_company", type: "string" },
        ],
      ),
      n("3", "hitApi", "Look up company data", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.clearbit.com/v2/combined/find?email={lead_email}",
          headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
          body: [],
          variables: [
            {
              name: "company.name",
              customName: "company_name",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "employee_count",
              type: "number",
            },
            {
              name: "company.category.industry",
              customName: "industry",
              type: "string",
            },
          ],
        },
      }),
      aiNode(
        "4",
        "AI score the lead",
        760,
        'Score this lead 1-100 for sales readiness. Consider company size, industry, and job title. Respond JSON: {lead_score: number, reasoning: string, is_qualified: \\"true\\" | \\"false\\"}',
        "Name: {lead_name}, Email: {lead_email}, Company: {company_name}, Employees: {employee_count}, Industry: {industry}",
        [
          { field: "lead_score", as: "lead_score", type: "number" },
          { field: "reasoning", as: "reasoning", type: "string" },
          { field: "is_qualified", as: "is_qualified", type: "string" },
        ],
      ),
      n("5", "condition", "Qualified? (score > 70)", 1000, {
        conditionData: [
          {
            variable: "lead_score",
            value: "70",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "6",
        "Alert sales on Slack",
        1240,
        "🎯 Hot lead! {lead_name} ({lead_email}) from {company_name}\\nScore: {lead_score}/100 — {reasoning}",
      ),
      emailNode(
        "7",
        "Send nurture email",
        1240,
        "{lead_email}",
        "Thanks for your interest, {lead_name}!",
        "<p>Hi {lead_name},</p><p>Thanks for reaching out. We'd love to help {company_name}.</p>",
        460,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("5", "7"),
    ],
    summary: `**Lead Qualification & Routing**\n\n1. Form submitted → **AI reads the submission** and extracts name, email, company\n2. Clearbit enriches company data (size, industry)\n3. AI scores lead 1-100\n4. Score > 70 → Slack alert to sales team\n5. Score ≤ 70 → nurture email\n\nWorks with ANY form tool — the AI handles different field formats.\n\n**To activate:** Say "connect openai", "connect clearbit", "connect slack", "connect email"`,
  },

  "Email Nurture Sequence": {
    nodes: [
      trigger("Form: New subscriber", "webhook_form", {
        source: "Outgrow Forms / Mailchimp / Signup Form",
        howItStarts: "Someone subscribes via your form or landing page",
        setup:
          "Connect your signup form → Webhooks → paste your workflow URL. The AI will read whatever data your form sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the form submission",
        140,
        "Read this form/signup webhook data and extract the subscriber email and subscriber name. Different forms send data in different formats — find the subscriber email and subscriber name regardless of field naming. Respond JSON: {subscriber_email: string, subscriber_name: string}",
        "{webhook_payload}",
        [
          { field: "subscriber_email", as: "subscriber_email", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
        ],
      ),
      emailNode(
        "3",
        "Welcome email",
        340,
        "{subscriber_email}",
        "Welcome, {subscriber_name}!",
        "<p>Hi {subscriber_name},</p><p>Thanks for subscribing! Here's what to expect...</p>",
      ),
      n("4", "delayAction", "Wait 3 days", 540, {
        delayAction: { time: 3, timeFormat: "days" },
      }),
      emailNode(
        "5",
        "Tips email",
        740,
        "{subscriber_email}",
        "3 tips to get started",
        "<p>Hi {subscriber_name},</p><p>Here are the top 3 things our most successful users do first...</p>",
      ),
      n("6", "delayAction", "Wait 5 days", 940, {
        delayAction: { time: 5, timeFormat: "days" },
      }),
      emailNode(
        "7",
        "Special offer",
        1140,
        "{subscriber_email}",
        "Exclusive offer for you, {subscriber_name}",
        "<p>Hi {subscriber_name},</p><p>As a valued subscriber, here's a special offer...</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**Email Nurture Sequence** — 3-email drip!\n\nDay 0: Welcome → Day 3: Tips → Day 8: Offer\n\n**AI reads the data** and extracts subscriber email and name. All emails personalized.\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "Cold Outreach Sequencer": {
    nodes: [
      trigger("Manual: Submit lead data", "manual", {
        howItStarts: "You provide lead details in the chat or via API",
        setup:
          "Type the lead info in chat: name, email, company, title. Or POST to your workflow API endpoint",
        inputFields: [
          { name: "name", type: "string" },
          { name: "email", type: "string" },
          { name: "company", type: "string" },
          { name: "title", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI personalize the email",
        280,
        "Write a short, personalized cold outreach email (under 100 words). Be specific about their company. Respond JSON: {subject: string, body: string, personalization_hook: string}",
        "To: {name}, Title: {title} at {company}. Our product: [your value prop]",
        [
          { field: "subject", as: "subject", type: "string" },
          { field: "body", as: "email_body", type: "string" },
          {
            field: "personalization_hook",
            as: "personalization_hook",
            type: "string",
          },
        ],
      ),
      emailNode(
        "3",
        "Send personalized email",
        520,
        "{email}",
        "{subject}",
        "<p>{email_body}</p>",
      ),
      n("4", "delayAction", "Wait 3 days", 760, {
        delayAction: { time: 3, timeFormat: "days" },
      }),
      emailNode(
        "5",
        "Follow-up email",
        1000,
        "{email}",
        "Quick follow-up, {name}",
        "<p>Hi {name},</p><p>Just checking if you saw my previous email. Would love to chat about how we can help {company}.</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5")],
    summary: `**Cold Outreach Sequencer**\n\n1. Lead data arrives\n2. AI writes a personalized email (mentions their company/role)\n3. Sends the email\n4. 3-day wait → sends follow-up\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "Form to CRM Pipeline": {
    nodes: [
      trigger("Form: Submission received", "webhook_form", {
        source: "Outgrow Forms / Outgrow",
        howItStarts: "Someone submits your form",
        setup:
          "In your form tool → Integrations → Webhooks → paste your workflow URL. The AI will read whatever data your form sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the form submission",
        180,
        "Read this form webhook data and extract the contact email, first name, and last name. Different forms send data in different formats — find the contact email, contact first name, and contact last name regardless of field naming. Respond JSON: {contact_email: string, contact_first_name: string, contact_last_name: string}",
        "{webhook_payload}",
        [
          { field: "contact_email", as: "contact_email", type: "string" },
          {
            field: "contact_first_name",
            as: "contact_first_name",
            type: "string",
          },
          {
            field: "contact_last_name",
            as: "contact_last_name",
            type: "string",
          },
        ],
      ),
      n("3", "hitApi", "Create HubSpot contact", 380, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"email":"{contact_email}","firstname":"{contact_first_name}","lastname":"{contact_last_name}"}',
            },
          ],
          variables: [{ name: "id", customName: "contact_id", type: "string" }],
        },
      }),
      slackNode(
        "4",
        "Notify sales team",
        580,
        "📋 New lead from form: {contact_first_name} {contact_last_name} ({contact_email})\\nCreated in HubSpot: {contact_id}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4")],
    summary: `**Form to CRM Pipeline**\n\n1. Form submitted → **AI reads the data** and extracts contact email, first name, last name\n2. Creates HubSpot contact\n3. Notifies sales team on Slack\n\n**To activate:** Say "connect openai", "connect hubspot", and "connect slack"`,
  },

  // ===================================================================
  // AI-POWERED
  // ===================================================================

  "AI Content Writer": {
    nodes: [
      trigger("Manual: Provide a blog topic", "manual", {
        howItStarts: "You type a blog topic in the chat",
        setup:
          "Type your topic in the chat (e.g., '10 tips for remote work') or POST to workflow API with {topic: '...'}",
        inputFields: [
          {
            name: "topic",
            type: "string",
            example: "10 productivity tips for remote teams",
          },
        ],
      }),
      aiNode(
        "2",
        "AI write the post",
        280,
        "Write a 600-word blog post with intro, 3 subheadings, and conclusion with CTA. Respond JSON: {title: string, body_html: string, meta_description: string, word_count: number}",
        "Topic: {topic}",
        [
          { field: "title", as: "post_title", type: "string" },
          { field: "body_html", as: "body_html", type: "string" },
          { field: "meta_description", as: "meta_description", type: "string" },
          { field: "word_count", as: "word_count", type: "number" },
        ],
      ),
      n("3", "approvalGate", "Review before publishing", 520, {
        approvalGateData: {
          message: "Review the AI blog draft before it goes live",
        },
      }),
      emailNode(
        "4",
        "Email the final draft",
        760,
        "{author_email}",
        "Blog draft ready: {topic}",
        "{body_html}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4")],
    summary: `**AI Content Writer**\n\n1. Provide a topic\n2. AI writes a 600-word post with subheadings\n3. You review and approve\n4. Final draft emailed\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "AI Customer Response": {
    nodes: [
      trigger("Email: Support request received", "email", {
        howItStarts: "A customer sends a support email",
        setup:
          "Forward support emails to your workflow inbox, or connect your helpdesk (Zendesk, Freshdesk) webhook",
        inputFields: [
          { name: "customer_email", type: "string" },
          { name: "subject", type: "string" },
          { name: "message_body", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI draft a response",
        280,
        "Draft a support response. Be empathetic and solution-oriented. Respond JSON: {response_text: string, needs_human: boolean, confidence: number}",
        "From: {customer_email}\\nSubject: {subject}\\nMessage: {message_body}",
        [
          { field: "response_text", as: "response_text", type: "string" },
          { field: "needs_human", as: "needs_human", type: "string" },
          { field: "confidence", as: "confidence", type: "number" },
        ],
      ),
      n("3", "condition", "AI confident enough to auto-send? (>80%)", 520, {
        conditionData: [
          {
            variable: "confidence",
            value: "80",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "4",
        "Auto-send to customer",
        760,
        "{customer_email}",
        "Re: {subject}",
        "<p>{response_text}</p>",
      ),
      slackNode(
        "5",
        "Escalate to human on Slack",
        760,
        "⚠️ Needs human review:\\nFrom: {customer_email}\\n{message_body}\\n\\nAI draft: {response_text}",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5")],
    summary: `**AI Customer Response**\n\n1. Support email arrives\n2. AI drafts response + assesses confidence\n3. Confidence > 80% → auto-sends to customer\n4. Confidence ≤ 80% → escalates to Slack for human review\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  "AI Research Assistant": {
    nodes: [
      trigger("Manual: Submit research topic", "manual", {
        howItStarts: "You type a research topic in the chat",
        setup:
          "Type your question in the chat (e.g., 'What are the latest trends in AI?') or POST via API",
        inputFields: [
          {
            name: "topic",
            type: "string",
            example: "AI trends in healthcare 2025",
          },
          { name: "requester_email", type: "string" },
        ],
      }),
      n("2", "hitApi", "Search the web", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search?q={topic}&num=5",
          headers: [],
          params: [{ key: "api_key", value: "{serpapi_key}" }],
          variables: [
            {
              name: "organic_results",
              customName: "search_results",
              type: "array",
            },
          ],
        },
      }),
      aiNode(
        "3",
        "AI synthesize findings",
        520,
        "Synthesize these search results into a research brief. Include key findings, sources, and confidence level. Respond JSON: {summary: string, key_findings: string[], source_count: number}",
        "Topic: {topic}\\nResults: {search_results}",
        [
          { field: "summary", as: "research_summary", type: "string" },
          { field: "key_findings", as: "key_findings", type: "string" },
          { field: "source_count", as: "source_count", type: "number" },
        ],
      ),
      emailNode(
        "4",
        "Email research report",
        760,
        "{requester_email}",
        "Research Report: {topic}",
        "<h2>{topic}</h2>{research_summary}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4")],
    summary: `**AI Research Assistant**\n\n1. Provide a research topic\n2. Searches the web (SerpAPI)\n3. AI synthesizes into a structured brief\n4. Emails the report\n\n**To activate:** Say "connect serpapi", "connect openai", "connect email"`,
  },

  "AI Data Classifier": {
    nodes: [
      trigger("Manual: Submit text for classification", "manual", {
        howItStarts: "You paste text to classify in the chat",
        setup:
          "Paste any text in the chat (review, ticket, feedback) or POST via API with {input_text: '...'}",
        inputFields: [
          {
            name: "input_text",
            type: "string",
            example: "This product broke after 2 days, very disappointed",
          },
        ],
      }),
      aiNode(
        "2",
        "AI classify",
        280,
        "Classify this text. Categories: [positive_feedback, negative_feedback, feature_request, bug_report, question]. Respond JSON: {category: string, sentiment_score: number, summary: string}",
        "{input_text}",
        [
          { field: "category", as: "category", type: "string" },
          { field: "sentiment_score", as: "sentiment_score", type: "number" },
          { field: "summary", as: "classification_summary", type: "string" },
        ],
      ),
      n("3", "hitApi", "Save to Google Sheets", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Sheet1:append?valueInputOption=RAW",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{input_text}","{category}","{sentiment_score}","{classification_summary}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_added",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [e("1", "2"), e("2", "3")],
    summary: `**AI Data Classifier**\n\n1. Submit any text (reviews, tickets, feedback)\n2. AI classifies into categories + sentiment score\n3. Saved to Google Sheets\n\n**To activate:** Say "connect openai" and "connect google sheets"`,
  },

  "Content Repurposer": {
    nodes: [
      trigger("Manual: Provide blog content", "manual", {
        howItStarts: "You paste blog content to repurpose",
        setup:
          "Paste your blog post text in the chat, or POST via API with {content_text: '...', author_email: '...'}",
        inputFields: [
          { name: "content_text", type: "string" },
          { name: "author_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI rewrite for Twitter",
        280,
        "Rewrite as a Twitter thread (max 5 tweets, each under 280 chars). Respond JSON: {tweets: string[], hashtags: string[]}",
        "{content_text}",
        [
          { field: "tweets", as: "twitter_thread", type: "string" },
          { field: "hashtags", as: "twitter_hashtags", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "AI rewrite for LinkedIn",
        280,
        "Rewrite as a LinkedIn post (professional, 1000 chars max, include CTA). Respond JSON: {post_text: string, cta: string}",
        "{content_text}",
        [
          { field: "post_text", as: "linkedin_post", type: "string" },
          { field: "cta", as: "linkedin_cta", type: "string" },
        ],
        460,
      ),
      emailNode(
        "4",
        "Email all versions",
        520,
        "{author_email}",
        "Repurposed content ready",
        "<h3>Twitter</h3><p>{twitter_thread}</p><p>{twitter_hashtags}</p><h3>LinkedIn</h3><p>{linkedin_post}</p><p>{linkedin_cta}</p>",
      ),
    ],
    edges: [e("1", "2"), e("1", "3"), e("2", "4"), e("3", "4")],
    summary: `**Content Repurposer**\n\n1. Provide blog content\n2. AI rewrites for Twitter (thread) + LinkedIn (professional post)\n3. Both versions emailed for review\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  // ===================================================================
  // SUPPORT & OPERATIONS
  // ===================================================================

  "Customer Support Triage": {
    nodes: [
      trigger("Email: New support ticket", "email", {
        howItStarts: "A customer submits a support ticket or sends an email",
        setup:
          "Connect your helpdesk webhook (Zendesk, Freshdesk, Intercom) or forward support emails to your workflow inbox",
        inputFields: [
          { name: "ticket_subject", type: "string" },
          { name: "ticket_body", type: "string" },
          { name: "customer_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI classify urgency",
        280,
        'Classify this support ticket. Respond JSON: {urgency: \\"high\\" | \\"medium\\" | \\"low\\", category: string, suggested_response: string}',
        "Subject: {ticket_subject}\\nBody: {ticket_body}",
        [
          { field: "urgency", as: "urgency", type: "string" },
          { field: "category", as: "category", type: "string" },
          {
            field: "suggested_response",
            as: "suggested_response",
            type: "string",
          },
        ],
      ),
      n("3", "condition", "Is it high urgency?", 520, {
        conditionData: [
          {
            variable: "urgency",
            value: "high",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "4",
        "Urgent → Slack alert",
        760,
        "🚨 Urgent ticket: {ticket_subject}\\nCategory: {category}\\nSuggested: {suggested_response}",
      ),
      emailNode(
        "5",
        "Auto-acknowledge customer",
        520,
        "{customer_email}",
        "Re: {ticket_subject}",
        "<p>We've received your request and are looking into it.</p>",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("1", "5")],
    summary: `**Customer Support Triage**\n\n1. Ticket arrives\n2. AI classifies urgency + suggests response\n3. High urgency → immediate Slack alert\n4. All tickets → auto-acknowledgment email\n\n**To activate:** Say "connect openai", "connect slack", "connect email"`,
  },

  "Approval Workflow": {
    nodes: [
      trigger("Form: Request submitted", "webhook_form", {
        source: "Internal Form / Slack / Custom App",
        howItStarts: "Someone submits a request that needs approval",
        setup:
          "Set up an internal form or Slack workflow that POSTs to your workflow URL with request details. The AI will read whatever data your form sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the request data",
        180,
        "Read this form/webhook data and extract the requester name, requester email, request title, request description, and approver email. Different forms send data in different formats — find the requester name, requester email, request title, request description, and approver email regardless of field naming. Respond JSON: {requester_name: string, requester_email: string, request_title: string, request_description: string, approver_email: string}",
        "{webhook_payload}",
        [
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "request_title", as: "request_title", type: "string" },
          {
            field: "request_description",
            as: "request_description",
            type: "string",
          },
          { field: "approver_email", as: "approver_email", type: "string" },
        ],
      ),
      emailNode(
        "3",
        "Notify approver",
        380,
        "{approver_email}",
        "Approval needed: {request_title}",
        "<p>{requester_name} submitted: {request_description}</p><p><a href='{approval_url}'>Approve</a> | <a href='{rejection_url}'>Reject</a></p>",
      ),
      n("4", "approvalGate", "Wait for approval", 580, {
        approvalGateData: {
          message: "Request from {requester_name}: {request_title}",
        },
      }),
      emailNode(
        "5",
        "Notify requester (approved)",
        780,
        "{requester_email}",
        "Your request was approved!",
        "<p>Good news — {request_title} has been approved.</p>",
      ),
      emailNode(
        "6",
        "Notify requester (rejected)",
        780,
        "{requester_email}",
        "Update on your request",
        "<p>{request_title} was not approved at this time.</p>",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("4", "6")],
    summary: `**Approval Workflow**\n\n1. Request submitted → **AI reads the data** and extracts requester info, request details, approver email\n2. Approver emailed\n3. Waits for human approval/rejection\n4. Requester notified of outcome\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "Daily Standup Summary": {
    nodes: [
      trigger("Schedule: Weekdays at 9:00 AM", "schedule", {
        schedule: "Every weekday (Mon-Fri) at 9:00 AM",
        howItStarts: "Runs automatically every weekday morning",
        setup:
          "Just activate this workflow — it runs on schedule. Make sure to connect Slack and set your channel_id",
        inputFields: [
          { name: "channel_id", type: "string", example: "C04XXXXXX" },
        ],
      }),
      n("2", "hitApi", "Get Slack messages", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://slack.com/api/conversations.history?channel={channel_id}&oldest={yesterday_ts}",
          headers: [
            { key: "Authorization", value: "Bearer {slack_bot_token}" },
          ],
          variables: [
            { name: "messages", customName: "slack_messages", type: "array" },
            { name: "ok", customName: "slack_ok", type: "boolean" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI summarize updates",
        520,
        "Summarize these Slack messages into a daily standup digest. Group by person. Highlight blockers. Respond JSON: {summary: string, update_count: number, has_blockers: boolean}",
        "{slack_messages}",
        [
          { field: "summary", as: "standup_summary", type: "string" },
          { field: "update_count", as: "update_count", type: "number" },
          { field: "has_blockers", as: "has_blockers", type: "string" },
        ],
      ),
      n("4", "condition", "Any updates found?", 760, {
        conditionData: [
          {
            variable: "update_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "5",
        "Email digest to team leads",
        1000,
        "team-leads@company.com",
        "Daily Standup Summary",
        "<h2>Team Updates</h2>{standup_summary}",
      ),
      slackNode(
        "6",
        "Post to #standup",
        1000,
        "📋 *Daily Summary*\\n{standup_summary}",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("4", "6")],
    summary: `**Daily Standup Summary**\n\n1. Runs every weekday at 9 AM\n2. Pulls messages from your Slack channel\n3. AI summarizes by person, highlights blockers\n4. If updates found → emails team leads + posts to #standup\n\n**To activate:** Say "connect slack" and "connect openai" and "connect email"`,
  },

  // ===================================================================
  // E-COMMERCE
  // ===================================================================

  "New Order Notification": {
    nodes: [
      trigger("Shopify: New order placed", "webhook_app", {
        source: "Shopify",
        event: "orders/create",
        howItStarts: "A customer places a new order in your Shopify store",
        setup:
          "In Shopify → Settings → Notifications → Webhooks → Create webhook → Event: 'Order creation' → URL: paste your workflow URL",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads order details",
        280,
        "Read this Shopify order webhook and extract the key details. Respond JSON: {customer_email: string, customer_first_name: string, order_number: string, total_price: string, item_summary: string}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          {
            field: "customer_first_name",
            as: "customer_first_name",
            type: "string",
          },
          { field: "order_number", as: "order_number", type: "string" },
          { field: "total_price", as: "total_price", type: "string" },
          { field: "item_summary", as: "item_summary", type: "string" },
        ],
      ),
      emailNode(
        "3",
        "Confirm to customer",
        520,
        "{customer_email}",
        "Order #{order_number} confirmed!",
        "<p>Hi {customer_first_name},</p><p>Thanks for your order! Total: ${total_price}</p><p>{item_summary}</p>",
      ),
      slackNode(
        "4",
        "Notify team",
        520,
        "🛒 New order #{order_number} — ${total_price} from {customer_email}",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("2", "4")],
    summary: `**New Order Notification**\n\n1. Shopify sends order data via webhook\n2. **AI reads the order** and extracts customer email, name, order number, total\n3. Customer gets confirmation email\n4. Team gets Slack notification\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  "Abandoned Cart Recovery": {
    nodes: [
      trigger("Shopify: Cart abandoned", "webhook_app", {
        source: "Shopify",
        event: "checkouts/create",
        howItStarts:
          "A customer adds items to cart but doesn't complete checkout",
        setup:
          "In Shopify → Settings → Notifications → Webhooks → Create webhook → Event: 'Checkout creation' → URL: paste your workflow URL. The AI will read whatever data Shopify sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the cart data",
        180,
        "Read this Shopify cart/checkout webhook data and extract the customer email, cart items, and cart total. Different Shopify stores send data in different formats — find the customer email, cart items, and cart total regardless of field naming. Respond JSON: {customer_email: string, cart_items: string, cart_total: string}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "cart_items", as: "cart_items", type: "string" },
          { field: "cart_total", as: "cart_total", type: "string" },
        ],
      ),
      n("3", "delayAction", "Wait 1 hour", 380, {
        delayAction: { time: 1, timeFormat: "hours" },
      }),
      aiNode(
        "4",
        "AI write recovery email",
        580,
        "Write a short, friendly abandoned cart recovery email. Mention their items. Respond JSON: {subject: string, body_html: string}",
        "Customer: {customer_email}, Items in cart: {cart_items}, Cart value: ${cart_total}",
        [
          { field: "subject", as: "subject", type: "string" },
          { field: "body_html", as: "body_html", type: "string" },
        ],
      ),
      emailNode(
        "5",
        "Send recovery email",
        780,
        "{customer_email}",
        "{subject}",
        "{body_html}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5")],
    summary: `**Abandoned Cart Recovery**\n\n1. Cart abandoned (Shopify webhook)\n2. **AI reads the data** and extracts customer email, cart items, cart total\n3. Wait 1 hour\n4. AI writes personalized recovery email\n5. Sends to customer\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "Stripe → Slack Payment Alerts": {
    nodes: [
      trigger("Stripe: Payment received", "webhook_app", {
        source: "Stripe",
        event: "payment_intent.succeeded",
        howItStarts: "A customer successfully completes a payment",
        setup:
          "In Stripe → Developers → Webhooks → Add endpoint → URL: your workflow URL → Events: payment_intent.succeeded",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads payment details",
        280,
        "Read this Stripe webhook payload. Extract the payment amount (in dollars, divide cents by 100), customer email, and description. Respond JSON: {payment_amount: string, customer_email: string, payment_description: string}",
        "{webhook_payload}",
        [
          { field: "payment_amount", as: "payment_amount", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          {
            field: "payment_description",
            as: "payment_description",
            type: "string",
          },
        ],
      ),
      slackNode(
        "3",
        "Celebrate in Slack",
        520,
        "💰 Payment received! ${payment_amount} from {customer_email} — {payment_description}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3")],
    summary: `**Stripe → Slack Payment Alerts**\n\n1. Stripe sends payment data via webhook\n2. **AI reads the payment** and extracts amount, customer email\n3. Celebration posted to Slack\n\n**To activate:** Say "connect openai" and "connect slack"`,
  },

  // ===================================================================
  // MONITORING
  // ===================================================================

  "Website Downtime Monitor": {
    nodes: [
      trigger("Schedule: Every 5 minutes", "schedule", {
        schedule: "Every 5 minutes, 24/7",
        howItStarts: "Pings your website automatically every 5 minutes",
        setup:
          "Just activate this workflow and update the URL in the next node to your website's health endpoint",
        inputFields: [],
      }),
      n("2", "hitApi", "Ping website", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://your-website.com/health",
          headers: [],
          body: [],
          variables: [
            { name: "status", customName: "http_status", type: "number" },
          ],
        },
      }),
      n("3", "condition", "Site down? (not 200)", 520, {
        conditionData: [
          {
            variable: "http_status",
            value: "200",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "4",
        "Alert on Slack",
        760,
        "🔴 SITE DOWN! Returned status {http_status}",
      ),
      emailNode(
        "5",
        "Email ops team",
        760,
        "ops@company.com",
        "⚠️ Website Down",
        "<p>Your site returned HTTP {http_status}.</p>",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5")],
    summary: `**Website Downtime Monitor**\n\n1. Pings your site every 5 minutes\n2. If HTTP status ≠ 200 → alerts\n3. Slack + email to ops team\n\n**To activate:** Update the URL, then say "connect slack" and "connect email"`,
  },

  "Crypto Price Alert": {
    nodes: [
      trigger("Schedule: Every 15 minutes", "schedule", {
        schedule: "Every 15 minutes",
        howItStarts: "Checks cryptocurrency prices automatically",
        setup:
          "Just activate this workflow. Optionally say 'connect coingecko' for premium API access",
        inputFields: [],
      }),
      n("2", "hitApi", "Get prices from CoinGecko", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,ethereum&vs_currencies=usd&include_24hr_change=true",
          headers: [{ key: "x-cg-demo-api-key", value: "{coingecko_key}" }],
          body: [],
          variables: [
            { name: "bitcoin.usd", customName: "btc_price", type: "number" },
            {
              name: "bitcoin.usd_24h_change",
              customName: "btc_change_pct",
              type: "number",
            },
          ],
        },
      }),
      n("3", "condition", "Big move? (>5% change)", 520, {
        conditionData: [
          {
            variable: "btc_change_pct",
            value: "5",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "4",
        "Alert on Slack",
        760,
        "📉 BTC moved {btc_change_pct}% in 24h! Current: ${btc_price}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4")],
    summary: `**Crypto Price Alert** — real CoinGecko data!\n\n1. Checks BTC price every 15 min\n2. If 24h change > 5% → Slack alert\n\nUses real CoinGecko API fields.\n\n**To activate:** Say "connect coingecko" (free) and "connect slack"`,
  },

  "Error Rate Monitor": {
    nodes: [
      trigger("Schedule: Every hour", "schedule", {
        schedule: "Every hour",
        howItStarts: "Checks your error tracking service for spikes",
        setup:
          "Activate this workflow and say 'connect sentry' to set up your Sentry API token",
        inputFields: [],
      }),
      n("2", "hitApi", "Get error count", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sentry.io/api/0/projects/{org}/{project}/stats/?stat=received&resolution=1h",
          headers: [{ key: "Authorization", value: "Bearer {sentry_token}" }],
          variables: [
            { name: "[0][1]", customName: "error_count", type: "number" },
          ],
        },
      }),
      n("3", "condition", "Spike? (>100 errors/hour)", 520, {
        conditionData: [
          {
            variable: "error_count",
            value: "100",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "4",
        "Page on-call",
        760,
        "🐛 Error spike: {error_count} errors/hour! @oncall investigate",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4")],
    summary: `**Error Rate Monitor**\n\n1. Checks Sentry error count every hour\n2. If > 100 errors → alerts on Slack\n\n**To activate:** Say "connect sentry" and "connect slack"`,
  },

  "Deploy Notification": {
    nodes: [
      trigger("GitHub: CI/CD pipeline completes", "webhook_app", {
        source: "GitHub Actions",
        event: "workflow_run.completed",
        howItStarts: "Your CI/CD pipeline finishes (success or failure)",
        setup:
          "In GitHub → repo Settings → Webhooks → Add webhook → URL: your workflow URL → Events: 'Workflow runs'. The AI will read whatever data GitHub sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the deploy data",
        180,
        "Read this GitHub Actions webhook data and extract the repo name, branch name, commit message, and build result. Different CI/CD systems send data in different formats — find the repo name, branch name, commit message, and build result regardless of field naming. Respond JSON: {repo_name: string, branch_name: string, commit_msg: string, build_result: string}",
        "{webhook_payload}",
        [
          { field: "repo_name", as: "repo_name", type: "string" },
          { field: "branch_name", as: "branch_name", type: "string" },
          { field: "commit_msg", as: "commit_msg", type: "string" },
          { field: "build_result", as: "build_result", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "AI summarize the deploy",
        380,
        'Summarize this deployment. Respond JSON: {deploy_status: \\"success\\" | \\"failure\\", summary: string, risk_level: \\"low\\" | \\"medium\\" | \\"high\\"}',
        "Repo: {repo_name}, Branch: {branch_name}, Commit: {commit_msg}, Build status: {build_result}",
        [
          { field: "deploy_status", as: "deploy_status", type: "string" },
          { field: "summary", as: "deploy_summary", type: "string" },
          { field: "risk_level", as: "risk_level", type: "string" },
        ],
      ),
      n("4", "condition", "Did it succeed?", 580, {
        conditionData: [
          {
            variable: "deploy_status",
            value: "success",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "5",
        "Success → celebrate",
        780,
        "🚀 Deploy succeeded! {deploy_summary}",
      ),
      slackNode(
        "6",
        "Failure → alert",
        780,
        "❌ Deploy FAILED: {deploy_summary}\\nRisk: {risk_level} @oncall",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("4", "6")],
    summary: `**Deploy Notification**\n\n1. CI/CD webhook fires\n2. **AI reads the data** and extracts repo, branch, commit, build result\n3. AI summarizes the deploy + assesses risk\n4. Success → celebration in Slack\n5. Failure → alert with risk level\n\n**To activate:** Say "connect openai" and "connect slack"`,
  },

  "GitHub → Slack PR Alerts": {
    nodes: [
      trigger("GitHub: Pull request opened", "webhook_app", {
        source: "GitHub",
        event: "pull_request.opened",
        howItStarts: "Someone opens a new pull request",
        setup:
          "In GitHub → repo Settings → Webhooks → Add webhook → URL: your workflow URL → Events: 'Pull requests'. The AI will read whatever data GitHub sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the PR data",
        180,
        "Read this GitHub pull request webhook data and extract the PR title, description, author, files changed count, and PR link. Different GitHub webhook formats vary — find the PR title, PR description, PR author, files changed, and PR link regardless of field naming. Respond JSON: {pr_title: string, pr_description: string, pr_author: string, files_changed: string, pr_link: string}",
        "{webhook_payload}",
        [
          { field: "pr_title", as: "pr_title", type: "string" },
          { field: "pr_description", as: "pr_description", type: "string" },
          { field: "pr_author", as: "pr_author", type: "string" },
          { field: "files_changed", as: "files_changed", type: "string" },
          { field: "pr_link", as: "pr_link", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "AI summarize the PR",
        380,
        'Summarize this pull request in 2 sentences. Assess risk. Respond JSON: {pr_summary: string, risk: \\"low\\" | \\"medium\\" | \\"high\\"}',
        "PR: {pr_title}\\nDescription: {pr_description}\\nFiles: {files_changed}",
        [
          { field: "pr_summary", as: "pr_summary", type: "string" },
          { field: "risk", as: "pr_risk", type: "string" },
        ],
      ),
      slackNode(
        "4",
        "Post to #engineering",
        580,
        "🔀 New PR by {pr_author}: *{pr_title}*\\n{pr_summary}\\nRisk: {pr_risk}\\n<{pr_link}|Review>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4")],
    summary: `**GitHub → Slack PR Alerts**\n\n1. PR opened webhook fires\n2. **AI reads the data** and extracts PR title, description, author, files changed, link\n3. AI summarizes changes + assesses risk\n4. Posts to #engineering on Slack\n\n**To activate:** Say "connect openai" and "connect slack"`,
  },

  // ===================================================================
  // FINANCE
  // ===================================================================

  "Invoice Reminder System": {
    nodes: [
      trigger("Schedule: Daily at 8:00 AM", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts: "Checks Stripe daily for overdue invoices",
        setup:
          "Activate this workflow and say 'connect stripe' to set up your API key",
        inputFields: [],
      }),
      n("2", "hitApi", "Get overdue invoices from Stripe", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.stripe.com/v1/invoices?status=open&due_date[lt]={today_timestamp}",
          headers: [{ key: "Authorization", value: "Bearer {stripe_key}" }],
          variables: [
            { name: "data", customName: "overdue_invoices", type: "array" },
            { name: "has_more", customName: "has_more", type: "boolean" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI analyze overdue invoices",
        520,
        "Analyze these invoices and summarize. Respond JSON: {overdue_count: number, total_overdue_amount: number, top_invoice_number: string, top_customer_email: string, summary: string}",
        "Invoices: {overdue_invoices}",
        [
          { field: "overdue_count", as: "overdue_count", type: "number" },
          {
            field: "total_overdue_amount",
            as: "total_overdue_amount",
            type: "number",
          },
          { field: "top_invoice_number", as: "invoice_number", type: "string" },
          { field: "top_customer_email", as: "customer_email", type: "string" },
          { field: "summary", as: "overdue_summary", type: "string" },
        ],
      ),
      n("4", "condition", "Any overdue?", 760, {
        conditionData: [
          {
            variable: "overdue_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "5",
        "Send reminder to customer",
        1000,
        "{customer_email}",
        "Friendly reminder: Invoice #{invoice_number}",
        "<p>Hi,</p><p>Just a reminder that invoice #{invoice_number} is past due.</p>",
      ),
      slackNode(
        "6",
        "Alert accounting team",
        1000,
        "📋 {overdue_count} overdue invoices (${total_overdue_amount} total)\\n{overdue_summary}",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("4", "6")],
    summary: `**Invoice Reminder System**\n\n1. Daily check for overdue Stripe invoices\n2. AI analyzes the list and extracts key details\n3. If any overdue → sends reminder + alerts accounting\n\n**To activate:** Say "connect stripe", "connect openai", "connect email", "connect slack"`,
  },

  // ===================================================================
  // HR
  // ===================================================================

  "Resume Screener": {
    nodes: [
      trigger("Form: Job application received", "webhook_form", {
        source: "Greenhouse / Lever / Application Form",
        howItStarts: "A candidate submits a job application",
        setup:
          "In your ATS (Greenhouse, Lever) → Webhooks → 'Application submitted' → paste your workflow URL. Or connect a Outgrow Forms application form. The AI will read whatever data your ATS sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the application data",
        180,
        "Read this job application webhook/form data and extract the candidate name, resume text, job title, job requirements, and recruiter email. Different ATS systems send data in different formats — find the candidate name, resume text, job title, job requirements, and recruiter email regardless of field naming. Respond JSON: {candidate_name: string, resume_text: string, job_title: string, job_requirements: string, recruiter_email: string}",
        "{webhook_payload}",
        [
          { field: "candidate_name", as: "candidate_name", type: "string" },
          { field: "resume_text", as: "resume_text", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "job_requirements", as: "job_requirements", type: "string" },
          { field: "recruiter_email", as: "recruiter_email", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "AI score the resume",
        380,
        'Score this resume against the job requirements. Respond JSON: {match_score: number, strengths: string[], gaps: string[], recommendation: \\"shortlist\\" | \\"maybe\\" | \\"pass\\"}',
        "Job: {job_title}\\nRequirements: {job_requirements}\\nResume: {resume_text}",
        [
          { field: "match_score", as: "match_score", type: "number" },
          { field: "strengths", as: "strengths", type: "string" },
          { field: "gaps", as: "gaps", type: "string" },
          { field: "recommendation", as: "recommendation", type: "string" },
        ],
      ),
      n("4", "condition", "Should shortlist? (score > 75)", 580, {
        conditionData: [
          {
            variable: "match_score",
            value: "75",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "5",
        "Notify recruiter (shortlisted)",
        780,
        "{recruiter_email}",
        "🎯 Strong candidate: {candidate_name}",
        "<p>Score: {match_score}/100</p><p>Strengths: {strengths}</p><p>Gaps: {gaps}</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5")],
    summary: `**Resume Screener**\n\n1. Application received\n2. **AI reads the data** and extracts candidate name, resume, job title, requirements, recruiter email\n3. AI scores resume against job requirements\n4. Score > 75 → recruiter notified with analysis\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "Onboarding Email Sequence": {
    nodes: [
      trigger("Webhook: New user signs up", "webhook_app", {
        source: "Your App / Auth0 / Firebase",
        howItStarts: "A new user creates an account in your product",
        setup:
          "In your app's signup flow, POST to your workflow URL after user creation. Or use Auth0/Firebase post-registration webhook. The AI will read whatever data your app sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the signup data",
        140,
        "Read this signup/registration webhook data and extract the user email and user name. Different apps send data in different formats — find the user email and user name regardless of field naming. Respond JSON: {user_email: string, user_name: string}",
        "{webhook_payload}",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
        ],
      ),
      emailNode(
        "3",
        "Day 0: Welcome",
        340,
        "{user_email}",
        "Welcome, {user_name}!",
        "<p>Hi {user_name}, we're thrilled to have you!</p>",
      ),
      n("4", "delayAction", "Wait 3 days", 540, {
        delayAction: { time: 3, timeFormat: "days" },
      }),
      emailNode(
        "5",
        "Day 3: Tips",
        740,
        "{user_email}",
        "3 things successful users do",
        "<p>Hi {user_name}, here are the top tips...</p>",
      ),
      n("6", "delayAction", "Wait 4 days", 940, {
        delayAction: { time: 4, timeFormat: "days" },
      }),
      emailNode(
        "7",
        "Day 7: Check-in",
        1140,
        "{user_email}",
        "How's it going?",
        "<p>Hi {user_name}, it's been a week! Need any help?</p>",
      ),
      n("8", "delayAction", "Wait 7 days", 1340, {
        delayAction: { time: 7, timeFormat: "days" },
      }),
      emailNode(
        "9",
        "Day 14: Recap",
        1540,
        "{user_email}",
        "Your first 2 weeks",
        "<p>Hi {user_name}, here's what you've accomplished...</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
    summary: `**Onboarding Email Sequence** — 4 emails over 14 days\n\n**AI reads the data** and extracts user email and name.\n\nDay 0: Welcome → Day 3: Tips → Day 7: Check-in → Day 14: Recap\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "NPS Survey Automation": {
    nodes: [
      trigger("Form: Survey response received", "webhook_form", {
        source: "Outgrow Forms / SurveyMonkey / Outgrow Forms",
        howItStarts: "Someone completes your NPS survey",
        setup:
          "In your survey tool → Integrations → Webhooks → paste your workflow URL. The AI will read whatever data your survey tool sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads and classifies NPS response",
        280,
        'Read this survey webhook data and extract the respondent email, NPS score, and feedback. Then classify the response. Different survey tools send data in different formats — find the respondent email, raw NPS score, and respondent feedback regardless of field naming. Respond JSON: {respondent_email: string, nps_raw_score: number, respondent_feedback: string, nps_score: number, is_promoter: \\"true\\" | \\"false\\", is_detractor: \\"true\\" | \\"false\\", sentiment: string, follow_up_action: string}',
        "{webhook_payload}",
        [
          { field: "respondent_email", as: "respondent_email", type: "string" },
          { field: "nps_raw_score", as: "nps_raw_score", type: "number" },
          {
            field: "respondent_feedback",
            as: "respondent_feedback",
            type: "string",
          },
          { field: "nps_score", as: "nps_score", type: "number" },
          { field: "is_promoter", as: "is_promoter", type: "string" },
          { field: "is_detractor", as: "is_detractor", type: "string" },
          { field: "sentiment", as: "sentiment", type: "string" },
          { field: "follow_up_action", as: "follow_up_action", type: "string" },
        ],
      ),
      n("3", "condition", "Promoter? (score >= 9)", 520, {
        conditionData: [
          {
            variable: "nps_score",
            value: "9",
            comparison_operator: "GREATER THAN EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "4",
        "Ask promoters for review",
        760,
        "{respondent_email}",
        "Thanks for the love!",
        "<p>Would you mind leaving a quick review? It means the world to us.</p>",
      ),
      slackNode(
        "5",
        "Alert CS for detractors",
        760,
        "⚠️ Low NPS ({nps_score}) from {respondent_email}\\nSentiment: {sentiment}\\nAction: {follow_up_action}",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5")],
    summary: `**NPS Survey Automation**\n\n1. Survey response arrives via webhook (Outgrow Forms, etc.)\n2. **AI reads the data** and extracts respondent email, score, feedback, then classifies promoter vs detractor\n3. Promoters → asked for a review\n4. Detractors → CS team alerted on Slack\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  "Churn Risk Detector": {
    nodes: [
      trigger("Schedule: Every Monday at 9:00 AM", "schedule", {
        schedule: "Every Monday at 9:00 AM",
        howItStarts: "Weekly scan of user activity to identify churn risk",
        setup:
          "Activate this workflow and say 'connect openai'. Ensure your user activity data source is configured",
        inputFields: [
          { name: "name", type: "string" },
          { name: "last_login_days", type: "number" },
          { name: "feature_count", type: "number" },
          { name: "ticket_count", type: "number" },
        ],
      }),
      aiNode(
        "2",
        "AI assess churn risk",
        280,
        'Based on this user\'s activity data, assess churn risk. Respond JSON: {risk_level: \\"high\\" | \\"medium\\" | \\"low\\", risk_score: number, suggested_action: string}',
        "User: {name}, Last login: {last_login_days} days ago, Features used: {feature_count}, Support tickets: {ticket_count}",
        [
          { field: "risk_level", as: "risk_level", type: "string" },
          { field: "risk_score", as: "risk_score", type: "number" },
          { field: "suggested_action", as: "suggested_action", type: "string" },
        ],
      ),
      n("3", "condition", "High risk? (score > 70)", 520, {
        conditionData: [
          {
            variable: "risk_score",
            value: "70",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "4",
        "Alert CS team",
        760,
        "⚠️ Churn risk: {name} (score: {risk_score})\\nSuggested action: {suggested_action}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4")],
    summary: `**Churn Risk Detector**\n\n1. Weekly check of user activity\n2. AI assesses churn risk (0-100)\n3. High risk (>70) → CS team alerted on Slack\n\n**To activate:** Say "connect openai" and "connect slack"`,
  },

  // ===================================================================
  // SOCIAL & MARKETING
  // ===================================================================

  "Social Media Scheduler": {
    nodes: [
      trigger("Manual: Provide a post idea", "manual", {
        howItStarts: "You type a content idea in the chat",
        setup:
          "Type your post idea in the chat (e.g., 'We just launched AI features') or POST via API",
        inputFields: [
          {
            name: "post_idea",
            type: "string",
            example: "We just launched our new AI-powered dashboard",
          },
          { name: "marketer_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI create platform-specific posts",
        280,
        "Create social media posts for Twitter (280 chars max + hashtags), LinkedIn (professional, 1000 chars), and Instagram (casual, with emoji). Respond JSON: {twitter_post: string, linkedin_post: string, instagram_post: string, best_time: string}",
        "Content idea: {post_idea}",
        [
          { field: "twitter_post", as: "twitter_post", type: "string" },
          { field: "linkedin_post", as: "linkedin_post", type: "string" },
          { field: "instagram_post", as: "instagram_post", type: "string" },
          { field: "best_time", as: "best_time", type: "string" },
        ],
      ),
      emailNode(
        "3",
        "Email posts for approval",
        520,
        "{marketer_email}",
        "Social posts ready for review",
        "<h3>Twitter</h3><p>{twitter_post}</p><h3>LinkedIn</h3><p>{linkedin_post}</p><h3>Instagram</h3><p>{instagram_post}</p><p>Best time to post: {best_time}</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3")],
    summary: `**Social Media Scheduler**\n\n1. Provide a post idea\n2. AI creates Twitter, LinkedIn, and Instagram versions\n3. Emailed for review\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "Brand Mention Monitor": {
    nodes: [
      trigger("Schedule: Every hour", "schedule", {
        schedule: "Every hour",
        howItStarts: "Searches Reddit for mentions of your brand every hour",
        setup:
          "Activate and say 'connect reddit'. Set your brand_name in the next node",
        inputFields: [
          { name: "brand_name", type: "string", example: "OmniEngage" },
        ],
      }),
      n("2", "hitApi", "Search Reddit for mentions", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://oauth.reddit.com/search.json?q={brand_name}&sort=new&limit=5",
          headers: [{ key: "Authorization", value: "Bearer {reddit_token}" }],
          variables: [
            { name: "data.children", customName: "mentions", type: "array" },
            { name: "data.dist", customName: "mention_count", type: "number" },
          ],
        },
      }),
      n("3", "condition", "New mentions found?", 520, {
        conditionData: [
          {
            variable: "mention_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      aiNode(
        "4",
        "AI classify sentiment",
        760,
        "Classify the sentiment of these brand mentions. Respond JSON: {positive_count: number, negative_count: number, mention_summary: string, needs_response: boolean}",
        "Mentions: {mentions}",
        [
          { field: "positive_count", as: "positive_count", type: "number" },
          { field: "negative_count", as: "negative_count", type: "number" },
          { field: "mention_summary", as: "mention_summary", type: "string" },
          { field: "needs_response", as: "needs_response", type: "string" },
        ],
      ),
      slackNode(
        "5",
        "Post summary to Slack",
        1000,
        "👁️ Brand monitor: {mention_count} new mentions\\n{mention_summary}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5")],
    summary: `**Brand Mention Monitor**\n\n1. Searches Reddit hourly for your brand\n2. If mentions found → AI classifies sentiment\n3. Summary posted to Slack\n\n**To activate:** Say "connect reddit", "connect openai", "connect slack"`,
  },

  "Reddit Engagement Autopilot": {
    nodes: [
      trigger("Schedule: Every 2 hours", "schedule", {
        schedule: "Every 2 hours",
        howItStarts: "Checks your target subreddit for relevant posts",
        setup:
          "Activate and say 'connect reddit'. Set your subreddit in the next node",
        inputFields: [{ name: "subreddit", type: "string", example: "saas" }],
      }),
      n("2", "hitApi", "Get trending posts", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://oauth.reddit.com/r/{subreddit}/hot.json?limit=10",
          headers: [{ key: "Authorization", value: "Bearer {reddit_token}" }],
          variables: [
            { name: "data.children", customName: "posts", type: "array" },
            { name: "data.dist", customName: "post_count", type: "number" },
          ],
        },
      }),
      n("3", "condition", "Found posts?", 520, {
        conditionData: [
          {
            variable: "post_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      aiNode(
        "4",
        "AI generate a helpful reply",
        760,
        "Write a helpful, genuine reply to this Reddit post. Add value, don't be promotional. Respond JSON: {reply: string, relevance: number}",
        "Post: {post_title}\\n{post_body}",
        [
          { field: "reply", as: "reddit_reply", type: "string" },
          { field: "relevance", as: "relevance_score", type: "number" },
        ],
      ),
      n("5", "approvalGate", "You review before posting", 1000, {
        approvalGateData: {
          message: "Review AI reply before posting to Reddit",
        },
      }),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5")],
    summary: `**Reddit Engagement Autopilot**\n\n1. Checks subreddit every 2 hours\n2. AI generates helpful replies\n3. YOU review before anything is posted\n\n**To activate:** Say "connect reddit" and "connect openai"`,
  },

  "Google Sheets → CRM Sync": {
    nodes: [
      trigger("Google Sheets: New row added", "webhook_app", {
        source: "Google Sheets",
        event: "row.created",
        howItStarts: "A new row is added to your Google Sheet",
        setup:
          "Use Google Apps Script or Zapier to POST to your workflow URL when a new row is added to your sheet. The AI will read whatever data Google Sheets sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the row data",
        180,
        "Read this Google Sheets webhook data and extract the row email, first name, last name, and company. Different sheets send data in different formats — find the row email, row first name, row last name, and row company regardless of field naming. Respond JSON: {row_email: string, row_first_name: string, row_last_name: string, row_company: string}",
        "{webhook_payload}",
        [
          { field: "row_email", as: "row_email", type: "string" },
          { field: "row_first_name", as: "row_first_name", type: "string" },
          { field: "row_last_name", as: "row_last_name", type: "string" },
          { field: "row_company", as: "row_company", type: "string" },
        ],
      ),
      n("3", "hitApi", "Create HubSpot contact", 380, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"email":"{row_email}","firstname":"{row_first_name}","lastname":"{row_last_name}","company":"{row_company}"}',
            },
          ],
          variables: [
            { name: "id", customName: "hubspot_contact_id", type: "string" },
          ],
        },
      }),
      slackNode(
        "4",
        "Confirm sync",
        580,
        "✅ Synced {row_first_name} {row_last_name} to HubSpot (ID: {hubspot_contact_id})",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4")],
    summary: `**Google Sheets → CRM Sync**\n\n1. New row added to Google Sheets\n2. **AI reads the data** and extracts email, first name, last name, company\n3. Creates contact in HubSpot\n4. Confirmation posted to Slack\n\n**To activate:** Say "connect openai", "connect hubspot", and "connect slack"`,
  },

  "Daily Report Generator": {
    nodes: [
      trigger("Schedule: Every day at 8:00 AM", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts: "Generates a daily business report every morning",
        setup:
          "Activate this workflow. Configure your data sources (sales_data, ticket_data, traffic_data) in the AI node",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI generate report",
        280,
        "Generate a daily business summary with key metrics. Use the data provided. Respond JSON: {executive_summary: string, highlights: string[], action_items: string[]}",
        "Sales: {sales_data}, Support tickets: {ticket_data}, Website traffic: {traffic_data}",
        [
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "highlights", as: "highlights", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
        ],
      ),
      emailNode(
        "3",
        "Email to leadership",
        520,
        "leadership@company.com",
        "Daily Business Report",
        "<h2>Executive Summary</h2><p>{executive_summary}</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3")],
    summary: `**Daily Report Generator**\n\n1. Runs every morning at 8 AM\n2. AI generates executive summary from your data\n3. Emails to leadership team\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  // ===================================================================
  // REMAINING TEMPLATES — Batch 2
  // ===================================================================

  "TikTok Video Ad Pipeline": {
    nodes: [
      trigger("Manual: Submit video brief", "manual", {
        howItStarts: "You provide product details for a TikTok ad script",
        setup:
          "Type your brief in chat: product name, target audience, and tone. Or POST via API",
        inputFields: [
          { name: "product_name", type: "string" },
          { name: "target_audience", type: "string" },
          { name: "tone", type: "string", example: "fun and energetic" },
          { name: "video_team_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI write video script",
        280,
        "Write a 30-second TikTok ad script. Include hook (first 3 sec), body, and CTA. Respond JSON: {hook: string, script_body: string, cta: string, caption: string, hashtags: string[]}",
        "Product: {product_name}, Audience: {target_audience}, Tone: {tone}",
        [
          { field: "hook", as: "hook", type: "string" },
          { field: "script_body", as: "script_body", type: "string" },
          { field: "cta", as: "cta", type: "string" },
          { field: "caption", as: "caption", type: "string" },
          { field: "hashtags", as: "hashtags", type: "string" },
        ],
      ),
      n("3", "approvalGate", "Review script before production", 520, {
        approvalGateData: {
          message: "Review TikTok script: {hook} → {script_body} → {cta}",
        },
      }),
      emailNode(
        "4",
        "Send script to video team",
        760,
        "{video_team_email}",
        "TikTok Script Ready: {product_name}",
        "<h3>Hook (0-3s)</h3><p>{hook}</p><h3>Body</h3><p>{script_body}</p><h3>CTA</h3><p>{cta}</p><h3>Caption</h3><p>{caption}</p><p>Hashtags: {hashtags}</p>",
      ),
      slackNode(
        "5",
        "Notify marketing channel",
        760,
        "🎬 New TikTok script ready for {product_name}\\nCaption: {caption}",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5")],
    summary: `**TikTok Video Ad Pipeline**\n\n1. Submit a product brief\n2. AI writes a 30-sec TikTok script (hook + body + CTA)\n3. You review and approve\n4. Script sent to video team + Slack notification\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  "Instagram Content Calendar": {
    nodes: [
      trigger("Schedule: Every Monday at 9:00 AM", "schedule", {
        schedule: "Every Monday at 9:00 AM",
        howItStarts: "Generates a weekly Instagram content plan automatically",
        setup:
          "Activate and say 'connect openai'. Set your brand_name, industry, and audience in the AI node",
        inputFields: [
          { name: "brand_name", type: "string" },
          { name: "industry", type: "string" },
          { name: "audience_description", type: "string" },
          { name: "marketing_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI generate 5 post ideas",
        280,
        "Generate 5 Instagram post ideas for this week. Mix carousels, reels, stories. Respond JSON: {content_plan: string, post_count: number, top_hashtags: string}",
        "Brand: {brand_name}, Industry: {industry}, Audience: {audience_description}",
        [
          { field: "content_plan", as: "content_plan", type: "string" },
          { field: "post_count", as: "post_count", type: "number" },
          { field: "top_hashtags", as: "top_hashtags", type: "string" },
        ],
      ),
      emailNode(
        "3",
        "Email content plan to team",
        520,
        "{marketing_email}",
        "This Week's Instagram Plan",
        "<h2>Instagram Content Calendar</h2><p>{content_plan}</p><p>Top hashtags: {top_hashtags}</p>",
      ),
      slackNode(
        "4",
        "Post plan to #content",
        520,
        "📸 This week's Instagram plan is ready! 5 posts planned. Check email for details.",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("2", "4")],
    summary: `**Instagram Content Calendar**\n\n1. Runs every Monday at 9 AM\n2. AI generates 5 post ideas (carousel, reel, story mix)\n3. Emailed to team + posted to Slack\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  "SEO Keyword Monitor": {
    nodes: [
      trigger("Schedule: Every day at 7:00 AM", "schedule", {
        schedule: "Every day at 7:00 AM",
        howItStarts: "Checks your keyword ranking daily on Google",
        setup:
          "Activate and say 'connect serpapi'. Set target_keyword and our_domain in the API node",
        inputFields: [
          {
            name: "target_keyword",
            type: "string",
            example: "workflow automation",
          },
          { name: "our_domain", type: "string", example: "omniengage.com" },
        ],
      }),
      n("2", "hitApi", "Check keyword rankings", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search?q={target_keyword}&num=20",
          headers: [],
          params: [{ key: "api_key", value: "{serpapi_key}" }],
          variables: [
            {
              name: "organic_results",
              customName: "serp_results",
              type: "array",
            },
            {
              name: "search_information.total_results",
              customName: "total_results",
              type: "number",
            },
          ],
        },
      }),
      aiNode(
        "3",
        "AI analyze ranking position",
        520,
        "Find where our domain appears in these search results. Respond JSON: {current_position: number, was_found: boolean, top_competitor: string, recommendation: string}",
        "Our domain: {our_domain}\\nKeyword: {target_keyword}\\nResults: {serp_results}",
        [
          { field: "current_position", as: "current_position", type: "number" },
          { field: "was_found", as: "was_found", type: "string" },
          { field: "top_competitor", as: "top_competitor", type: "string" },
          { field: "recommendation", as: "seo_recommendation", type: "string" },
        ],
      ),
      n("4", "condition", "Position dropped below 10?", 760, {
        conditionData: [
          {
            variable: "current_position",
            value: "10",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "5",
        "Alert: ranking dropped",
        1000,
        "📉 SEO Alert: '{target_keyword}' dropped to position {current_position}\\nTop competitor: {top_competitor}\\nAction: {seo_recommendation}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5")],
    summary: `**SEO Keyword Monitor**\n\n1. Daily SERP check via SerpAPI\n2. AI analyzes where your site ranks\n3. If position drops below 10 → Slack alert with competitor info\n\n**To activate:** Say "connect serpapi", "connect openai", "connect slack"`,
  },

  "Competitor Price Tracker": {
    nodes: [
      trigger("Schedule: Every day at 6:00 AM", "schedule", {
        schedule: "Every day at 6:00 AM",
        howItStarts: "Scrapes competitor pricing page daily to detect changes",
        setup:
          "Activate and say 'connect scrapingbee'. Set competitor_url, our_price, and product_name in the nodes",
        inputFields: [
          { name: "competitor_url", type: "string" },
          { name: "our_price", type: "string" },
          { name: "product_name", type: "string" },
          { name: "pricing_team_email", type: "string" },
        ],
      }),
      n("2", "hitApi", "Scrape competitor page", 280, {
        hitApiDetails: {
          method: "GET",
          url: 'https://api.scrapingbee.com/v1/?api_key={scrapingbee_key}&url={competitor_url}&extract_rules={"price":{"selector":".price","type":"text"}}',
          headers: [],
          variables: [
            { name: "price", customName: "competitor_price", type: "string" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI compare prices",
        520,
        "Compare competitor price to ours. Determine if we should adjust. Respond JSON: {price_difference_pct: number, pricing_recommendation: string, should_alert: boolean}",
        "Competitor price: {competitor_price}, Our price: {our_price}, Product: {product_name}",
        [
          {
            field: "price_difference_pct",
            as: "price_difference_pct",
            type: "number",
          },
          {
            field: "pricing_recommendation",
            as: "pricing_recommendation",
            type: "string",
          },
          { field: "should_alert", as: "should_alert", type: "string" },
        ],
      ),
      n("4", "condition", "Significant difference? (>10%)", 760, {
        conditionData: [
          {
            variable: "price_difference_pct",
            value: "10",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "5",
        "Email pricing team",
        1000,
        "{pricing_team_email}",
        "Price Alert: {product_name}",
        "<p>Competitor changed price to {competitor_price} (ours: {our_price})</p><p>Difference: {price_difference_pct}%</p><p>Recommendation: {pricing_recommendation}</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5")],
    summary: `**Competitor Price Tracker**\n\n1. Daily scrape of competitor pricing page\n2. AI compares to your price and calculates gap\n3. If >10% difference → emails pricing team\n\n**To activate:** Say "connect scrapingbee" (or any scraper), "connect openai", "connect email"`,
  },

  "Meeting Follow-up Automator": {
    nodes: [
      trigger("Manual: Submit meeting notes", "manual", {
        howItStarts: "You paste meeting notes after a call",
        setup:
          "After your meeting, paste the notes in chat with meeting title and attendee emails. Or POST via API",
        inputFields: [
          { name: "meeting_title", type: "string" },
          { name: "attendees", type: "string" },
          { name: "meeting_notes", type: "string" },
          { name: "attendees_emails", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI extract action items",
        280,
        "Extract action items, decisions, and key takeaways from these meeting notes. Respond JSON: {meeting_summary: string, action_items_text: string, decisions_text: string, next_meeting_topics: string}",
        "Meeting: {meeting_title}\\nAttendees: {attendees}\\nNotes: {meeting_notes}",
        [
          { field: "meeting_summary", as: "meeting_summary", type: "string" },
          {
            field: "action_items_text",
            as: "action_items_text",
            type: "string",
          },
          { field: "decisions_text", as: "decisions_text", type: "string" },
          {
            field: "next_meeting_topics",
            as: "next_meeting_topics",
            type: "string",
          },
        ],
      ),
      emailNode(
        "3",
        "Email recap to attendees",
        520,
        "{attendees_emails}",
        "Meeting Recap: {meeting_title}",
        "<h2>{meeting_title}</h2><h3>Summary</h3><p>{meeting_summary}</p><h3>Action Items</h3><p>{action_items_text}</p><h3>Decisions</h3><p>{decisions_text}</p>",
      ),
      slackNode(
        "4",
        "Post summary to Slack",
        520,
        "📝 Meeting recap: *{meeting_title}*\\n{meeting_summary}\\n\\nAction items posted — check your email!",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("2", "4")],
    summary: `**Meeting Follow-up Automator**\n\n1. Submit meeting notes\n2. AI extracts action items, decisions, and takeaways\n3. Recap emailed to all attendees\n4. Summary posted to Slack\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  "Proposal Generator": {
    nodes: [
      trigger("Webhook: Deal reaches proposal stage", "webhook_app", {
        source: "HubSpot / Salesforce / CRM",
        event: "deal.stage_changed",
        howItStarts:
          "Your CRM sends a notification when a deal moves to 'proposal' stage",
        setup:
          "In HubSpot: Settings → Integrations → Webhooks → Event: 'Deal stage change' → paste your workflow URL. The AI will read whatever data HubSpot sends and extract the details it needs.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the CRM data and extracts deal info",
        280,
        "Read this CRM webhook payload and extract the deal details. Different CRMs send different formats — find the client name, email, company, deal value, and any notes or requirements. Respond JSON: {client_name: string, client_email: string, client_company: string, deal_value: string, product_name: string, requirements: string}",
        "{webhook_payload}",
        [
          { field: "client_name", as: "client_name", type: "string" },
          { field: "client_email", as: "client_email", type: "string" },
          { field: "client_company", as: "client_company", type: "string" },
          { field: "deal_value", as: "deal_value", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "requirements", as: "requirements", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "AI generate proposal",
        520,
        "Generate a professional proposal based on the client details and deal info. Respond JSON: {proposal_title: string, executive_summary: string, scope_of_work: string, pricing_section: string, timeline: string, next_steps: string}",
        "Client: {client_name}, Company: {client_company}, Deal value: ${deal_value}, Product: {product_name}, Requirements: {requirements}",
        [
          { field: "proposal_title", as: "proposal_title", type: "string" },
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "scope_of_work", as: "scope_of_work", type: "string" },
          { field: "pricing_section", as: "pricing_section", type: "string" },
          { field: "timeline", as: "timeline", type: "string" },
          { field: "next_steps", as: "next_steps", type: "string" },
        ],
      ),
      n("4", "approvalGate", "Review proposal before sending", 760, {
        approvalGateData: {
          message:
            "Review AI-generated proposal for {client_name} at {client_company}",
        },
      }),
      emailNode(
        "5",
        "Send proposal to client",
        1000,
        "{client_email}",
        "Proposal: {proposal_title}",
        "<h1>{proposal_title}</h1><h2>Executive Summary</h2><p>{executive_summary}</p><h2>Scope</h2><p>{scope_of_work}</p><h2>Pricing</h2><p>{pricing_section}</p><h2>Timeline</h2><p>{timeline}</p><h2>Next Steps</h2><p>{next_steps}</p>",
      ),
      slackNode(
        "6",
        "Notify sales team",
        1000,
        "📄 Proposal sent to {client_name} ({client_company}) — ${deal_value}",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("4", "6")],
    summary: `**Proposal Generator**\n\n1. Your CRM sends a webhook when a deal reaches proposal stage\n2. **AI reads the raw CRM data** and extracts client name, email, company, deal value\n3. AI generates a full proposal (summary, scope, pricing, timeline)\n4. You review and approve\n5. Sent to client via email + sales team notified\n\nThe AI handles different CRM formats automatically — HubSpot, Salesforce, Pipedrive, etc.\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  "Deal Stagnation Alert": {
    nodes: [
      trigger("Schedule: Every weekday at 9:00 AM", "schedule", {
        schedule: "Every weekday at 9:00 AM",
        howItStarts: "Checks HubSpot daily for deals stuck more than 7 days",
        setup: "Activate and say 'connect hubspot' to set up your API token",
        inputFields: [{ name: "today_date", type: "string" }],
      }),
      n("2", "hitApi", "Get open deals from HubSpot", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/deals?properties=dealname,amount,dealstage,hs_lastmodifieddate&limit=50",
          headers: [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
          variables: [
            { name: "results", customName: "open_deals", type: "array" },
            { name: "total", customName: "total_deals", type: "number" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI find stuck deals",
        520,
        "Analyze these deals and find any that haven't been updated in 7+ days. For each stuck deal, suggest a next action. Respond JSON: {stuck_deals_summary: string, stuck_count: number}",
        "Today: {today_date}\\nDeals: {open_deals}",
        [
          {
            field: "stuck_deals_summary",
            as: "stuck_deals_summary",
            type: "string",
          },
          { field: "stuck_count", as: "stuck_count", type: "number" },
        ],
      ),
      n("4", "condition", "Any deals stuck?", 760, {
        conditionData: [
          {
            variable: "stuck_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "5",
        "Alert sales reps",
        1000,
        "⏰ {stuck_count} deals stuck 7+ days:\\n{stuck_deals_summary}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5")],
    summary: `**Deal Stagnation Alert**\n\n1. Daily check of HubSpot deals\n2. AI identifies deals stuck 7+ days\n3. Suggests next action for each stuck deal\n4. Alerts sales reps on Slack\n\n**To activate:** Say "connect hubspot", "connect openai", "connect slack"`,
  },

  "Webhook Event Router": {
    nodes: [
      trigger("Webhook: Any app event", "webhook_app", {
        source: "Any App",
        howItStarts: "Any external app sends an event to your webhook URL",
        setup:
          "Copy your workflow webhook URL and paste it in ANY app that sends events. The AI will automatically classify and route it",
        inputFields: [{ name: "webhook_payload", type: "object" }],
      }),
      aiNode(
        "2",
        "AI classify and route",
        280,
        'Classify this webhook event. Determine the source app, event type, and correct routing. Respond JSON: {source_app: string, event_type: string, priority: \\"high\\" | \\"normal\\" | \\"low\\", route_to: \\"sales\\" | \\"engineering\\" | \\"support\\" | \\"ops\\", event_summary: string}',
        "{webhook_payload}",
        [
          { field: "source_app", as: "source_app", type: "string" },
          { field: "event_type", as: "event_type", type: "string" },
          { field: "priority", as: "priority", type: "string" },
          { field: "route_to", as: "route_to", type: "string" },
          { field: "event_summary", as: "event_summary", type: "string" },
        ],
      ),
      n("3", "condition", "Route to sales?", 520, {
        conditionData: [
          {
            variable: "route_to",
            value: "sales",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "4",
        "Post to #sales",
        760,
        "🎯 [{source_app}] {event_type}: {event_summary}",
      ),
      slackNode(
        "5",
        "Post to #engineering or #support",
        760,
        "⚡ [{source_app}] {event_type}: {event_summary}\\nPriority: {priority}",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5")],
    summary: `**Webhook Event Router**\n\n1. Any app sends an event to your webhook\n2. AI reads the payload and classifies it (source, type, priority)\n3. Routes to the correct Slack channel\n\nNo hardcoded event types — AI handles ANY payload.\n\n**To activate:** Say "connect openai" and "connect slack"`,
  },

  "Inventory Low-Stock Alert": {
    nodes: [
      trigger("Schedule: Every day at 8:00 AM", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts: "Checks Shopify inventory levels daily",
        setup:
          "Activate and say 'connect shopify' to set up your Shopify API token",
        inputFields: [],
      }),
      n("2", "hitApi", "Get product inventory from Shopify", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shop_domain}.myshopify.com/admin/api/2024-01/products.json?fields=id,title,variants&limit=50",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          ],
          variables: [
            { name: "products", customName: "all_products", type: "array" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI find low-stock items",
        520,
        "Analyze this product inventory. Find items with quantity below 10. Respond JSON: {low_stock_list: string, low_stock_count: number, most_urgent: string}",
        "Products: {all_products}",
        [
          { field: "low_stock_list", as: "low_stock_list", type: "string" },
          { field: "low_stock_count", as: "low_stock_count", type: "number" },
          { field: "most_urgent", as: "most_urgent", type: "string" },
        ],
      ),
      n("4", "condition", "Any low stock items?", 760, {
        conditionData: [
          {
            variable: "low_stock_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "5",
        "Alert purchasing team",
        1000,
        "📦 {low_stock_count} items below threshold!\\nMost urgent: {most_urgent}\\n\\nFull list: {low_stock_list}",
      ),
      emailNode(
        "6",
        "Email reorder list",
        1000,
        "{purchasing_email}",
        "Low Stock Alert: {low_stock_count} items need reorder",
        "<p>Most urgent: {most_urgent}</p><p>{low_stock_list}</p>",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("4", "6")],
    summary: `**Inventory Low-Stock Alert**\n\n1. Daily Shopify inventory check\n2. AI finds items below threshold (qty < 10)\n3. Slack alert + email to purchasing team\n\n**To activate:** Say "connect shopify", "connect openai", "connect slack", "connect email"`,
  },

  "New Employee Onboarding": {
    nodes: [
      trigger("Webhook: New hire added to HR system", "webhook_app", {
        source: "BambooHR / Gusto / Rippling",
        event: "employee.created",
        howItStarts: "A new employee is added to your HR system",
        setup:
          "In your HR tool → Integrations → Webhooks → Event: 'New employee' → URL: your workflow URL. The AI will read whatever data your HR system sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the HR data",
        180,
        "Read this HR system webhook data and extract the employee name, employee email, job title, start date, manager name, and company name. Different HR systems send data in different formats — find the employee name, employee email, job title, start date, manager, and company regardless of field naming. Respond JSON: {emp_name: string, emp_email: string, emp_title: string, emp_start_date: string, emp_manager: string, emp_company: string}",
        "{webhook_payload}",
        [
          { field: "emp_name", as: "emp_name", type: "string" },
          { field: "emp_email", as: "emp_email", type: "string" },
          { field: "emp_title", as: "emp_title", type: "string" },
          { field: "emp_start_date", as: "emp_start_date", type: "string" },
          { field: "emp_manager", as: "emp_manager", type: "string" },
          { field: "emp_company", as: "emp_company", type: "string" },
        ],
      ),
      emailNode(
        "3",
        "Welcome email to new hire",
        380,
        "{emp_email}",
        "Welcome to {emp_company}!",
        "<p>Hi {emp_name},</p><p>We're excited to have you join as {emp_title}!</p><p>Your start date: {emp_start_date}</p><p>Your manager: {emp_manager}</p>",
      ),
      slackNode(
        "4",
        "Announce in #team",
        380,
        "👋 Welcome {emp_name}! Joining as {emp_title} on {emp_start_date}. Manager: {emp_manager}",
        460,
      ),
      n("5", "delayAction", "Wait until start date", 580, {
        delayAction: { time: 1, timeFormat: "days" },
      }),
      emailNode(
        "6",
        "Day 1: orientation guide",
        780,
        "{emp_email}",
        "Your Day 1 Guide",
        "<p>Hi {emp_name},</p><p>Here's everything you need for your first day...</p>",
      ),
      slackNode(
        "7",
        "Remind manager",
        780,
        "📋 Reminder: {emp_name} starts today! Please ensure workstation and accounts are ready.",
        460,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("2", "4"),
      e("3", "5"),
      e("5", "6"),
      e("5", "7"),
    ],
    summary: `**New Employee Onboarding**\n\n1. New hire added to HR system\n2. **AI reads the data** and extracts employee name, email, title, start date, manager, company\n3. Welcome email + Slack announcement\n4. On start date: orientation guide + manager reminder\n\n**To activate:** Say "connect openai", "connect email", and "connect slack"`,
  },

  "AI Document Analyzer": {
    nodes: [
      trigger("Manual: Submit document text", "manual", {
        howItStarts: "You paste document text in the chat for analysis",
        setup:
          "Paste the document content in chat, or POST via API with {document_text: '...', requester_email: '...'}",
        inputFields: [
          { name: "document_text", type: "string" },
          { name: "requester_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI analyze document",
        280,
        "Analyze this document. Extract key points, entities, action items, and a summary. Respond JSON: {doc_summary: string, key_points: string, entities: string, action_items: string, word_count: number, sentiment: string}",
        "{document_text}",
        [
          { field: "doc_summary", as: "doc_summary", type: "string" },
          { field: "key_points", as: "key_points", type: "string" },
          { field: "entities", as: "entities", type: "string" },
          { field: "action_items", as: "doc_action_items", type: "string" },
          { field: "word_count", as: "word_count", type: "number" },
          { field: "sentiment", as: "sentiment", type: "string" },
        ],
      ),
      n("3", "hitApi", "Save to Google Sheets", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Sheet1:append?valueInputOption=RAW",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{doc_summary}","{key_points}","{doc_action_items}","{sentiment}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_added",
              type: "number",
            },
          ],
        },
      }),
      emailNode(
        "4",
        "Email analysis report",
        520,
        "{requester_email}",
        "Document Analysis Complete",
        "<h2>Summary</h2><p>{doc_summary}</p><h3>Key Points</h3><p>{key_points}</p><h3>Action Items</h3><p>{doc_action_items}</p><p>Sentiment: {sentiment}</p>",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("2", "4")],
    summary: `**AI Document Analyzer**\n\n1. Submit any document text\n2. AI extracts summary, key points, entities, and action items\n3. Saved to Google Sheets + emailed to you\n\n**To activate:** Say "connect openai", "connect google sheets", "connect email"`,
  },

  "AI Social Listener": {
    nodes: [
      trigger("Schedule: Every 2 hours", "schedule", {
        schedule: "Every 2 hours",
        howItStarts: "Monitors Reddit for brand mentions automatically",
        setup:
          "Activate and say 'connect reddit' and 'connect openai'. Set brand_name in the API node",
        inputFields: [{ name: "brand_name", type: "string" }],
      }),
      n("2", "hitApi", "Search Reddit for brand mentions", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://oauth.reddit.com/search.json?q={brand_name}&sort=new&limit=10&t=hour",
          headers: [{ key: "Authorization", value: "Bearer {reddit_token}" }],
          variables: [
            {
              name: "data.children",
              customName: "reddit_mentions",
              type: "array",
            },
            { name: "data.dist", customName: "mention_count", type: "number" },
          ],
        },
      }),
      n("3", "condition", "Any new mentions?", 520, {
        conditionData: [
          {
            variable: "mention_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      aiNode(
        "4",
        "AI analyze sentiment & draft response",
        760,
        "Analyze these brand mentions. For negative ones, draft a helpful response. Respond JSON: {positive_count: number, negative_count: number, needs_response: boolean, draft_response: string, threat_summary: string}",
        "Brand: {brand_name}\\nMentions: {reddit_mentions}",
        [
          { field: "positive_count", as: "positive_count", type: "number" },
          { field: "negative_count", as: "negative_count", type: "number" },
          { field: "needs_response", as: "needs_response", type: "string" },
          { field: "draft_response", as: "draft_response", type: "string" },
          { field: "threat_summary", as: "threat_summary", type: "string" },
        ],
      ),
      n("5", "condition", "Any negative mentions?", 1000, {
        conditionData: [
          {
            variable: "negative_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "6",
        "Alert: negative mention detected",
        1240,
        "⚠️ Negative brand mention on Reddit!\\n{threat_summary}\\n\\nDraft response: {draft_response}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
    summary: `**AI Social Listener**\n\n1. Monitors Reddit every 2 hours for brand mentions\n2. If mentions found → AI analyzes sentiment\n3. Negative mentions → Slack alert with draft response\n\n**To activate:** Say "connect reddit", "connect openai", "connect slack"`,
  },

  "AI Email Responder": {
    nodes: [
      trigger("Email: Support email received", "email", {
        howItStarts: "A customer sends a support email",
        setup:
          "Set up email forwarding to your workflow inbox, or connect helpdesk webhook (Zendesk, Freshdesk, Intercom)",
        inputFields: [
          { name: "sender_email", type: "string" },
          { name: "email_subject", type: "string" },
          { name: "email_body", type: "string" },
          { name: "kb_context", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI draft response from knowledge base",
        280,
        'Draft a helpful support response. Use the knowledge base context. Respond JSON: {draft_response: string, confidence: number, ticket_category: string, needs_human: \\"true\\" | \\"false\\", matched_kb_topic: string}',
        "From: {sender_email}\\nSubject: {email_subject}\\nBody: {email_body}\\nKnowledge Base: {kb_context}",
        [
          { field: "draft_response", as: "draft_response", type: "string" },
          { field: "confidence", as: "confidence", type: "number" },
          { field: "ticket_category", as: "ticket_category", type: "string" },
          { field: "needs_human", as: "needs_human", type: "string" },
          { field: "matched_kb_topic", as: "matched_kb_topic", type: "string" },
        ],
      ),
      n("3", "condition", "Confident enough? (>85%)", 520, {
        conditionData: [
          {
            variable: "confidence",
            value: "85",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      n("4", "approvalGate", "Human reviews the draft", 760, {
        approvalGateData: {
          message:
            "Review AI response for: {email_subject}\\n\\nDraft: {draft_response}",
        },
      }),
      emailNode(
        "5",
        "Send approved response",
        1000,
        "{sender_email}",
        "Re: {email_subject}",
        "<p>{draft_response}</p>",
      ),
      slackNode(
        "6",
        "Escalate low-confidence to support",
        760,
        "🆘 AI unsure (confidence: {confidence}%):\\nFrom: {sender_email}\\nSubject: {email_subject}\\nCategory: {ticket_category}",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("3", "6")],
    summary: `**AI Email Responder**\n\n1. Support email arrives\n2. AI drafts response using your knowledge base\n3. Confidence > 85% → human reviews → sends\n4. Low confidence → escalates to Slack\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  "YouTube Video Pipeline": {
    nodes: [
      trigger("Manual: Submit video topic", "manual", {
        howItStarts: "You type a video topic in the chat",
        setup:
          "Type your video idea in chat (e.g., 'How to automate your business') or POST via API",
        inputFields: [
          {
            name: "video_topic",
            type: "string",
            example: "How to automate your business in 2025",
          },
          { name: "channel_niche", type: "string" },
          { name: "target_audience", type: "string" },
          { name: "production_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI write video script",
        280,
        "Write a YouTube video script. Include intro hook, 3 main sections, and outro with CTA. Respond JSON: {video_title: string, video_description: string, hook: string, script_content: string, outro: string, tags: string, estimated_length_min: number}",
        "Topic: {video_topic}, Channel niche: {channel_niche}, Target audience: {target_audience}",
        [
          { field: "video_title", as: "video_title", type: "string" },
          { field: "hook", as: "video_hook", type: "string" },
          { field: "script_content", as: "script_content", type: "string" },
          { field: "outro", as: "outro", type: "string" },
          {
            field: "estimated_length_min",
            as: "estimated_length_min",
            type: "number",
          },
        ],
      ),
      aiNode(
        "3",
        "AI create thumbnail brief",
        520,
        "Create a thumbnail concept for this video. Respond JSON: {main_text: string, background_description: string, emotion: string, color_scheme: string}",
        "Video title: {video_title}\\nTopic: {video_topic}",
        [
          { field: "main_text", as: "thumb_text", type: "string" },
          { field: "background_description", as: "thumb_bg", type: "string" },
          { field: "emotion", as: "thumb_emotion", type: "string" },
          { field: "color_scheme", as: "thumb_colors", type: "string" },
        ],
        460,
      ),
      n("4", "approvalGate", "Review script and thumbnail brief", 760, {
        approvalGateData: {
          message: "Review video script and thumbnail brief for: {video_title}",
        },
      }),
      emailNode(
        "5",
        "Send to production team",
        1000,
        "{production_email}",
        "Video Brief: {video_title}",
        "<h2>{video_title}</h2><h3>Hook</h3><p>{video_hook}</p><h3>Script</h3><p>{script_content}</p><h3>Thumbnail</h3><p>Text: {thumb_text}, Emotion: {thumb_emotion}</p><p>Est. length: {estimated_length_min} min</p>",
      ),
    ],
    edges: [e("1", "2"), e("1", "3"), e("2", "4"), e("3", "4"), e("4", "5")],
    summary: `**YouTube Video Pipeline**\n\n1. Submit a video topic\n2. AI writes script (hook, sections, outro) + thumbnail brief\n3. You review and approve\n4. Sent to production team\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "Facebook Ad Campaign Builder": {
    nodes: [
      trigger("Manual: Submit campaign brief", "manual", {
        howItStarts: "You provide campaign details in the chat",
        setup:
          "Type your campaign brief: product, audience, budget, goal. Or POST via API",
        inputFields: [
          { name: "product_name", type: "string" },
          { name: "target_audience", type: "string" },
          { name: "daily_budget", type: "number" },
          { name: "campaign_goal", type: "string", example: "conversions" },
          { name: "ads_manager_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI generate ad copy variations",
        280,
        "Write 3 Facebook ad copy variations. Each should have a headline (40 chars), primary text (125 chars), and CTA. Respond JSON: {ad_variations: string, target_audience_suggestion: string, recommended_budget_split: string}",
        "Product: {product_name}, Audience: {target_audience}, Budget: ${daily_budget}, Goal: {campaign_goal}",
        [
          { field: "ad_variations", as: "ad_variations", type: "string" },
          {
            field: "target_audience_suggestion",
            as: "audience_suggestion",
            type: "string",
          },
          {
            field: "recommended_budget_split",
            as: "budget_split",
            type: "string",
          },
        ],
      ),
      n("3", "approvalGate", "Review ad copies", 520, {
        approvalGateData: {
          message: "Review 3 ad variations for {product_name}",
        },
      }),
      emailNode(
        "4",
        "Send to ads manager",
        760,
        "{ads_manager_email}",
        "Facebook Ad Copy: {product_name}",
        "<h2>Campaign: {product_name}</h2><p>{ad_variations}</p><p>Target: {audience_suggestion}</p><p>Budget split: {budget_split}</p>",
      ),
      slackNode(
        "5",
        "Notify marketing team",
        760,
        "📣 Facebook ad copy ready for {product_name}\\nBudget: ${daily_budget}/day\\nGoal: {campaign_goal}",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5")],
    summary: `**Facebook Ad Campaign Builder**\n\n1. Submit campaign brief (product, audience, budget)\n2. AI generates 3 ad copy variations\n3. You review and approve\n4. Sent to ads manager + team notified\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  "Ad Performance Dashboard": {
    nodes: [
      trigger("Schedule: Every Monday at 8:00 AM", "schedule", {
        schedule: "Every Monday at 8:00 AM",
        howItStarts: "Pulls weekly ad performance metrics from Facebook",
        setup:
          "Activate and say 'connect facebook' to set up your Facebook Ads API token",
        inputFields: [
          { name: "ad_account_id", type: "string" },
          { name: "stakeholder_email", type: "string" },
        ],
      }),
      n("2", "hitApi", "Get Facebook Ad stats", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://graph.facebook.com/v19.0/act_{ad_account_id}/insights?fields=spend,impressions,clicks,cpc,ctr,conversions&date_preset=last_7d",
          headers: [{ key: "Authorization", value: "Bearer {facebook_token}" }],
          variables: [
            {
              name: "data[0].spend",
              customName: "weekly_spend",
              type: "string",
            },
            {
              name: "data[0].impressions",
              customName: "impressions",
              type: "string",
            },
            { name: "data[0].clicks", customName: "clicks", type: "string" },
            { name: "data[0].ctr", customName: "ctr", type: "string" },
            { name: "data[0].cpc", customName: "cpc", type: "string" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI analyze performance",
        520,
        "Analyze these ad metrics. Compare to benchmarks. Respond JSON: {performance_grade: string, roas_assessment: string, top_recommendation: string, should_increase_budget: boolean, ad_summary: string}",
        "Spend: ${weekly_spend}, Impressions: {impressions}, Clicks: {clicks}, CTR: {ctr}%, CPC: ${cpc}",
        [
          {
            field: "performance_grade",
            as: "performance_grade",
            type: "string",
          },
          { field: "roas_assessment", as: "roas_assessment", type: "string" },
          {
            field: "top_recommendation",
            as: "top_recommendation",
            type: "string",
          },
          {
            field: "should_increase_budget",
            as: "should_increase_budget",
            type: "string",
          },
          { field: "ad_summary", as: "ad_summary", type: "string" },
        ],
      ),
      emailNode(
        "4",
        "Email weekly report",
        760,
        "{stakeholder_email}",
        "Weekly Ad Performance Report",
        "<h2>Ad Performance Summary</h2><p>{ad_summary}</p><p>Grade: {performance_grade}</p><p>ROAS: {roas_assessment}</p><p>Top recommendation: {top_recommendation}</p><p>Spend: ${weekly_spend} | Clicks: {clicks} | CTR: {ctr}%</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4")],
    summary: `**Ad Performance Dashboard**\n\n1. Weekly pull of Facebook Ad metrics\n2. AI grades performance and recommends actions\n3. Report emailed to stakeholders\n\n**To activate:** Say "connect facebook", "connect openai", "connect email"`,
  },

  "Expense Report Processor": {
    nodes: [
      trigger("Form: Expense submitted", "webhook_form", {
        source: "Expense Form / Slack Bot",
        howItStarts: "An employee submits an expense report",
        setup:
          "Create an expense form (Outgrow Forms, Outgrow Forms) → Webhooks → paste your workflow URL. Include: employee name, amount, description, receipt",
        inputFields: [
          { name: "employee_name", type: "string" },
          { name: "expense_amount", type: "number" },
          { name: "expense_description", type: "string" },
          { name: "receipt_details", type: "string" },
          { name: "manager_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI validate and categorize",
        280,
        'Validate this expense against company policy (max $500 without approval, no alcohol). Categorize it. Respond JSON: {expense_category: string, is_within_policy: \\"true\\" | \\"false\\", needs_approval: \\"true\\" | \\"false\\", violation_reason: string}',
        "Employee: {employee_name}, Amount: ${expense_amount}, Description: {expense_description}, Receipt: {receipt_details}",
        [
          { field: "expense_category", as: "expense_category", type: "string" },
          { field: "is_within_policy", as: "is_within_policy", type: "string" },
          { field: "needs_approval", as: "needs_approval", type: "string" },
          { field: "violation_reason", as: "violation_reason", type: "string" },
        ],
      ),
      n("3", "condition", "Needs approval? (>$500 or policy issue)", 520, {
        conditionData: [
          {
            variable: "needs_approval",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "4",
        "Route to manager for approval",
        760,
        "{manager_email}",
        "Expense Approval Needed: ${expense_amount}",
        "<p>{employee_name} submitted ${expense_amount} for: {expense_description}</p><p>Category: {expense_category}</p><p>{violation_reason}</p>",
      ),
      n(
        "5",
        "hitApi",
        "Auto-approve → log to Google Sheets",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Expenses:append?valueInputOption=RAW",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "values",
                value:
                  '[["{employee_name}","{expense_amount}","{expense_category}","{expense_description}","auto-approved"]]',
              },
            ],
            variables: [
              {
                name: "updates.updatedRows",
                customName: "rows_added",
                type: "number",
              },
            ],
          },
        },
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5")],
    summary: `**Expense Report Processor**\n\n1. Expense submitted\n2. AI validates against company policy + categorizes\n3. Within policy → auto-approved, logged to Sheets\n4. Over limit or policy issue → routed to manager\n\n**To activate:** Say "connect openai", "connect google sheets", "connect email"`,
  },

  "Subscription Renewal Alert": {
    nodes: [
      trigger("Schedule: Every Monday at 9:00 AM", "schedule", {
        schedule: "Every Monday at 9:00 AM",
        howItStarts: "Weekly check of Stripe for subscriptions renewing soon",
        setup:
          "Activate and say 'connect stripe' to set up your Stripe API key",
        inputFields: [
          { name: "today_date", type: "string" },
          { name: "finance_email", type: "string" },
        ],
      }),
      n("2", "hitApi", "Get subscriptions from Stripe", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.stripe.com/v1/subscriptions?status=active&limit=100",
          headers: [{ key: "Authorization", value: "Bearer {stripe_key}" }],
          variables: [
            { name: "data", customName: "active_subscriptions", type: "array" },
            { name: "has_more", customName: "has_more", type: "boolean" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI find upcoming renewals",
        520,
        "Find subscriptions renewing in the next 30 days. For each, suggest whether to renew, downgrade, or cancel based on usage. Respond JSON: {renewals_summary: string, renewal_count: number, total_cost: number}",
        "Today: {today_date}\\nSubscriptions: {active_subscriptions}",
        [
          { field: "renewals_summary", as: "renewals_summary", type: "string" },
          { field: "renewal_count", as: "renewal_count", type: "number" },
          { field: "total_cost", as: "total_cost", type: "number" },
        ],
      ),
      n("4", "condition", "Any renewals coming up?", 760, {
        conditionData: [
          {
            variable: "renewal_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "5",
        "Email renewal summary",
        1000,
        "{finance_email}",
        "SaaS Renewal Alert: {renewal_count} subscriptions in 30 days",
        "<h2>{renewal_count} Upcoming Renewals (${total_cost} total)</h2><p>{renewals_summary}</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5")],
    summary: `**Subscription Renewal Alert**\n\n1. Weekly check of Stripe subscriptions\n2. AI finds renewals in next 30 days + suggests actions\n3. Summary emailed to finance team\n\n**To activate:** Say "connect stripe", "connect openai", "connect email"`,
  },

  "Interview Scheduler": {
    nodes: [
      trigger("Webhook: Candidate shortlisted", "webhook_app", {
        source: "Greenhouse / Lever / Internal Tool",
        event: "candidate.shortlisted",
        howItStarts: "A recruiter shortlists a candidate for interview",
        setup:
          "In your ATS → Webhooks → Event: 'Candidate moved to interview stage' → URL: your workflow URL",
        inputFields: [
          { name: "candidate_name", type: "string" },
          { name: "candidate_email", type: "string" },
          { name: "job_title", type: "string" },
          { name: "interviewer_name", type: "string" },
          { name: "interviewer_email", type: "string" },
          { name: "available_slots", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI draft interview invite",
        280,
        "Write a professional interview invitation email. Respond JSON: {invite_subject: string, invite_body: string, suggested_questions: string}",
        "Candidate: {candidate_name}, Role: {job_title}, Interviewer: {interviewer_name}, Suggested times: {available_slots}",
        [
          { field: "invite_subject", as: "invite_subject", type: "string" },
          { field: "invite_body", as: "invite_body", type: "string" },
          {
            field: "suggested_questions",
            as: "suggested_questions",
            type: "string",
          },
        ],
      ),
      emailNode(
        "3",
        "Send interview invite",
        520,
        "{candidate_email}",
        "{invite_subject}",
        "<p>{invite_body}</p>",
      ),
      emailNode(
        "4",
        "Notify interviewer",
        520,
        "{interviewer_email}",
        "Interview Scheduled: {candidate_name} for {job_title}",
        "<p>You have an interview with {candidate_name} for the {job_title} role.</p><h3>Suggested questions:</h3><p>{suggested_questions}</p>",
        460,
      ),
      slackNode(
        "5",
        "Update #hiring channel",
        760,
        "📅 Interview scheduled: {candidate_name} for {job_title} with {interviewer_name}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("2", "4"), e("3", "5")],
    summary: `**Interview Scheduler**\n\n1. Candidate shortlisted\n2. AI writes personalized interview invite + suggests questions\n3. Invite sent to candidate + interviewer notified\n4. Hiring channel updated on Slack\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  "Job Board Multi-Poster": {
    nodes: [
      trigger("Manual: Submit job description", "manual", {
        howItStarts:
          "You provide a job description to adapt for multiple platforms",
        setup:
          "Type the role details in chat: title, description, requirements. Or POST via API",
        inputFields: [
          { name: "job_title", type: "string" },
          { name: "job_description", type: "string" },
          { name: "requirements", type: "string" },
          { name: "hr_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI adapt for LinkedIn",
        280,
        "Rewrite this job description optimized for LinkedIn. Professional tone, emphasize growth. Respond JSON: {linkedin_title: string, linkedin_description: string, linkedin_skills: string}",
        "Role: {job_title}\\nDescription: {job_description}\\nRequirements: {requirements}",
        [
          { field: "linkedin_title", as: "linkedin_title", type: "string" },
          {
            field: "linkedin_description",
            as: "linkedin_description",
            type: "string",
          },
          { field: "linkedin_skills", as: "linkedin_skills", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "AI adapt for Indeed",
        280,
        "Rewrite this job description for Indeed. Focus on benefits, salary range, clear requirements. Respond JSON: {indeed_title: string, indeed_description: string, benefits_highlight: string}",
        "Role: {job_title}\\nDescription: {job_description}\\nRequirements: {requirements}",
        [
          { field: "indeed_title", as: "indeed_title", type: "string" },
          {
            field: "indeed_description",
            as: "indeed_description",
            type: "string",
          },
          {
            field: "benefits_highlight",
            as: "benefits_highlight",
            type: "string",
          },
        ],
        460,
      ),
      emailNode(
        "4",
        "Email all versions to HR",
        520,
        "{hr_email}",
        "Job Posting Ready: {job_title}",
        "<h2>LinkedIn Version</h2><p>{linkedin_title}</p><p>{linkedin_description}</p><h2>Indeed Version</h2><p>{indeed_title}</p><p>{indeed_description}</p>",
      ),
      slackNode(
        "5",
        "Notify #hiring",
        520,
        "💼 Job posting ready for {job_title} — LinkedIn + Indeed versions created!",
        460,
      ),
    ],
    edges: [e("1", "2"), e("1", "3"), e("2", "4"), e("3", "4"), e("2", "5")],
    summary: `**Job Board Multi-Poster**\n\n1. Submit job description once\n2. AI adapts for LinkedIn (professional) + Indeed (benefits-focused)\n3. Both versions emailed to HR for review\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  "CSV Import Processor": {
    nodes: [
      trigger("Manual: Submit CSV data", "manual", {
        howItStarts: "You submit CSV data (as JSON) for cleaning and import",
        setup:
          "Paste your CSV data as JSON array in chat, or POST via API with {csv_data: [...], data_owner_email: '...'}",
        inputFields: [
          { name: "csv_data", type: "string" },
          { name: "data_owner_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI validate and clean data",
        280,
        "Validate and clean this data. Fix formatting issues, standardize phone/email, flag invalid rows. Respond JSON: {valid_rows: number, invalid_rows: number, issues_found: string, cleaned_data: string, first_invalid_reason: string}",
        "{csv_data}",
        [
          { field: "valid_rows", as: "valid_rows", type: "number" },
          { field: "invalid_rows", as: "invalid_rows", type: "number" },
          { field: "issues_found", as: "issues_found", type: "string" },
          { field: "cleaned_data", as: "cleaned_data", type: "string" },
          {
            field: "first_invalid_reason",
            as: "first_invalid_reason",
            type: "string",
          },
        ],
      ),
      n("3", "condition", "Any invalid rows?", 520, {
        conditionData: [
          {
            variable: "invalid_rows",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "4",
        "Alert: data quality issues",
        760,
        "{data_owner_email}",
        "CSV Import: {invalid_rows} rows need attention",
        "<p>{valid_rows} rows clean, {invalid_rows} need fixes.</p><p>Issues: {issues_found}</p><p>First issue: {first_invalid_reason}</p>",
      ),
      n(
        "5",
        "hitApi",
        "Push clean rows to Google Sheets",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Import:append?valueInputOption=RAW",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [{ key: "values", value: "{cleaned_data}" }],
            variables: [
              {
                name: "updates.updatedRows",
                customName: "rows_imported",
                type: "number",
              },
            ],
          },
        },
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("2", "5")],
    summary: `**CSV Import Processor**\n\n1. Submit CSV data\n2. AI validates, cleans, and standardizes\n3. Clean rows pushed to Google Sheets\n4. Invalid rows → email alert with details\n\n**To activate:** Say "connect openai", "connect google sheets", "connect email"`,
  },

  "AI Meeting Notes": {
    nodes: [
      trigger("Manual: Submit meeting transcript", "manual", {
        howItStarts: "You paste a meeting transcript for AI analysis",
        setup:
          "Paste the transcript in chat, or POST via API. You can also connect Zoom/Teams for auto-transcription",
        inputFields: [
          { name: "transcript_text", type: "string" },
          { name: "attendees_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI extract structured notes",
        280,
        'Extract structured notes from this meeting transcript. Respond JSON: {meeting_title: string, notes_summary: string, decisions_text: string, action_items_text: string, follow_up_needed: \\"true\\" | \\"false\\"}',
        "Transcript: {transcript_text}",
        [
          { field: "meeting_title", as: "meeting_title", type: "string" },
          { field: "notes_summary", as: "notes_summary", type: "string" },
          { field: "decisions_text", as: "decisions_text", type: "string" },
          {
            field: "action_items_text",
            as: "action_items_text",
            type: "string",
          },
          { field: "follow_up_needed", as: "follow_up_needed", type: "string" },
        ],
      ),
      n("3", "condition", "Any action items?", 520, {
        conditionData: [
          {
            variable: "follow_up_needed",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "4",
        "Email meeting summary",
        760,
        "{attendees_email}",
        "Meeting Notes: {meeting_title}",
        "<h2>{meeting_title}</h2><h3>Summary</h3><p>{notes_summary}</p><h3>Decisions</h3><p>{decisions_text}</p><h3>Action Items</h3><p>{action_items_text}</p>",
      ),
      slackNode(
        "5",
        "Post action items to Slack",
        760,
        "📝 Meeting: *{meeting_title}*\\nAction items: {action_items_text}",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5")],
    summary: `**AI Meeting Notes**\n\n1. Submit meeting transcript\n2. AI extracts summary, decisions, and action items\n3. If action items exist → email to attendees + post to Slack\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  "AI Image Generator": {
    nodes: [
      trigger("Manual: Describe the image you want", "manual", {
        howItStarts: "You describe an image in the chat",
        setup:
          "Type what you want to see (e.g., 'A futuristic city at sunset') or POST via API with {image_description: '...'}",
        inputFields: [
          {
            name: "image_description",
            type: "string",
            example: "A futuristic city at sunset, cyberpunk style",
          },
          { name: "requester_email", type: "string" },
        ],
      }),
      n("2", "hitApi", "Generate image with DALL-E", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/images/generations",
          headers: [
            { key: "Authorization", value: "Bearer {openai_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "model", value: "dall-e-3" },
            { key: "prompt", value: "{image_description}" },
            { key: "n", value: "1" },
            { key: "size", value: "1024x1024" },
          ],
          variables: [
            { name: "data[0].url", customName: "image_url", type: "string" },
            {
              name: "data[0].revised_prompt",
              customName: "revised_prompt",
              type: "string",
            },
          ],
        },
      }),
      emailNode(
        "3",
        "Email the generated image",
        520,
        "{requester_email}",
        "Your AI Image is Ready",
        "<p>Prompt: {image_description}</p><p>Revised prompt: {revised_prompt}</p><p><img src='{image_url}' style='max-width:600px' /></p>",
      ),
      slackNode(
        "4",
        "Share in Slack",
        520,
        "🎨 AI image generated!\\nPrompt: {image_description}\\nImage: {image_url}",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("2", "4")],
    summary: `**AI Image Generator**\n\n1. Describe the image you want\n2. DALL-E 3 generates it\n3. Emailed to you + shared on Slack\n\nUses real DALL-E 3 API — returns actual image URL.\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  "Low Stock Alert": {
    nodes: [
      trigger("Schedule: Every day at 7:00 AM", "schedule", {
        schedule: "Every day at 7:00 AM",
        howItStarts: "Checks Shopify inventory levels daily",
        setup:
          "Activate and say 'connect shopify' to set up your Shopify API token",
        inputFields: [],
      }),
      n("2", "hitApi", "Get inventory from Shopify", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shop_domain}.myshopify.com/admin/api/2024-01/inventory_levels.json?limit=50",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          ],
          variables: [
            {
              name: "inventory_levels",
              customName: "inventory_data",
              type: "array",
            },
          ],
        },
      }),
      aiNode(
        "3",
        "AI find items below threshold",
        520,
        "Find inventory items with available quantity below 5. Respond JSON: {low_items_list: string, low_count: number, reorder_urgency: string}",
        "Inventory: {inventory_data}, Threshold: 5",
        [
          { field: "low_items_list", as: "low_items_list", type: "string" },
          { field: "low_count", as: "low_count", type: "number" },
          { field: "reorder_urgency", as: "reorder_urgency", type: "string" },
        ],
      ),
      n("4", "condition", "Any low stock?", 760, {
        conditionData: [
          {
            variable: "low_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "5",
        "Alert purchasing",
        1000,
        "📦 Low stock alert! {low_count} items below threshold\\nUrgency: {reorder_urgency}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5")],
    summary: `**Low Stock Alert**\n\n1. Daily Shopify inventory check\n2. AI finds items below threshold (qty < 5)\n3. Alerts purchasing team on Slack\n\n**To activate:** Say "connect shopify", "connect openai", "connect slack"`,
  },

  "Review Request Automation": {
    nodes: [
      trigger("Shopify: Order delivered", "webhook_app", {
        source: "Shopify",
        event: "orders/fulfilled",
        howItStarts: "A Shopify order is marked as fulfilled/delivered",
        setup:
          "In Shopify → Settings → Notifications → Webhooks → Create webhook → Event: 'Order fulfillment' → URL: your workflow URL. The AI will read whatever data Shopify sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the order data",
        180,
        "Read this Shopify order fulfillment webhook data and extract the customer name, customer email, purchased product name, and order number. Different Shopify stores send data in different formats — find the customer name, customer email, purchased product, and order number regardless of field naming. Respond JSON: {customer_name: string, customer_email: string, purchased_product: string, order_num: string}",
        "{webhook_payload}",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          {
            field: "purchased_product",
            as: "purchased_product",
            type: "string",
          },
          { field: "order_num", as: "order_num", type: "string" },
        ],
      ),
      n("3", "delayAction", "Wait 7 days", 380, {
        delayAction: { time: 7, timeFormat: "days" },
      }),
      aiNode(
        "4",
        "AI write personalized review request",
        580,
        "Write a friendly review request email. Mention their specific product. Respond JSON: {review_subject: string, review_body_html: string, review_link_text: string}",
        "Customer: {customer_name}, Product: {purchased_product}, Order: #{order_num}",
        [
          { field: "review_subject", as: "review_subject", type: "string" },
          { field: "review_body_html", as: "review_body_html", type: "string" },
          { field: "review_link_text", as: "review_link_text", type: "string" },
        ],
      ),
      emailNode(
        "5",
        "Send review request",
        780,
        "{customer_email}",
        "{review_subject}",
        "{review_body_html}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5")],
    summary: `**Review Request Automation**\n\n1. Order marked delivered (Shopify webhook)\n2. **AI reads the data** and extracts customer name, email, product, order number\n3. Wait 7 days for customer to use product\n4. AI writes personalized review request\n5. Email sent to customer\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "Calendar → Task Creator": {
    nodes: [
      trigger("Calendar: Meeting ended", "webhook_app", {
        source: "Google Calendar / Outlook",
        event: "event.ended",
        howItStarts: "A calendar meeting ends",
        setup:
          "Connect Google Calendar via Zapier or use Microsoft Graph webhooks → Event: 'Event ended' → URL: your workflow URL. The AI will read whatever data your calendar sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads calendar data and extracts action items",
        280,
        "Read this calendar webhook data and extract the meeting title, meeting notes, attendees, and attendees email. Then extract action items from the notes. Different calendar systems send data in different formats — find the meeting title, meeting notes, attendees, and attendees email regardless of field naming. Respond JSON: {cal_meeting_title: string, cal_meeting_notes: string, cal_attendees: string, cal_attendees_email: string, tasks_list: string, task_count: number, cal_meeting_summary: string}",
        "{webhook_payload}",
        [
          {
            field: "cal_meeting_title",
            as: "cal_meeting_title",
            type: "string",
          },
          {
            field: "cal_meeting_notes",
            as: "cal_meeting_notes",
            type: "string",
          },
          { field: "cal_attendees", as: "cal_attendees", type: "string" },
          {
            field: "cal_attendees_email",
            as: "cal_attendees_email",
            type: "string",
          },
          { field: "tasks_list", as: "tasks_list", type: "string" },
          { field: "task_count", as: "task_count", type: "number" },
          {
            field: "cal_meeting_summary",
            as: "cal_meeting_summary",
            type: "string",
          },
        ],
      ),
      n("3", "condition", "Any tasks extracted?", 520, {
        conditionData: [
          {
            variable: "task_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "4",
        "Post tasks to Slack",
        760,
        "✅ {task_count} action items from *{cal_meeting_title}*:\\n{tasks_list}",
      ),
      emailNode(
        "5",
        "Email tasks to attendees",
        760,
        "{cal_attendees_email}",
        "Action Items: {cal_meeting_title}",
        "<h2>Tasks from {cal_meeting_title}</h2><p>{cal_meeting_summary}</p><p>{tasks_list}</p>",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5")],
    summary: `**Calendar → Task Creator**\n\n1. Meeting ends (calendar webhook)\n2. **AI reads the data** and extracts meeting title, notes, attendees, then extracts action items\n3. Tasks posted to Slack + emailed to attendees\n\n**To activate:** Say "connect openai", "connect slack", "connect email"`,
  },

  "Viral Content Finder": {
    nodes: [
      trigger("Schedule: Every 4 hours", "schedule", {
        schedule: "Every 4 hours",
        howItStarts: "Scans Reddit for trending content in your niche",
        setup:
          "Activate and say 'connect reddit'. Set your niche_subreddit in the API node",
        inputFields: [
          { name: "niche_subreddit", type: "string", example: "entrepreneur" },
        ],
      }),
      n("2", "hitApi", "Get trending posts from Reddit", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://oauth.reddit.com/r/{niche_subreddit}/top.json?t=day&limit=10",
          headers: [{ key: "Authorization", value: "Bearer {reddit_token}" }],
          variables: [
            { name: "data.children", customName: "top_posts", type: "array" },
            { name: "data.dist", customName: "post_count", type: "number" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI find content opportunities",
        520,
        "Analyze these trending posts. Find content ideas we can create. Respond JSON: {opportunities_list: string, top_opportunity: string, opportunity_count: number}",
        "Niche: {niche_subreddit}\\nTrending: {top_posts}",
        [
          {
            field: "opportunities_list",
            as: "opportunities_list",
            type: "string",
          },
          { field: "top_opportunity", as: "top_opportunity", type: "string" },
          {
            field: "opportunity_count",
            as: "opportunity_count",
            type: "number",
          },
        ],
      ),
      n("4", "condition", "Found opportunities?", 760, {
        conditionData: [
          {
            variable: "opportunity_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "5",
        "Post to #content-ideas",
        1000,
        "🔥 {opportunity_count} viral content opportunities found!\\nTop: {top_opportunity}\\n\\n{opportunities_list}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5")],
    summary: `**Viral Content Finder**\n\n1. Scans Reddit every 4 hours for trending posts\n2. AI identifies content opportunities in your niche\n3. Best ideas posted to Slack\n\n**To activate:** Say "connect reddit", "connect openai", "connect slack"`,
  },

  "Invoice Auto-Sender": {
    nodes: [
      trigger("Webhook: Project marked complete", "webhook_app", {
        source: "Asana / Monday / ClickUp",
        event: "project.completed",
        howItStarts:
          "A project is marked complete in your project management tool",
        setup:
          "In your PM tool → Integrations → Webhooks → Event: 'Project completed' → URL: your workflow URL. The AI will read whatever data your PM tool sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads project data and generates invoice",
        280,
        "Read this project management webhook data and extract the client name, client email, project name, total hours, hourly rate, and any additional charges. Then generate invoice line items. Different PM tools send data in different formats — find the client name, client email, project name, total hours, hourly rate, and extras regardless of field naming. Respond JSON: {inv_client_name: string, inv_client_email: string, inv_project_name: string, inv_total_hours: number, inv_hourly_rate: number, inv_extras: string, invoice_number: string, line_items: string, subtotal: number, tax: number, total: number, due_date: string, payment_terms: string}",
        "{webhook_payload}",
        [
          { field: "inv_client_name", as: "inv_client_name", type: "string" },
          { field: "inv_client_email", as: "inv_client_email", type: "string" },
          { field: "inv_project_name", as: "inv_project_name", type: "string" },
          { field: "inv_total_hours", as: "inv_total_hours", type: "number" },
          { field: "inv_hourly_rate", as: "inv_hourly_rate", type: "number" },
          { field: "inv_extras", as: "inv_extras", type: "string" },
          { field: "invoice_number", as: "invoice_number", type: "string" },
          { field: "line_items", as: "line_items", type: "string" },
          { field: "total", as: "invoice_total", type: "number" },
          { field: "due_date", as: "due_date", type: "string" },
          { field: "payment_terms", as: "payment_terms", type: "string" },
        ],
      ),
      emailNode(
        "3",
        "Send invoice to client",
        520,
        "{inv_client_email}",
        "Invoice #{invoice_number} — {inv_project_name}",
        "<h2>Invoice #{invoice_number}</h2><p>Client: {inv_client_name}</p><p>Project: {inv_project_name}</p><p>{line_items}</p><p><strong>Total: ${invoice_total}</strong></p><p>Due: {due_date}</p><p>Terms: {payment_terms}</p>",
      ),
      n("4", "delayAction", "Wait 7 days", 760, {
        delayAction: { time: 7, timeFormat: "days" },
      }),
      emailNode(
        "5",
        "Follow-up if unpaid",
        1000,
        "{inv_client_email}",
        "Friendly Reminder: Invoice #{invoice_number}",
        "<p>Hi {inv_client_name},</p><p>Just a reminder that invoice #{invoice_number} for ${invoice_total} is due {due_date}.</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5")],
    summary: `**Invoice Auto-Sender**\n\n1. Project marked complete\n2. **AI reads the data** and extracts client name, email, project details, then generates invoice\n3. Invoice emailed to client\n4. 7-day follow-up if unpaid\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "Daily Expense Logger": {
    nodes: [
      trigger("Email: Receipt forwarded", "email", {
        howItStarts: "You forward a receipt email to your workflow inbox",
        setup:
          "Set up a forwarding rule: forward receipt emails to your workflow email address (shown in workflow settings)",
        inputFields: [
          { name: "sender", type: "string" },
          { name: "email_subject", type: "string" },
          { name: "email_body", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI extract expense details",
        280,
        "Extract expense details from this receipt/email. Respond JSON: {vendor: string, amount: number, currency: string, expense_category: string, expense_date: string, expense_description: string, is_business_expense: boolean}",
        "Email from: {sender}\\nSubject: {email_subject}\\nBody: {email_body}",
        [
          { field: "vendor", as: "vendor", type: "string" },
          { field: "amount", as: "expense_amount", type: "number" },
          { field: "currency", as: "currency", type: "string" },
          { field: "expense_category", as: "expense_category", type: "string" },
          { field: "expense_date", as: "expense_date", type: "string" },
          { field: "expense_description", as: "expense_desc", type: "string" },
        ],
      ),
      n("3", "hitApi", "Log to Google Sheets", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Expenses:append?valueInputOption=RAW",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{expense_date}","{vendor}","{expense_amount}","{currency}","{expense_category}","{expense_desc}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_added",
              type: "number",
            },
          ],
        },
      }),
      slackNode(
        "4",
        "Confirm logged",
        520,
        "🧮 Expense logged: ${expense_amount} at {vendor} ({expense_category})",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("2", "4")],
    summary: `**Daily Expense Logger**\n\n1. Forward a receipt email\n2. AI extracts vendor, amount, category, and date\n3. Logged to Google Sheets + confirmed on Slack\n\n**To activate:** Say "connect openai", "connect google sheets", "connect slack"`,
  },

  // ===================================================================
  // HR TEMPLATES
  // ===================================================================

  "Candidate Pipeline Tracker": {
    nodes: [
      trigger("Webhook: Candidate stage changed", "webhook_app", {
        source: "Greenhouse / Lever",
        event: "candidate.stage_changed",
        howItStarts:
          "Your ATS sends a notification when a candidate moves to a new stage",
        setup:
          "In your ATS (Greenhouse, Lever) → Webhooks → Event: 'Candidate stage changed' → paste your workflow URL. The AI will read whatever data your ATS sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the ATS webhook",
        280,
        "Read this ATS webhook data and extract the candidate name, candidate email, new pipeline stage, and job title. Different ATS systems send data in different formats — find the candidate name, candidate email, new stage, and job title regardless of field naming. Respond JSON: {candidate_name: string, candidate_email: string, new_stage: string, job_title: string}",
        "{webhook_payload}",
        [
          { field: "candidate_name", as: "candidate_name", type: "string" },
          { field: "candidate_email", as: "candidate_email", type: "string" },
          { field: "new_stage", as: "new_stage", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
        ],
      ),
      slackNode(
        "3",
        "Notify #hiring",
        520,
        "📋 {candidate_name} moved to *{new_stage}* for {job_title}",
      ),
      emailNode(
        "4",
        "Email candidate status update",
        520,
        "{candidate_email}",
        "Update on your application for {job_title}",
        "<p>Hi {candidate_name},</p><p>We wanted to let you know that your application for {job_title} has been updated. Your current status: {new_stage}.</p><p>We'll be in touch with next steps soon.</p>",
        460,
      ),
      n("5", "hitApi", "Log to Google Sheets", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Pipeline:append?valueInputOption=RAW",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{candidate_name}","{candidate_email}","{new_stage}","{job_title}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_added",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [e("1", "2"), e("2", "3"), e("2", "4"), e("3", "5")],
    summary: `**Candidate Pipeline Tracker**\n\n1. ATS sends webhook when candidate changes stage\n2. **AI reads the data** and extracts candidate name, email, new stage, job title\n3. Slack notification to #hiring\n4. Status update email to candidate\n5. Logged to Google Sheets\n\n**To activate:** Say "connect openai", "connect slack", "connect email", "connect google sheets"`,
  },

  "Employee Offboarding": {
    nodes: [
      trigger("Webhook: Employee departure", "webhook_app", {
        source: "BambooHR / Gusto / Rippling",
        event: "employee.terminated",
        howItStarts:
          "Your HR system sends a notification when an employee departure is recorded",
        setup:
          "In your HR tool → Integrations → Webhooks → Event: 'Employee terminated/departed' → URL: your workflow URL. The AI will read whatever data your HR system sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the HR webhook",
        280,
        "Read this HR system webhook data and extract the employee name, employee email, department, last working day, and manager email. Different HR systems send data in different formats — find the employee name, employee email, department, last day, and manager email regardless of field naming. Respond JSON: {emp_name: string, emp_email: string, department: string, last_day: string, manager_email: string}",
        "{webhook_payload}",
        [
          { field: "emp_name", as: "emp_name", type: "string" },
          { field: "emp_email", as: "emp_email", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "last_day", as: "last_day", type: "string" },
          { field: "manager_email", as: "manager_email", type: "string" },
        ],
      ),
      emailNode(
        "3",
        "Email IT: revoke access",
        520,
        "it@company.com",
        "Access Revocation: {emp_name} — Last day {last_day}",
        "<p>Please revoke all access for {emp_name} ({emp_email}) from {department}.</p><p>Last working day: {last_day}</p><p>Ensure all accounts, badges, and system access are disabled by end of day on {last_day}.</p>",
      ),
      emailNode(
        "4",
        "Email employee: exit survey",
        520,
        "{emp_email}",
        "Your exit survey",
        "<p>Hi {emp_name},</p><p>As part of our offboarding process, we'd appreciate your feedback. Please complete this brief exit survey: [survey link]</p><p>Your last day with us is {last_day}. Thank you for your contributions to {department}.</p>",
        460,
      ),
      slackNode(
        "5",
        "Notify #hr",
        760,
        "📋 Offboarding initiated: {emp_name} ({department}) — Last day: {last_day}",
      ),
      n("6", "delayAction", "Wait 1 day", 960, {
        delayAction: { time: 1, timeFormat: "days" },
      }),
      emailNode(
        "7",
        "Remind manager: exit interview",
        1160,
        "{manager_email}",
        "Reminder: Schedule exit interview with {emp_name}",
        "<p>Hi,</p><p>Please schedule an exit interview with {emp_name} before their last day ({last_day}).</p><p>This helps us understand their experience and improve retention.</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("2", "4"),
      e("2", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**Employee Offboarding**\n\n1. HR system sends webhook for employee departure\n2. **AI reads the data** and extracts employee name, email, department, last day, manager email\n3. IT emailed to revoke access\n4. Employee emailed exit survey\n5. #hr notified on Slack\n6. 1-day delay → manager reminded to schedule exit interview\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  "PTO Request Automator": {
    nodes: [
      trigger("Form: PTO request submitted", "webhook_form", {
        source: "Internal PTO Form / Outgrow Forms",
        howItStarts: "An employee submits a PTO request form",
        setup:
          "Create a PTO form (Outgrow Forms, Outgrow Forms) → Webhooks → paste your workflow URL. The AI will read whatever data your form sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the PTO form",
        280,
        "Read this PTO request form data and extract the employee name, employee email, start date, end date, number of days requested, reason, and manager email. Different forms send data in different formats — find the employee name, employee email, start date, end date, days requested, reason, and manager email regardless of field naming. Respond JSON: {emp_name: string, emp_email: string, start_date: string, end_date: string, days_requested: number, reason: string, manager_email: string}",
        "{webhook_payload}",
        [
          { field: "emp_name", as: "emp_name", type: "string" },
          { field: "emp_email", as: "emp_email", type: "string" },
          { field: "start_date", as: "start_date", type: "string" },
          { field: "end_date", as: "end_date", type: "string" },
          { field: "days_requested", as: "days_requested", type: "number" },
          { field: "reason", as: "reason", type: "string" },
          { field: "manager_email", as: "manager_email", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "AI assess PTO request",
        520,
        'Assess this PTO request. Check if the dates seem reasonable and flag any potential conflicts. Respond JSON: {is_reasonable: \\"true\\" | \\"false\\", conflict_warning: string, recommendation: string}',
        "Employee: {emp_name}, Start: {start_date}, End: {end_date}, Days: {days_requested}, Reason: {reason}",
        [
          { field: "is_reasonable", as: "is_reasonable", type: "string" },
          { field: "conflict_warning", as: "conflict_warning", type: "string" },
          { field: "recommendation", as: "recommendation", type: "string" },
        ],
      ),
      n("4", "condition", "Is request reasonable?", 760, {
        conditionData: [
          {
            variable: "is_reasonable",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "5",
        "Email manager for approval",
        1000,
        "{manager_email}",
        "PTO Request: {emp_name} — {start_date} to {end_date}",
        "<p>{emp_name} has requested {days_requested} days off from {start_date} to {end_date}.</p><p>Reason: {reason}</p><p>AI assessment: {recommendation}</p>",
      ),
      slackNode(
        "6",
        "Notify #team",
        1000,
        "📅 PTO Request: {emp_name} requesting {days_requested} days ({start_date} to {end_date})",
        460,
      ),
      emailNode(
        "7",
        "Email employee: conflict found",
        1000,
        "{emp_email}",
        "PTO Request Update: Potential Conflict",
        "<p>Hi {emp_name},</p><p>Your PTO request for {start_date} to {end_date} has a potential issue:</p><p>{conflict_warning}</p><p>Recommendation: {recommendation}</p><p>Please review and resubmit if needed.</p>",
        840,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("4", "6"),
      e("4", "7"),
    ],
    summary: `**PTO Request Automator**\n\n1. Employee submits PTO form\n2. **AI reads the data** and extracts employee info, dates, reason, manager email\n3. AI assesses if request is reasonable and checks for conflicts\n4. Reasonable → emails manager for approval + notifies #team on Slack\n5. Not reasonable → emails employee with conflict details\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  // ===================================================================
  // FINANCE TEMPLATES
  // ===================================================================

  "Purchase Order Approval": {
    nodes: [
      trigger("Form: PO submission", "webhook_form", {
        source: "PO Submission Form / Outgrow Forms",
        howItStarts: "Someone submits a purchase order request form",
        setup:
          "Create a PO form (Outgrow Forms, Outgrow Forms) → Webhooks → paste your workflow URL. The AI will read whatever data your form sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the PO form",
        280,
        "Read this purchase order form data and extract the vendor name, PO amount, department, description, requester name, and requester email. Different forms send data in different formats — find the vendor name, PO amount, department, description, requester name, and requester email regardless of field naming. Respond JSON: {vendor_name: string, po_amount: number, department: string, description: string, requester_name: string, requester_email: string}",
        "{webhook_payload}",
        [
          { field: "vendor_name", as: "vendor_name", type: "string" },
          { field: "po_amount", as: "po_amount", type: "number" },
          { field: "department", as: "department", type: "string" },
          { field: "description", as: "description", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "AI validate against budget rules",
        520,
        'Validate this purchase order against budget rules. PO amounts over $5000 require VP approval. Assess the request. Respond JSON: {needs_vp_approval: \\"true\\" | \\"false\\", budget_assessment: string, approval_recommendation: string}',
        "Vendor: {vendor_name}, Amount: ${po_amount}, Department: {department}, Description: {description}, Requester: {requester_name}",
        [
          {
            field: "needs_vp_approval",
            as: "needs_vp_approval",
            type: "string",
          },
          {
            field: "budget_assessment",
            as: "budget_assessment",
            type: "string",
          },
          {
            field: "approval_recommendation",
            as: "approval_recommendation",
            type: "string",
          },
        ],
      ),
      n("4", "condition", "Needs VP approval? (>$5000)", 760, {
        conditionData: [
          {
            variable: "needs_vp_approval",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "5",
        "Email VP for approval",
        1000,
        "vp@company.com",
        "PO Approval Needed: ${po_amount} for {vendor_name}",
        "<p>{requester_name} ({department}) has submitted a PO for ${po_amount} to {vendor_name}.</p><p>Description: {description}</p><p>AI assessment: {budget_assessment}</p><p>Recommendation: {approval_recommendation}</p>",
      ),
      n(
        "6",
        "hitApi",
        "Auto-approve → log to Sheets",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/PurchaseOrders:append?valueInputOption=RAW",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "values",
                value:
                  '[["{vendor_name}","{po_amount}","{department}","{description}","{requester_name}","auto-approved"]]',
              },
            ],
            variables: [
              {
                name: "updates.updatedRows",
                customName: "rows_added",
                type: "number",
              },
            ],
          },
        },
        460,
      ),
      emailNode(
        "7",
        "Notify requester: approved",
        1240,
        "{requester_email}",
        "PO Approved: ${po_amount} for {vendor_name}",
        "<p>Hi {requester_name},</p><p>Your purchase order for ${po_amount} to {vendor_name} has been auto-approved.</p><p>{budget_assessment}</p>",
        460,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("4", "6"),
      e("6", "7"),
    ],
    summary: `**Purchase Order Approval**\n\n1. PO form submitted\n2. **AI reads the data** and extracts vendor, amount, department, description, requester\n3. AI validates against budget rules (>$5000 needs VP)\n4. Over $5000 → emails VP for approval\n5. Under $5000 → auto-approved, logged to Sheets, requester notified\n\n**To activate:** Say "connect openai", "connect email", "connect google sheets"`,
  },

  "Vendor Invoice Processor": {
    nodes: [
      trigger("Email: Vendor sends invoice", "email", {
        howItStarts: "A vendor sends an invoice via email",
        setup:
          "Forward vendor invoice emails to your workflow inbox, or set up auto-forwarding from your AP inbox",
        inputFields: [
          { name: "sender_email", type: "string" },
          { name: "email_subject", type: "string" },
          { name: "email_body", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI extract invoice details",
        280,
        "Read this vendor invoice email and extract the vendor name, invoice number, invoice amount, line items summary, and due date. Different vendors format invoices differently — find the vendor name, invoice number, invoice amount, line items summary, and due date regardless of format. Respond JSON: {vendor_name: string, invoice_number: string, invoice_amount: number, line_items_summary: string, due_date: string}",
        "From: {sender_email}\\nSubject: {email_subject}\\nBody: {email_body}",
        [
          { field: "vendor_name", as: "vendor_name", type: "string" },
          { field: "invoice_number", as: "invoice_number", type: "string" },
          { field: "invoice_amount", as: "invoice_amount", type: "number" },
          {
            field: "line_items_summary",
            as: "line_items_summary",
            type: "string",
          },
          { field: "due_date", as: "due_date", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "AI match PO and flag discrepancies",
        520,
        'Match this invoice against known purchase orders and flag any discrepancies (amount mismatch, unknown vendor, missing PO). Respond JSON: {matched_po: string, has_discrepancy: \\"true\\" | \\"false\\", discrepancy_detail: string}',
        "Vendor: {vendor_name}, Invoice: #{invoice_number}, Amount: ${invoice_amount}, Line items: {line_items_summary}, Due: {due_date}",
        [
          { field: "matched_po", as: "matched_po", type: "string" },
          { field: "has_discrepancy", as: "has_discrepancy", type: "string" },
          {
            field: "discrepancy_detail",
            as: "discrepancy_detail",
            type: "string",
          },
        ],
      ),
      n("4", "condition", "Has discrepancy?", 760, {
        conditionData: [
          {
            variable: "has_discrepancy",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "5",
        "Email AP: discrepancy found",
        1000,
        "ap@company.com",
        "Invoice Discrepancy: #{invoice_number} from {vendor_name}",
        "<p>Invoice #{invoice_number} from {vendor_name} has a discrepancy.</p><p>Amount: ${invoice_amount}</p><p>Matched PO: {matched_po}</p><p>Issue: {discrepancy_detail}</p><p>Line items: {line_items_summary}</p>",
      ),
      n(
        "6",
        "hitApi",
        "Auto-approve → log to Sheets",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Invoices:append?valueInputOption=RAW",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "values",
                value:
                  '[["{vendor_name}","{invoice_number}","{invoice_amount}","{due_date}","{matched_po}","auto-approved"]]',
              },
            ],
            variables: [
              {
                name: "updates.updatedRows",
                customName: "rows_added",
                type: "number",
              },
            ],
          },
        },
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("4", "6")],
    summary: `**Vendor Invoice Processor**\n\n1. Vendor sends invoice via email\n2. **AI reads the email** and extracts vendor name, invoice number, amount, line items, due date\n3. AI matches against known POs and flags discrepancies\n4. Discrepancy found → emails accounts payable\n5. No discrepancy → auto-approved, logged to Sheets\n\n**To activate:** Say "connect openai", "connect email", "connect google sheets"`,
  },

  "Budget Alert Monitor": {
    nodes: [
      trigger("Schedule: Daily at 8:00 AM", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts: "Checks spending data daily against budget thresholds",
        setup:
          "Activate this workflow and configure your spending data API endpoint in the next node",
        inputFields: [],
      }),
      n("2", "hitApi", "Get spending data", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.your-finance-tool.com/v1/spending/summary?period=current_month",
          headers: [
            { key: "Authorization", value: "Bearer {finance_api_key}" },
          ],
          variables: [
            { name: "data", customName: "spending_data", type: "object" },
            { name: "total_spend", customName: "total_spend", type: "number" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI analyze budget vs spending",
        520,
        "Compare this spending data to budget thresholds. Identify departments over budget. Respond JSON: {over_budget_departments: string, alert_count: number, budget_summary: string}",
        "Spending data: {spending_data}, Total spend: ${total_spend}",
        [
          {
            field: "over_budget_departments",
            as: "over_budget_departments",
            type: "string",
          },
          { field: "alert_count", as: "alert_count", type: "number" },
          { field: "budget_summary", as: "budget_summary", type: "string" },
        ],
      ),
      n("4", "condition", "Any departments over budget?", 760, {
        conditionData: [
          {
            variable: "alert_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "5",
        "Email finance team",
        1000,
        "finance@company.com",
        "Budget Alert: {alert_count} departments over budget",
        "<h2>Budget Alert</h2><p>{budget_summary}</p><p>Over budget: {over_budget_departments}</p>",
      ),
      slackNode(
        "6",
        "Alert #finance",
        1000,
        "⚠️ Budget Alert: {alert_count} departments over budget!\\n{budget_summary}\\n\\n{over_budget_departments}",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("4", "6")],
    summary: `**Budget Alert Monitor**\n\n1. Daily check of spending data at 8 AM\n2. AI compares spending to budget thresholds\n3. If any department over budget → emails finance team + alerts #finance on Slack\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  "Revenue Recognition Tracker": {
    nodes: [
      trigger("Stripe: Invoice paid", "webhook_app", {
        source: "Stripe",
        event: "invoice.paid",
        howItStarts: "Stripe sends a notification when an invoice is paid",
        setup:
          "In Stripe → Developers → Webhooks → Add endpoint → URL: your workflow URL → Events: invoice.paid. The AI will read whatever data Stripe sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the Stripe webhook",
        280,
        "Read this Stripe invoice.paid webhook data and extract the payment amount (in dollars, divide cents by 100), customer name, customer email, invoice ID, and payment date. Different Stripe webhook formats vary — find the payment amount, customer name, customer email, invoice ID, and payment date regardless of field naming. Respond JSON: {payment_amount: number, customer_name: string, customer_email: string, invoice_id: string, payment_date: string}",
        "{webhook_payload}",
        [
          { field: "payment_amount", as: "payment_amount", type: "number" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "invoice_id", as: "invoice_id", type: "string" },
          { field: "payment_date", as: "payment_date", type: "string" },
        ],
      ),
      n("3", "hitApi", "Log to Google Sheets", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Revenue:append?valueInputOption=RAW",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{payment_date}","{customer_name}","{customer_email}","{invoice_id}","{payment_amount}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_added",
              type: "number",
            },
          ],
        },
      }),
      slackNode(
        "4",
        "Notify #finance",
        520,
        "💰 Revenue: ${payment_amount} from {customer_name} — Invoice #{invoice_id} ({payment_date})",
        460,
      ),
      n("5", "condition", "Large payment? (>$1000)", 760, {
        conditionData: [
          {
            variable: "payment_amount",
            value: "1000",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "6",
        "Email CEO: large payment",
        1000,
        "ceo@company.com",
        "Large Payment: ${payment_amount} from {customer_name}",
        "<p>A large payment has been received.</p><p>Amount: ${payment_amount}</p><p>Customer: {customer_name} ({customer_email})</p><p>Invoice: #{invoice_id}</p><p>Date: {payment_date}</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("2", "4"), e("2", "5"), e("5", "6")],
    summary: `**Revenue Recognition Tracker**\n\n1. Stripe sends invoice.paid webhook\n2. **AI reads the data** and extracts payment amount, customer name, email, invoice ID, date\n3. Logged to Google Sheets\n4. #finance notified on Slack\n5. Payments over $1000 → CEO emailed\n\n**To activate:** Say "connect openai", "connect google sheets", "connect slack", "connect email"`,
  },

  // ===================================================================
  // OPERATIONS TEMPLATES
  // ===================================================================

  "Vendor Onboarding": {
    nodes: [
      trigger("Form: Vendor registration", "webhook_form", {
        source: "Vendor Registration Form / Outgrow Forms",
        howItStarts: "A new vendor submits a registration form",
        setup:
          "Create a vendor registration form (Outgrow Forms, Outgrow Forms) → Webhooks → paste your workflow URL. The AI will read whatever data your form sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the vendor form",
        280,
        "Read this vendor registration form data and extract the company name, contact name, contact email, tax ID, and services provided. Different forms send data in different formats — find the company name, contact name, contact email, tax ID, and services provided regardless of field naming. Respond JSON: {company_name: string, contact_name: string, contact_email: string, tax_id: string, services_provided: string}",
        "{webhook_payload}",
        [
          { field: "company_name", as: "company_name", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "tax_id", as: "tax_id", type: "string" },
          {
            field: "services_provided",
            as: "services_provided",
            type: "string",
          },
        ],
      ),
      aiNode(
        "3",
        "AI validate vendor",
        520,
        'Validate this vendor registration. Check if the tax ID format looks valid and assess vendor risk based on provided information. Respond JSON: {tax_valid: \\"true\\" | \\"false\\", risk_level: string, onboarding_notes: string}',
        "Company: {company_name}, Contact: {contact_name}, Tax ID: {tax_id}, Services: {services_provided}",
        [
          { field: "tax_valid", as: "tax_valid", type: "string" },
          { field: "risk_level", as: "risk_level", type: "string" },
          { field: "onboarding_notes", as: "onboarding_notes", type: "string" },
        ],
      ),
      emailNode(
        "4",
        "Email vendor: welcome packet",
        760,
        "{contact_email}",
        "Welcome to our vendor program, {company_name}!",
        "<p>Hi {contact_name},</p><p>Thank you for registering {company_name} as a vendor.</p><p>Services: {services_provided}</p><p>Next steps: We'll review your registration and be in touch shortly.</p>",
      ),
      slackNode(
        "5",
        "Notify #procurement",
        760,
        "🏢 New vendor registered: {company_name}\\nContact: {contact_name} ({contact_email})\\nServices: {services_provided}\\nRisk level: {risk_level}\\nTax ID valid: {tax_valid}",
        460,
      ),
      n("6", "hitApi", "Log to Google Sheets", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Vendors:append?valueInputOption=RAW",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{company_name}","{contact_name}","{contact_email}","{tax_id}","{services_provided}","{risk_level}","{tax_valid}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_added",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5"), e("3", "6")],
    summary: `**Vendor Onboarding**\n\n1. Vendor submits registration form\n2. **AI reads the data** and extracts company name, contact, tax ID, services\n3. AI validates tax ID format and assesses vendor risk\n4. Welcome email sent to vendor\n5. #procurement notified on Slack\n6. Logged to Google Sheets\n\n**To activate:** Say "connect openai", "connect email", "connect slack", "connect google sheets"`,
  },

  "SLA Monitor": {
    nodes: [
      trigger("Schedule: Every hour", "schedule", {
        schedule: "Every hour",
        howItStarts:
          "Checks your helpdesk for tickets exceeding SLA every hour",
        setup:
          "Activate this workflow and configure your helpdesk API endpoint in the next node",
        inputFields: [],
      }),
      n("2", "hitApi", "Get open tickets from helpdesk", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.your-helpdesk.com/v2/tickets?status=open&sort_by=created_at",
          headers: [
            { key: "Authorization", value: "Bearer {helpdesk_api_key}" },
          ],
          variables: [
            { name: "tickets", customName: "open_tickets", type: "array" },
            { name: "count", customName: "ticket_count", type: "number" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI analyze SLA breaches",
        520,
        "Analyze these open tickets and find any that have exceeded their SLA response or resolution time. Respond JSON: {breached_count: number, critical_tickets: string, sla_summary: string}",
        "Open tickets: {open_tickets}, Total: {ticket_count}",
        [
          { field: "breached_count", as: "breached_count", type: "number" },
          { field: "critical_tickets", as: "critical_tickets", type: "string" },
          { field: "sla_summary", as: "sla_summary", type: "string" },
        ],
      ),
      n("4", "condition", "Any SLA breaches?", 760, {
        conditionData: [
          {
            variable: "breached_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "5",
        "Alert #support",
        1000,
        "🚨 SLA Breach: {breached_count} tickets over SLA!\\n{critical_tickets}\\n\\n{sla_summary}",
      ),
      emailNode(
        "6",
        "Email team lead",
        1000,
        "support-lead@company.com",
        "SLA Alert: {breached_count} tickets breached",
        "<h2>SLA Breach Alert</h2><p>{breached_count} tickets have exceeded SLA.</p><p>Critical tickets: {critical_tickets}</p><p>{sla_summary}</p>",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("4", "6")],
    summary: `**SLA Monitor**\n\n1. Checks helpdesk every hour for open tickets\n2. AI identifies tickets exceeding SLA\n3. If SLA breaches found → Slack alert + email to team lead\n\n**To activate:** Say "connect openai", "connect slack", "connect email"`,
  },

  "Incident Response": {
    nodes: [
      trigger("Webhook: Monitoring alert", "webhook_app", {
        source: "Datadog / PagerDuty / New Relic",
        howItStarts:
          "Your monitoring tool sends an alert when an incident is detected",
        setup:
          "In your monitoring tool (Datadog, PagerDuty, New Relic) → Integrations → Webhooks → paste your workflow URL. The AI will read whatever data your monitoring tool sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the monitoring alert",
        280,
        "Read this monitoring alert webhook data and extract the incident type, severity, affected service, and description. Different monitoring tools send data in different formats — find the incident type, severity, affected service, and description regardless of field naming. Respond JSON: {incident_type: string, severity: string, affected_service: string, description: string}",
        "{webhook_payload}",
        [
          { field: "incident_type", as: "incident_type", type: "string" },
          { field: "severity", as: "severity", type: "string" },
          { field: "affected_service", as: "affected_service", type: "string" },
          { field: "description", as: "description", type: "string" },
        ],
      ),
      n("3", "condition", "Is it critical?", 520, {
        conditionData: [
          {
            variable: "severity",
            value: "critical",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "4",
        "Urgent: alert #incidents",
        760,
        "🚨 *CRITICAL INCIDENT*\\nType: {incident_type}\\nService: {affected_service}\\n{description}\\n@oncall please respond immediately!",
      ),
      emailNode(
        "5",
        "Email on-call engineer",
        760,
        "oncall@company.com",
        "CRITICAL: {incident_type} — {affected_service}",
        "<p><strong>Critical Incident</strong></p><p>Type: {incident_type}</p><p>Service: {affected_service}</p><p>{description}</p><p>Immediate response required.</p>",
        460,
      ),
      slackNode(
        "6",
        "Normal: post to #engineering",
        760,
        "⚠️ Incident: {incident_type} ({severity})\\nService: {affected_service}\\n{description}",
        840,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5"), e("3", "6")],
    summary: `**Incident Response**\n\n1. Monitoring tool sends alert via webhook\n2. **AI reads the data** and extracts incident type, severity, affected service, description\n3. Critical → #incidents Slack alert + on-call email\n4. Non-critical → #engineering Slack notification\n\n**To activate:** Say "connect openai", "connect slack", "connect email"`,
  },

  "Contract Renewal Tracker": {
    nodes: [
      trigger("Schedule: Weekly Monday at 9:00 AM", "schedule", {
        schedule: "Every Monday at 9:00 AM",
        howItStarts: "Checks for upcoming contract renewals every Monday",
        setup:
          "Activate this workflow. The AI will analyze contract data you provide or connect to your contract management system",
        inputFields: [{ name: "contract_data", type: "string" }],
      }),
      aiNode(
        "2",
        "AI analyze upcoming renewals",
        280,
        "Analyze the provided contract data and find contracts expiring in the next 30 days. Summarize the renewals. Respond JSON: {expiring_contracts: string, renewal_count: number, total_value: number, top_renewal: string}",
        "Contracts: {contract_data}",
        [
          {
            field: "expiring_contracts",
            as: "expiring_contracts",
            type: "string",
          },
          { field: "renewal_count", as: "renewal_count", type: "number" },
          { field: "total_value", as: "total_value", type: "number" },
          { field: "top_renewal", as: "top_renewal", type: "string" },
        ],
      ),
      n("3", "condition", "Any renewals coming up?", 520, {
        conditionData: [
          {
            variable: "renewal_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "4",
        "Email procurement team",
        760,
        "procurement@company.com",
        "Contract Renewals: {renewal_count} contracts expiring soon (${total_value} total)",
        "<h2>Upcoming Contract Renewals</h2><p>{renewal_count} contracts expiring in the next 30 days.</p><p>Total value: ${total_value}</p><p>Top renewal: {top_renewal}</p><p>{expiring_contracts}</p>",
      ),
      slackNode(
        "5",
        "Alert #contracts",
        760,
        "📋 {renewal_count} contracts expiring soon (${total_value} total)\\nTop: {top_renewal}\\n\\n{expiring_contracts}",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5")],
    summary: `**Contract Renewal Tracker**\n\n1. Runs every Monday at 9 AM\n2. AI analyzes contract data for upcoming renewals (next 30 days)\n3. If renewals found → emails procurement + alerts #contracts on Slack\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  // ===================================================================
  // AI & CHATBOT TEMPLATES
  // ===================================================================

  "RAG Chatbot Builder": {
    nodes: [
      trigger("Manual: Ask a question with context", "manual", {
        howItStarts:
          "You provide a question and context documents for the AI to answer from",
        setup:
          "Type your question and paste context documents in the chat, or POST via API with {question: '...', context_docs: '...', requester_email: '...'}",
        inputFields: [
          {
            name: "question",
            type: "string",
            example: "What is our refund policy?",
          },
          {
            name: "context_docs",
            type: "string",
            example:
              "Paste your knowledge base articles, docs, or FAQ content here",
          },
          { name: "requester_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI generate answer from context",
        280,
        'Answer the question using ONLY the provided context documents. If the answer is not in the context, say so. Cite which part of the context you used. Respond JSON: {answer: string, confidence: number, source_excerpt: string, was_found_in_context: \\"true\\" | \\"false\\"}',
        "Question: {question}\\nContext Documents: {context_docs}",
        [
          { field: "answer", as: "answer", type: "string" },
          { field: "confidence", as: "confidence", type: "number" },
          { field: "source_excerpt", as: "source_excerpt", type: "string" },
          {
            field: "was_found_in_context",
            as: "was_found_in_context",
            type: "string",
          },
        ],
      ),
      n("3", "condition", "Answer found in context?", 520, {
        conditionData: [
          {
            variable: "was_found_in_context",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "4",
        "Email answer to requester",
        760,
        "{requester_email}",
        "Answer: {question}",
        "<h2>Your Question</h2><p>{question}</p><h2>Answer</h2><p>{answer}</p><p>Confidence: {confidence}%</p><p>Source: {source_excerpt}</p>",
      ),
      emailNode(
        "5",
        "Email: answer not found",
        760,
        "{requester_email}",
        "Could not answer: {question}",
        "<p>We could not find a definitive answer to your question in the provided context.</p><p>AI's best attempt: {answer}</p><p>Please provide more context or contact support.</p>",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5")],
    summary: `**RAG Chatbot Builder**\n\n1. Provide a question + context documents\n2. AI answers using ONLY the provided context (RAG-style)\n3. Answer found → emailed with source citation\n4. Not found → notification with best attempt\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "AI Agent Task Runner": {
    nodes: [
      trigger("Manual: Describe a task", "manual", {
        howItStarts:
          "You describe a task for the AI to break into steps and execute",
        setup:
          "Type your task in the chat (e.g., 'Research top 5 competitors and summarize their pricing') or POST via API with {task_description: '...', requester_email: '...'}",
        inputFields: [
          {
            name: "task_description",
            type: "string",
            example: "Research top 5 competitors and summarize their pricing",
          },
          { name: "requester_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI break task into steps",
        280,
        "Break this task into 3-5 actionable steps. Respond JSON: {step_1: string, step_2: string, step_3: string, total_steps: number, plan_summary: string}",
        "Task: {task_description}",
        [
          { field: "step_1", as: "step_1", type: "string" },
          { field: "step_2", as: "step_2", type: "string" },
          { field: "step_3", as: "step_3", type: "string" },
          { field: "total_steps", as: "total_steps", type: "number" },
          { field: "plan_summary", as: "plan_summary", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "AI execute step 1",
        520,
        'Execute this step of the task plan. Provide a thorough result. Respond JSON: {step_result: string, has_more_steps: \\"true\\" | \\"false\\", next_step_instruction: string}',
        "Task: {task_description}\\nPlan: {plan_summary}\\nCurrent step: {step_1}\\nStep 2: {step_2}\\nStep 3: {step_3}",
        [
          { field: "step_result", as: "step_result", type: "string" },
          { field: "has_more_steps", as: "has_more_steps", type: "string" },
          {
            field: "next_step_instruction",
            as: "next_step_instruction",
            type: "string",
          },
        ],
      ),
      n("4", "condition", "More steps to execute?", 760, {
        conditionData: [
          {
            variable: "has_more_steps",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      aiNode(
        "5",
        "AI execute remaining steps",
        1000,
        "Continue executing the remaining steps. Build on the previous result. Provide a comprehensive final output. Respond JSON: {final_result: string, steps_completed: number, task_summary: string}",
        "Task: {task_description}\\nPlan: {plan_summary}\\nStep 1 result: {step_result}\\nNext: {next_step_instruction}\\nRemaining steps: {step_2}, {step_3}",
        [
          { field: "final_result", as: "final_result", type: "string" },
          { field: "steps_completed", as: "steps_completed", type: "number" },
          { field: "task_summary", as: "task_summary", type: "string" },
        ],
      ),
      emailNode(
        "6",
        "Email task results",
        1240,
        "{requester_email}",
        "Task Complete: {task_description}",
        "<h2>Task Summary</h2><p>{task_summary}</p><h3>Steps Completed: {steps_completed}</h3><h3>Result</h3><p>{final_result}</p>",
      ),
      emailNode(
        "7",
        "Email single-step result",
        1000,
        "{requester_email}",
        "Task Complete: {task_description}",
        "<h2>Task Result</h2><p>{step_result}</p><p>Plan: {plan_summary}</p>",
        460,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("4", "7"),
    ],
    summary: `**AI Agent Task Runner**\n\n1. Describe any task\n2. AI breaks it into actionable steps\n3. AI executes step 1\n4. If more steps → AI continues and completes all\n5. Full results emailed\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "Document Q&A": {
    nodes: [
      trigger("Manual: Submit document + question", "manual", {
        howItStarts: "You provide document text and a question about it",
        setup:
          "Paste the document content and your question in chat, or POST via API with {document_text: '...', question: '...', requester_email: '...'}",
        inputFields: [
          { name: "document_text", type: "string" },
          {
            name: "question",
            type: "string",
            example: "What are the key terms of this contract?",
          },
          { name: "requester_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI extract answer from document",
        280,
        "Read this document carefully and answer the question based on its contents. Respond JSON: {extracted_answer: string, relevant_section: string, confidence: number}",
        "Document: {document_text}\\nQuestion: {question}",
        [
          { field: "extracted_answer", as: "extracted_answer", type: "string" },
          { field: "relevant_section", as: "relevant_section", type: "string" },
          { field: "confidence", as: "confidence", type: "number" },
        ],
      ),
      emailNode(
        "3",
        "Email the answer",
        520,
        "{requester_email}",
        "Answer: {question}",
        "<h2>Your Question</h2><p>{question}</p><h2>Answer</h2><p>{extracted_answer}</p><h3>Relevant Section</h3><p>{relevant_section}</p><p>Confidence: {confidence}%</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3")],
    summary: `**Document Q&A**\n\n1. Submit any document text + a question\n2. AI reads the document and extracts the answer\n3. Answer emailed with relevant section cited\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "Sentiment Analysis Pipeline": {
    nodes: [
      trigger("Form: Text submitted for analysis", "webhook_form", {
        source: "Outgrow Forms / Custom Form",
        howItStarts:
          "Someone submits text (review, feedback, comment) via a form for sentiment analysis",
        setup:
          "In your form tool → Integrations → Webhooks → paste your workflow URL. The AI will read whatever data your form sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract text from form",
        280,
        "Read this form submission and extract the text to analyze. Different forms send data in different formats — find the text content regardless of field naming. Respond JSON: {text_content: string, submitter_email: string}",
        "{webhook_payload}",
        [
          { field: "text_content", as: "text_content", type: "string" },
          { field: "submitter_email", as: "submitter_email", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "AI classify sentiment",
        520,
        'Classify the sentiment of this text. Respond JSON: {sentiment: \\"positive\\" | \\"negative\\" | \\"neutral\\", sentiment_score: number, key_phrases: string, summary: string}',
        "{text_content}",
        [
          { field: "sentiment", as: "sentiment", type: "string" },
          { field: "sentiment_score", as: "sentiment_score", type: "number" },
          { field: "key_phrases", as: "key_phrases", type: "string" },
          { field: "summary", as: "sentiment_summary", type: "string" },
        ],
      ),
      n("4", "condition", "Negative sentiment? (score < 3)", 760, {
        conditionData: [
          {
            variable: "sentiment_score",
            value: "3",
            comparison_operator: "LESS THAN",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "5",
        "Alert: negative sentiment detected",
        1000,
        "⚠️ Negative sentiment detected (score: {sentiment_score}/10)\\nSentiment: {sentiment}\\nKey phrases: {key_phrases}\\nSummary: {sentiment_summary}",
      ),
      n(
        "6",
        "hitApi",
        "Log to Google Sheets",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Sentiment:append?valueInputOption=RAW",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "values",
                value:
                  '[["{text_content}","{sentiment}","{sentiment_score}","{key_phrases}","{sentiment_summary}"]]',
              },
            ],
            variables: [
              {
                name: "updates.updatedRows",
                customName: "rows_added",
                type: "number",
              },
            ],
          },
        },
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("3", "6")],
    summary: `**Sentiment Analysis Pipeline**\n\n1. Text submitted via form\n2. **AI reads the submission** and extracts text content\n3. AI classifies sentiment (positive/negative/neutral + score)\n4. Negative sentiment (score < 3) → Slack alert\n5. All results logged to Google Sheets\n\n**To activate:** Say "connect openai", "connect slack", "connect google sheets"`,
  },

  "AI Translation Workflow": {
    nodes: [
      trigger("Manual: Submit text for translation", "manual", {
        howItStarts:
          "You provide text and a target language for AI translation",
        setup:
          "Type the text and target language in chat, or POST via API with {source_text: '...', target_language: '...', requester_email: '...'}",
        inputFields: [
          {
            name: "source_text",
            type: "string",
            example: "Hello, how can I help you today?",
          },
          { name: "target_language", type: "string", example: "Spanish" },
          { name: "requester_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI translate text",
        280,
        "Translate the following text into the target language. Preserve tone and meaning. Respond JSON: {translated_text: string, detected_source_language: string, word_count: number, translation_notes: string}",
        "Text: {source_text}\\nTranslate to: {target_language}",
        [
          { field: "translated_text", as: "translated_text", type: "string" },
          {
            field: "detected_source_language",
            as: "detected_source_language",
            type: "string",
          },
          { field: "word_count", as: "word_count", type: "number" },
          {
            field: "translation_notes",
            as: "translation_notes",
            type: "string",
          },
        ],
      ),
      emailNode(
        "3",
        "Email translated text",
        520,
        "{requester_email}",
        "Translation Complete: {detected_source_language} → {target_language}",
        "<h2>Original ({detected_source_language})</h2><p>{source_text}</p><h2>Translation ({target_language})</h2><p>{translated_text}</p><p>Notes: {translation_notes}</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3")],
    summary: `**AI Translation Workflow**\n\n1. Provide text + target language\n2. AI translates preserving tone and meaning\n3. Translated text emailed with source language detected\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "AI Code Review Bot": {
    nodes: [
      trigger("GitHub: Pull request opened", "webhook_app", {
        source: "GitHub",
        event: "pull_request.opened",
        howItStarts: "Someone opens a pull request on GitHub",
        setup:
          "In GitHub → repo Settings → Webhooks → Add webhook → URL: your workflow URL → Events: 'Pull requests'. The AI will read whatever data GitHub sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract PR details",
        280,
        "Read this GitHub pull request webhook data and extract the PR title, PR body/description, author, diff/changes summary, files changed count, and PR URL. Different GitHub webhook formats vary — find the PR title, PR body, author, changes, files changed, and PR URL regardless of field naming. Respond JSON: {pr_title: string, pr_body: string, pr_author: string, changes_summary: string, files_changed: string, pr_url: string}",
        "{webhook_payload}",
        [
          { field: "pr_title", as: "pr_title", type: "string" },
          { field: "pr_body", as: "pr_body", type: "string" },
          { field: "pr_author", as: "pr_author", type: "string" },
          { field: "changes_summary", as: "changes_summary", type: "string" },
          { field: "files_changed", as: "files_changed", type: "string" },
          { field: "pr_url", as: "pr_url", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "AI review code quality",
        520,
        'Review this pull request for code quality. Check for potential bugs, security issues, performance concerns, and best practices. Respond JSON: {overall_grade: string, bugs_found: string, security_issues: string, performance_notes: string, suggestions: string, approval_recommendation: \\"approve\\" | \\"request_changes\\" | \\"needs_discussion\\"}',
        "PR: {pr_title}\\nDescription: {pr_body}\\nChanges: {changes_summary}\\nFiles changed: {files_changed}",
        [
          { field: "overall_grade", as: "overall_grade", type: "string" },
          { field: "bugs_found", as: "bugs_found", type: "string" },
          { field: "security_issues", as: "security_issues", type: "string" },
          {
            field: "performance_notes",
            as: "performance_notes",
            type: "string",
          },
          { field: "suggestions", as: "suggestions", type: "string" },
          {
            field: "approval_recommendation",
            as: "approval_recommendation",
            type: "string",
          },
        ],
      ),
      slackNode(
        "4",
        "Post review to #code-review",
        760,
        "🔍 Code Review: *{pr_title}* by {pr_author}\\nGrade: {overall_grade}\\nRecommendation: {approval_recommendation}\\nBugs: {bugs_found}\\nSecurity: {security_issues}\\nSuggestions: {suggestions}\\n<{pr_url}|View PR>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4")],
    summary: `**AI Code Review Bot**\n\n1. GitHub PR webhook fires\n2. **AI reads the data** and extracts PR title, body, author, changes, files\n3. AI reviews for bugs, security, performance, and best practices\n4. Review posted to #code-review on Slack\n\n**To activate:** Say "connect openai" and "connect slack"`,
  },

  "AI Competitor Monitor": {
    nodes: [
      trigger("Schedule: Every day at 7:00 AM", "schedule", {
        schedule: "Every day at 7:00 AM",
        howItStarts: "Scrapes competitor website daily to detect changes",
        setup:
          "Activate and say 'connect scrapingbee'. Set competitor_url in the API node",
        inputFields: [
          {
            name: "competitor_url",
            type: "string",
            example: "https://competitor.com/pricing",
          },
          { name: "competitor_name", type: "string" },
          { name: "team_email", type: "string" },
        ],
      }),
      n("2", "hitApi", "Scrape competitor page", 280, {
        hitApiDetails: {
          method: "GET",
          url: 'https://api.scrapingbee.com/v1/?api_key={scrapingbee_key}&url={competitor_url}&extract_rules={"content":{"selector":"body","type":"text"}}',
          headers: [],
          variables: [
            { name: "content", customName: "page_content", type: "string" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI analyze changes",
        520,
        'Analyze this competitor page content. Identify any notable changes, new features, pricing updates, or announcements. Compare to what you\'d expect from a typical page. Respond JSON: {has_changes: \\"true\\" | \\"false\\", change_summary: string, importance: \\"high\\" | \\"medium\\" | \\"low\\", competitive_implications: string}',
        "Competitor: {competitor_name}\\nPage URL: {competitor_url}\\nContent: {page_content}",
        [
          { field: "has_changes", as: "has_changes", type: "string" },
          { field: "change_summary", as: "change_summary", type: "string" },
          { field: "importance", as: "importance", type: "string" },
          {
            field: "competitive_implications",
            as: "competitive_implications",
            type: "string",
          },
        ],
      ),
      n("4", "condition", "Notable changes detected?", 760, {
        conditionData: [
          {
            variable: "has_changes",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "5",
        "Alert #competitive-intel",
        1000,
        "🔍 Competitor Update: {competitor_name}\\nImportance: {importance}\\n{change_summary}\\nImplications: {competitive_implications}",
      ),
      emailNode(
        "6",
        "Email team",
        1000,
        "{team_email}",
        "Competitor Alert: {competitor_name} — {importance} importance",
        "<h2>Competitor Update: {competitor_name}</h2><p>{change_summary}</p><h3>Competitive Implications</h3><p>{competitive_implications}</p>",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("4", "6")],
    summary: `**AI Competitor Monitor**\n\n1. Daily scrape of competitor page\n2. AI analyzes for notable changes\n3. If changes detected → Slack alert + email to team\n\n**To activate:** Say "connect scrapingbee", "connect openai", "connect slack", "connect email"`,
  },

  "Blog SEO Optimizer": {
    nodes: [
      trigger("Manual: Submit blog text", "manual", {
        howItStarts: "You paste a blog post for SEO optimization",
        setup:
          "Paste your blog post text in the chat, or POST via API with {blog_text: '...', author_email: '...'}",
        inputFields: [
          { name: "blog_text", type: "string" },
          { name: "author_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI optimize for SEO",
        280,
        "Optimize this blog post for SEO. Generate an optimized title, meta description, target keywords, and specific improvement suggestions. Respond JSON: {optimized_title: string, meta_description: string, target_keywords: string, seo_score: number, suggestions: string, optimized_intro: string}",
        "{blog_text}",
        [
          { field: "optimized_title", as: "optimized_title", type: "string" },
          { field: "meta_description", as: "meta_description", type: "string" },
          { field: "target_keywords", as: "target_keywords", type: "string" },
          { field: "seo_score", as: "seo_score", type: "number" },
          { field: "suggestions", as: "seo_suggestions", type: "string" },
          { field: "optimized_intro", as: "optimized_intro", type: "string" },
        ],
      ),
      emailNode(
        "3",
        "Email optimized version",
        520,
        "{author_email}",
        "SEO Optimized: {optimized_title}",
        "<h2>SEO Score: {seo_score}/100</h2><h3>Optimized Title</h3><p>{optimized_title}</p><h3>Meta Description</h3><p>{meta_description}</p><h3>Target Keywords</h3><p>{target_keywords}</p><h3>Optimized Intro</h3><p>{optimized_intro}</p><h3>Improvement Suggestions</h3><p>{seo_suggestions}</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3")],
    summary: `**Blog SEO Optimizer**\n\n1. Submit your blog post text\n2. AI generates optimized title, meta description, keywords, and suggestions\n3. Optimized version emailed for review\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "AI Podcast Summarizer": {
    nodes: [
      trigger("Manual: Submit podcast transcript", "manual", {
        howItStarts: "You paste a podcast transcript for AI summarization",
        setup:
          "Paste the podcast transcript in chat, or POST via API with {transcript: '...', requester_email: '...'}",
        inputFields: [
          { name: "transcript", type: "string" },
          { name: "requester_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI summarize with key points",
        280,
        "Summarize this podcast transcript. Extract key points with approximate timestamps, notable quotes, and main takeaways. Respond JSON: {podcast_summary: string, key_points_with_timestamps: string, notable_quotes: string, main_takeaways: string, episode_title_suggestion: string}",
        "Transcript: {transcript}",
        [
          { field: "podcast_summary", as: "podcast_summary", type: "string" },
          {
            field: "key_points_with_timestamps",
            as: "key_points_with_timestamps",
            type: "string",
          },
          { field: "notable_quotes", as: "notable_quotes", type: "string" },
          { field: "main_takeaways", as: "main_takeaways", type: "string" },
          {
            field: "episode_title_suggestion",
            as: "episode_title_suggestion",
            type: "string",
          },
        ],
      ),
      emailNode(
        "3",
        "Email podcast summary",
        520,
        "{requester_email}",
        "Podcast Summary: {episode_title_suggestion}",
        "<h2>{episode_title_suggestion}</h2><h3>Summary</h3><p>{podcast_summary}</p><h3>Key Points & Timestamps</h3><p>{key_points_with_timestamps}</p><h3>Notable Quotes</h3><p>{notable_quotes}</p><h3>Main Takeaways</h3><p>{main_takeaways}</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3")],
    summary: `**AI Podcast Summarizer**\n\n1. Submit a podcast transcript\n2. AI extracts summary, key points with timestamps, quotes, and takeaways\n3. Summary emailed\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "YouTube Video Summarizer": {
    nodes: [
      trigger("Manual: Submit video transcript", "manual", {
        howItStarts:
          "You provide a YouTube video URL or transcript for summarization",
        setup:
          "Paste the video transcript (or copy from YouTube's auto-captions) in the chat, or POST via API with {video_info: '...', requester_email: '...'}",
        inputFields: [
          {
            name: "video_info",
            type: "string",
            example: "Paste the transcript or video URL + transcript here",
          },
          { name: "requester_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI extract key points + summary",
        280,
        "Summarize this video transcript. Extract the key points, main argument, and a concise summary. Respond JSON: {video_summary: string, key_points: string, main_argument: string, action_items: string, tldr: string}",
        "Video content: {video_info}",
        [
          { field: "video_summary", as: "video_summary", type: "string" },
          { field: "key_points", as: "key_points", type: "string" },
          { field: "main_argument", as: "main_argument", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
          { field: "tldr", as: "tldr", type: "string" },
        ],
      ),
      emailNode(
        "3",
        "Email video summary",
        520,
        "{requester_email}",
        "Video Summary Ready",
        "<h2>TL;DR</h2><p>{tldr}</p><h3>Full Summary</h3><p>{video_summary}</p><h3>Key Points</h3><p>{key_points}</p><h3>Main Argument</h3><p>{main_argument}</p><h3>Action Items</h3><p>{action_items}</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3")],
    summary: `**YouTube Video Summarizer**\n\n1. Submit a video transcript\n2. AI extracts key points, summary, main argument, and action items\n3. Summary emailed\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "AI Email Inbox Manager": {
    nodes: [
      trigger("Email: New email received", "email", {
        howItStarts: "An email arrives in your inbox",
        setup:
          "Forward emails to your workflow inbox, or connect your email provider via webhook",
        inputFields: [
          { name: "sender_email", type: "string" },
          { name: "email_subject", type: "string" },
          { name: "email_body", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI classify email",
        280,
        'Classify this email by urgency and category. Determine the best action. Respond JSON: {urgency: \\"urgent\\" | \\"normal\\" | \\"spam\\", category: string, suggested_action: string, summary: string}',
        "From: {sender_email}\\nSubject: {email_subject}\\nBody: {email_body}",
        [
          { field: "urgency", as: "urgency", type: "string" },
          { field: "category", as: "category", type: "string" },
          { field: "suggested_action", as: "suggested_action", type: "string" },
          { field: "summary", as: "email_summary", type: "string" },
        ],
      ),
      n("3", "condition", "Is it urgent?", 520, {
        conditionData: [
          {
            variable: "urgency",
            value: "urgent",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "4",
        "Alert: urgent email",
        760,
        "🚨 Urgent email from {sender_email}\\nSubject: {email_subject}\\nCategory: {category}\\nAction: {suggested_action}\\nSummary: {email_summary}",
      ),
      n(
        "5",
        "hitApi",
        "Log to Google Sheets",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Inbox:append?valueInputOption=RAW",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "values",
                value:
                  '[["{sender_email}","{email_subject}","{urgency}","{category}","{suggested_action}","{email_summary}"]]',
              },
            ],
            variables: [
              {
                name: "updates.updatedRows",
                customName: "rows_added",
                type: "number",
              },
            ],
          },
        },
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("2", "5")],
    summary: `**AI Email Inbox Manager**\n\n1. Email arrives\n2. AI classifies urgency (urgent/normal/spam) and category\n3. Urgent → Slack alert with suggested action\n4. All emails logged to Google Sheets\n\n**To activate:** Say "connect openai", "connect slack", "connect google sheets"`,
  },

  // ===================================================================
  // SALES & LEAD ENRICHMENT TEMPLATES
  // ===================================================================

  "AI Data Enrichment Pipeline": {
    nodes: [
      trigger("Form: Company/person info submitted", "webhook_form", {
        source: "Outgrow Forms / Internal Tool",
        howItStarts:
          "Someone submits company or person information via a form for enrichment",
        setup:
          "In your form tool → Integrations → Webhooks → paste your workflow URL. The AI will read whatever data your form sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract company/person info",
        280,
        "Read this form submission and extract the person or company details for enrichment. Different forms send data in different formats — find the email, name, and company regardless of field naming. Respond JSON: {contact_email: string, contact_name: string, company_name: string}",
        "{webhook_payload}",
        [
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
        ],
      ),
      n("3", "hitApi", "Enrich via Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.clearbit.com/v2/combined/find?email={contact_email}",
          headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
          body: [],
          variables: [
            {
              name: "person.name.fullName",
              customName: "full_name",
              type: "string",
            },
            { name: "person.title", customName: "job_title", type: "string" },
            {
              name: "company.name",
              customName: "enriched_company",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "employee_count",
              type: "number",
            },
            {
              name: "company.category.industry",
              customName: "industry",
              type: "string",
            },
            {
              name: "company.metrics.annualRevenue",
              customName: "annual_revenue",
              type: "string",
            },
          ],
        },
      }),
      aiNode(
        "4",
        "AI merge and enrich profile",
        760,
        "Merge the original submission data with the enriched Clearbit data. Create a comprehensive profile. Respond JSON: {enriched_profile: string, profile_completeness: number, notable_insights: string}",
        "Original: Name={contact_name}, Email={contact_email}, Company={company_name}\\nEnriched: Full name={full_name}, Title={job_title}, Company={enriched_company}, Employees={employee_count}, Industry={industry}, Revenue={annual_revenue}",
        [
          { field: "enriched_profile", as: "enriched_profile", type: "string" },
          {
            field: "profile_completeness",
            as: "profile_completeness",
            type: "number",
          },
          { field: "notable_insights", as: "notable_insights", type: "string" },
        ],
      ),
      emailNode(
        "5",
        "Email enriched profile",
        1000,
        "{contact_email}",
        "Enriched Profile: {contact_name} at {enriched_company}",
        "<h2>Enriched Profile</h2><p>{enriched_profile}</p><h3>Notable Insights</h3><p>{notable_insights}</p><p>Profile completeness: {profile_completeness}%</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5")],
    summary: `**AI Data Enrichment Pipeline**\n\n1. Form submitted with company/person info\n2. **AI reads the submission** and extracts contact details\n3. Clearbit enriches with company data (size, industry, revenue)\n4. AI merges and creates comprehensive profile\n5. Enriched profile emailed\n\n**To activate:** Say "connect openai", "connect clearbit", "connect email"`,
  },

  "LinkedIn Lead Scraper to CRM": {
    nodes: [
      trigger("Schedule: Every day at 10:00 AM", "schedule", {
        schedule: "Every day at 10:00 AM",
        howItStarts:
          "Scrapes lead data from a target page daily and pushes to CRM",
        setup:
          "Activate and say 'connect scrapingbee' and 'connect hubspot'. Set the target_url to your lead source page",
        inputFields: [
          {
            name: "target_url",
            type: "string",
            example: "https://your-lead-source-page.com/directory",
          },
        ],
      }),
      n("2", "hitApi", "Scrape lead source page", 280, {
        hitApiDetails: {
          method: "GET",
          url: 'https://api.scrapingbee.com/v1/?api_key={scrapingbee_key}&url={target_url}&extract_rules={"leads":{"selector":".profile-card","type":"list","output":{"name":{"selector":".name","type":"text"},"title":{"selector":".title","type":"text"},"company":{"selector":".company","type":"text"}}}}',
          headers: [],
          variables: [
            { name: "leads", customName: "scraped_leads", type: "array" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI extract lead data",
        520,
        "Parse and clean these scraped lead profiles. Extract the best lead with name, title, company, and a professional email guess. Respond JSON: {lead_name: string, lead_title: string, lead_company: string, lead_email_guess: string, lead_count: number}",
        "Scraped data: {scraped_leads}",
        [
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "lead_title", as: "lead_title", type: "string" },
          { field: "lead_company", as: "lead_company", type: "string" },
          { field: "lead_email_guess", as: "lead_email_guess", type: "string" },
          { field: "lead_count", as: "lead_count", type: "number" },
        ],
      ),
      n("4", "hitApi", "Create HubSpot contact", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"email":"{lead_email_guess}","firstname":"{lead_name}","jobtitle":"{lead_title}","company":"{lead_company}"}',
            },
          ],
          variables: [
            { name: "id", customName: "hubspot_contact_id", type: "string" },
          ],
        },
      }),
      slackNode(
        "5",
        "Notify sales team",
        1000,
        "🎯 New lead scraped and added to CRM!\\n{lead_name} — {lead_title} at {lead_company}\\nHubSpot ID: {hubspot_contact_id}\\nTotal leads found: {lead_count}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5")],
    summary: `**LinkedIn Lead Scraper to CRM**\n\n1. Daily scrape of lead source page\n2. AI extracts and cleans lead data\n3. Creates contact in HubSpot CRM\n4. Sales team notified on Slack\n\n**To activate:** Say "connect scrapingbee", "connect openai", "connect hubspot", "connect slack"`,
  },

  "Automated Sales Follow-up": {
    nodes: [
      trigger("Webhook: CRM deal updated", "webhook_app", {
        source: "HubSpot / Salesforce / CRM",
        event: "deal.updated",
        howItStarts: "Your CRM sends a notification when a deal is updated",
        setup:
          "In HubSpot → Settings → Integrations → Webhooks → Event: 'Deal updated' → paste your workflow URL. The AI will read whatever data your CRM sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract deal info",
        280,
        "Read this CRM deal webhook data and extract the deal name, deal stage, contact name, contact email, deal value, and last activity. Different CRMs send data in different formats — find the deal name, deal stage, contact name, contact email, deal value, and last activity regardless of field naming. Respond JSON: {deal_name: string, deal_stage: string, contact_name: string, contact_email: string, deal_value: string, last_activity: string}",
        "{webhook_payload}",
        [
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_stage", as: "deal_stage", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "deal_value", as: "deal_value", type: "string" },
          { field: "last_activity", as: "last_activity", type: "string" },
        ],
      ),
      n("3", "delayAction", "Wait 2 days", 520, {
        delayAction: { time: 2, timeFormat: "days" },
      }),
      aiNode(
        "4",
        "AI write follow-up email",
        760,
        "Write a personalized follow-up email for this deal. Be helpful, not pushy. Reference the deal stage and last activity. Respond JSON: {follow_up_subject: string, follow_up_body: string}",
        "Contact: {contact_name}, Deal: {deal_name}, Stage: {deal_stage}, Value: ${deal_value}, Last activity: {last_activity}",
        [
          {
            field: "follow_up_subject",
            as: "follow_up_subject",
            type: "string",
          },
          { field: "follow_up_body", as: "follow_up_body", type: "string" },
        ],
      ),
      emailNode(
        "5",
        "Send follow-up to prospect",
        1000,
        "{contact_email}",
        "{follow_up_subject}",
        "<p>{follow_up_body}</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5")],
    summary: `**Automated Sales Follow-up**\n\n1. CRM deal updated webhook fires\n2. **AI reads the data** and extracts deal info + contact details\n3. 2-day delay for natural timing\n4. AI writes personalized follow-up email\n5. Sent to prospect\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "Deal Won Celebration": {
    nodes: [
      trigger("Webhook: CRM deal won", "webhook_app", {
        source: "HubSpot / Salesforce / CRM",
        event: "deal.won",
        howItStarts:
          "Your CRM sends a notification when a deal is marked as won",
        setup:
          "In HubSpot → Settings → Integrations → Webhooks → Event: 'Deal stage changed to Closed Won' → paste your workflow URL. The AI will read whatever data your CRM sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract deal details",
        280,
        "Read this CRM deal-won webhook data and extract the deal name, customer name, customer email, deal value, sales rep name, and product/service sold. Different CRMs send data in different formats — find the deal name, customer name, customer email, deal value, sales rep, and product regardless of field naming. Respond JSON: {deal_name: string, customer_name: string, customer_email: string, deal_value: string, sales_rep: string, product_sold: string}",
        "{webhook_payload}",
        [
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "deal_value", as: "deal_value", type: "string" },
          { field: "sales_rep", as: "sales_rep", type: "string" },
          { field: "product_sold", as: "product_sold", type: "string" },
        ],
      ),
      slackNode(
        "3",
        "Celebrate in #wins",
        520,
        "🎉 DEAL WON! {deal_name} — ${deal_value}!\\nCustomer: {customer_name}\\nProduct: {product_sold}\\nClosed by: {sales_rep}\\nCongratulations! 🏆",
      ),
      emailNode(
        "4",
        "Thank you to customer",
        520,
        "{customer_email}",
        "Welcome aboard, {customer_name}!",
        "<p>Hi {customer_name},</p><p>Thank you for choosing us! We're excited to get started with {product_sold}.</p><p>Your dedicated contact is {sales_rep}, who will ensure a smooth onboarding.</p><p>Welcome to the family!</p>",
        460,
      ),
      n("5", "delayAction", "Wait 7 days", 760, {
        delayAction: { time: 7, timeFormat: "days" },
      }),
      emailNode(
        "6",
        "Ask for review",
        1000,
        "{customer_email}",
        "How's your experience so far, {customer_name}?",
        "<p>Hi {customer_name},</p><p>It's been a week since you started with {product_sold}. We'd love to hear how things are going!</p><p>Would you mind leaving a quick review? It helps other businesses like yours find us.</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("2", "4"), e("4", "5"), e("5", "6")],
    summary: `**Deal Won Celebration**\n\n1. CRM deal-won webhook fires\n2. **AI reads the data** and extracts deal name, customer, value, rep, product\n3. Celebration posted to #wins on Slack\n4. Thank-you email to customer\n5. 7-day delay → review request email\n\n**To activate:** Say "connect openai", "connect slack", "connect email"`,
  },

  "Lead Enrich Score Assign": {
    nodes: [
      trigger("Form: New lead submitted", "webhook_form", {
        source: "Outgrow Forms / Landing Page",
        howItStarts: "Someone fills out your lead capture form",
        setup:
          "In your form tool → Integrations → Webhooks → paste your workflow URL. The AI will read whatever data your form sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract lead data",
        280,
        "Read this form submission and extract the lead details. Different forms have different field names — find the person's name, email, and company regardless of format. Respond JSON: {lead_name: string, lead_email: string, lead_company: string}",
        "{webhook_payload}",
        [
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "lead_company", as: "lead_company", type: "string" },
        ],
      ),
      n("3", "hitApi", "Enrich via Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.clearbit.com/v2/combined/find?email={lead_email}",
          headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
          body: [],
          variables: [
            {
              name: "company.name",
              customName: "enriched_company",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "employee_count",
              type: "number",
            },
            {
              name: "company.category.industry",
              customName: "industry",
              type: "string",
            },
            { name: "person.title", customName: "job_title", type: "string" },
          ],
        },
      }),
      aiNode(
        "4",
        "AI score lead and assign rep",
        760,
        "Score this lead 1-100 based on company size, industry, and title. Assign to the appropriate sales rep (enterprise leads to senior reps, SMB to junior reps). Respond JSON: {lead_score: number, assigned_rep: string, scoring_reason: string, lead_tier: string}",
        "Lead: {lead_name}, Email: {lead_email}, Company: {enriched_company}, Employees: {employee_count}, Industry: {industry}, Title: {job_title}",
        [
          { field: "lead_score", as: "lead_score", type: "number" },
          { field: "assigned_rep", as: "assigned_rep", type: "string" },
          { field: "scoring_reason", as: "scoring_reason", type: "string" },
          { field: "lead_tier", as: "lead_tier", type: "string" },
        ],
      ),
      n("5", "condition", "Hot lead? (score > 70)", 1000, {
        conditionData: [
          {
            variable: "lead_score",
            value: "70",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "6",
        "Hot lead alert",
        1240,
        "🔥 Hot lead! Score: {lead_score}/100\\n{lead_name} — {job_title} at {enriched_company}\\nTier: {lead_tier}\\nAssigned to: {assigned_rep}\\nReason: {scoring_reason}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
    summary: `**Lead Enrich Score Assign**\n\n1. Lead form submitted\n2. **AI reads the form** and extracts lead data\n3. Clearbit enriches with company data\n4. AI scores 1-100 and assigns to sales rep\n5. Score > 70 → hot lead alert on Slack\n\n**To activate:** Say "connect openai", "connect clearbit", "connect slack"`,
  },

  // ===================================================================
  // EMAIL SEQUENCES & MARKETING TEMPLATES
  // ===================================================================

  "Webinar to Email Sequence": {
    nodes: [
      trigger("Form: Webinar registration", "webhook_form", {
        source: "Outgrow Forms / Landing Page / Eventbrite",
        howItStarts: "Someone registers for your webinar via a form",
        setup:
          "In your registration form → Integrations → Webhooks → paste your workflow URL. The AI will read whatever data your form sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract registration data",
        280,
        "Read this webinar registration form data and extract the registrant name, registrant email, and webinar title. Different forms send data in different formats — find the registrant name, registrant email, and webinar title regardless of field naming. Respond JSON: {registrant_name: string, registrant_email: string, webinar_title: string}",
        "{webhook_payload}",
        [
          { field: "registrant_name", as: "registrant_name", type: "string" },
          { field: "registrant_email", as: "registrant_email", type: "string" },
          { field: "webinar_title", as: "webinar_title", type: "string" },
        ],
      ),
      emailNode(
        "3",
        "Confirmation email",
        520,
        "{registrant_email}",
        "You're registered: {webinar_title}",
        "<p>Hi {registrant_name},</p><p>You're all set for <strong>{webinar_title}</strong>!</p><p>We'll send you a reminder before the event. Mark your calendar!</p>",
      ),
      n("4", "delayAction", "Wait 1 day", 720, {
        delayAction: { time: 1, timeFormat: "days" },
      }),
      emailNode(
        "5",
        "Reminder email",
        920,
        "{registrant_email}",
        "Reminder: {webinar_title} is coming up!",
        "<p>Hi {registrant_name},</p><p>Just a friendly reminder that <strong>{webinar_title}</strong> is coming up soon. Don't miss it!</p><p>Add it to your calendar if you haven't already.</p>",
      ),
      n("6", "delayAction", "Wait until event (3 days)", 1120, {
        delayAction: { time: 3, timeFormat: "days" },
      }),
      emailNode(
        "7",
        "Replay link email",
        1320,
        "{registrant_email}",
        "Replay available: {webinar_title}",
        "<p>Hi {registrant_name},</p><p>The replay for <strong>{webinar_title}</strong> is now available!</p><p>Watch it here: [replay link]</p><p>Thanks for registering!</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**Webinar to Email Sequence**\n\n1. Registration form submitted\n2. **AI reads the form** and extracts registrant name, email, webinar title\n3. Confirmation email sent immediately\n4. 1-day delay → reminder email\n5. 3-day delay (until event) → replay link email\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "Meeting Booked to CRM": {
    nodes: [
      trigger("Webhook: Meeting booked on Calendly", "webhook_app", {
        source: "Calendly",
        event: "invitee.created",
        howItStarts: "Someone books a meeting via your Calendly link",
        setup:
          "In Calendly → Integrations → Webhooks → Event: 'invitee.created' → paste your workflow URL. The AI will read whatever data Calendly sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract meeting details",
        280,
        "Read this Calendly webhook data and extract the invitee name, invitee email, meeting type, scheduled date/time, and any answers to scheduling questions. Different Calendly formats vary — find the invitee name, invitee email, meeting type, scheduled time, and notes regardless of field naming. Respond JSON: {invitee_name: string, invitee_email: string, meeting_type: string, scheduled_time: string, invitee_notes: string}",
        "{webhook_payload}",
        [
          { field: "invitee_name", as: "invitee_name", type: "string" },
          { field: "invitee_email", as: "invitee_email", type: "string" },
          { field: "meeting_type", as: "meeting_type", type: "string" },
          { field: "scheduled_time", as: "scheduled_time", type: "string" },
          { field: "invitee_notes", as: "invitee_notes", type: "string" },
        ],
      ),
      n("3", "hitApi", "Create/update HubSpot contact", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"email":"{invitee_email}","firstname":"{invitee_name}","notes_last_contacted":"{meeting_type} booked for {scheduled_time}. Notes: {invitee_notes}"}',
            },
          ],
          variables: [
            { name: "id", customName: "hubspot_contact_id", type: "string" },
          ],
        },
      }),
      slackNode(
        "4",
        "Notify sales rep",
        760,
        "📅 Meeting booked!\\n{invitee_name} ({invitee_email})\\nType: {meeting_type}\\nScheduled: {scheduled_time}\\nNotes: {invitee_notes}\\nHubSpot: {hubspot_contact_id}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4")],
    summary: `**Meeting Booked to CRM**\n\n1. Calendly meeting booked webhook fires\n2. **AI reads the data** and extracts invitee name, email, meeting type, time, notes\n3. Creates/updates contact in HubSpot\n4. Sales rep notified on Slack\n\n**To activate:** Say "connect openai", "connect hubspot", "connect slack"`,
  },

  "Referral Tracking System": {
    nodes: [
      trigger("Form: Referral submitted", "webhook_form", {
        source: "Referral Form / Outgrow Forms",
        howItStarts: "Someone submits a referral through your referral form",
        setup:
          "In your referral form → Integrations → Webhooks → paste your workflow URL. The AI will read whatever data your form sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract referrer and referee",
        280,
        "Read this referral form submission and extract the referrer name, referrer email, referee name, and referee email. Different forms send data in different formats — find the referrer name, referrer email, referee name, and referee email regardless of field naming. Respond JSON: {referrer_name: string, referrer_email: string, referee_name: string, referee_email: string}",
        "{webhook_payload}",
        [
          { field: "referrer_name", as: "referrer_name", type: "string" },
          { field: "referrer_email", as: "referrer_email", type: "string" },
          { field: "referee_name", as: "referee_name", type: "string" },
          { field: "referee_email", as: "referee_email", type: "string" },
        ],
      ),
      emailNode(
        "3",
        "Thank referrer",
        520,
        "{referrer_email}",
        "Thanks for the referral, {referrer_name}!",
        "<p>Hi {referrer_name},</p><p>Thank you for referring {referee_name}! We really appreciate it.</p><p>We'll reach out to them soon and keep you posted.</p>",
      ),
      emailNode(
        "4",
        "Welcome referee",
        520,
        "{referee_email}",
        "{referrer_name} thinks you'd love this!",
        "<p>Hi {referee_name},</p><p>Your friend {referrer_name} thought you'd benefit from our service.</p><p>We'd love to show you what we can do. Let us know if you'd like to learn more!</p>",
        460,
      ),
      n("5", "hitApi", "Log referral to Google Sheets", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Referrals:append?valueInputOption=RAW",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{referrer_name}","{referrer_email}","{referee_name}","{referee_email}","pending"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_added",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [e("1", "2"), e("2", "3"), e("2", "4"), e("3", "5")],
    summary: `**Referral Tracking System**\n\n1. Referral form submitted\n2. **AI reads the form** and extracts referrer + referee details\n3. Thank-you email to referrer\n4. Welcome email to referee\n5. Logged to Google Sheets\n\n**To activate:** Say "connect openai", "connect email", "connect google sheets"`,
  },

  "Blog to Multi-Channel": {
    nodes: [
      trigger("Manual: Provide blog post", "manual", {
        howItStarts: "You paste a blog post to repurpose for multiple channels",
        setup:
          "Paste your blog post text in the chat, or POST via API with {blog_post: '...', author_email: '...'}",
        inputFields: [
          { name: "blog_post", type: "string" },
          { name: "author_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI rewrite for Twitter",
        280,
        "Rewrite this blog post as a Twitter thread (max 5 tweets, each under 280 chars). Include hashtags. Respond JSON: {twitter_thread: string, twitter_hashtags: string}",
        "{blog_post}",
        [
          { field: "twitter_thread", as: "twitter_thread", type: "string" },
          { field: "twitter_hashtags", as: "twitter_hashtags", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "AI rewrite for LinkedIn",
        280,
        "Rewrite this blog post as a professional LinkedIn post (max 1300 chars, include CTA). Respond JSON: {linkedin_post: string, linkedin_hook: string}",
        "{blog_post}",
        [
          { field: "linkedin_post", as: "linkedin_post", type: "string" },
          { field: "linkedin_hook", as: "linkedin_hook", type: "string" },
        ],
        460,
      ),
      aiNode(
        "4",
        "AI rewrite for email newsletter",
        520,
        "Rewrite this blog post as a concise email newsletter (under 500 words, with subject line and preview text). Respond JSON: {newsletter_subject: string, newsletter_preview: string, newsletter_body: string}",
        "{blog_post}",
        [
          {
            field: "newsletter_subject",
            as: "newsletter_subject",
            type: "string",
          },
          {
            field: "newsletter_preview",
            as: "newsletter_preview",
            type: "string",
          },
          { field: "newsletter_body", as: "newsletter_body", type: "string" },
        ],
        840,
      ),
      emailNode(
        "5",
        "Email all versions for review",
        760,
        "{author_email}",
        "Multi-Channel Content Ready for Review",
        "<h2>Twitter Thread</h2><p>{twitter_thread}</p><p>{twitter_hashtags}</p><h2>LinkedIn Post</h2><p>{linkedin_hook}</p><p>{linkedin_post}</p><h2>Email Newsletter</h2><p>Subject: {newsletter_subject}</p><p>Preview: {newsletter_preview}</p><p>{newsletter_body}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("1", "3"),
      e("1", "4"),
      e("2", "5"),
      e("3", "5"),
      e("4", "5"),
    ],
    summary: `**Blog to Multi-Channel**\n\n1. Provide your blog post\n2. AI rewrites for Twitter (thread) + LinkedIn (professional) + Email newsletter\n3. All three versions emailed for review\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "A/B Test Email Subject": {
    nodes: [
      trigger("Manual: Provide email content", "manual", {
        howItStarts:
          "You provide email content and the AI generates subject line variations for A/B testing",
        setup:
          "Paste your email content in the chat, or POST via API with {email_content: '...', marketer_email: '...'}",
        inputFields: [
          { name: "email_content", type: "string" },
          { name: "marketer_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI generate 3 subject lines",
        280,
        "Generate 3 email subject line variations for A/B testing. Each should use a different strategy (curiosity, urgency, benefit-driven). Also recommend which to test first. Respond JSON: {subject_1: string, strategy_1: string, subject_2: string, strategy_2: string, subject_3: string, strategy_3: string, recommendation: string}",
        "Email content: {email_content}",
        [
          { field: "subject_1", as: "subject_1", type: "string" },
          { field: "strategy_1", as: "strategy_1", type: "string" },
          { field: "subject_2", as: "subject_2", type: "string" },
          { field: "strategy_2", as: "strategy_2", type: "string" },
          { field: "subject_3", as: "subject_3", type: "string" },
          { field: "strategy_3", as: "strategy_3", type: "string" },
          { field: "recommendation", as: "ab_recommendation", type: "string" },
        ],
      ),
      emailNode(
        "3",
        "Email all 3 variations",
        520,
        "{marketer_email}",
        "A/B Test Subject Lines Ready",
        "<h2>Subject Line Variations for A/B Testing</h2><h3>Option A: {strategy_1}</h3><p>{subject_1}</p><h3>Option B: {strategy_2}</h3><p>{subject_2}</p><h3>Option C: {strategy_3}</h3><p>{subject_3}</p><h3>Recommendation</h3><p>{ab_recommendation}</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3")],
    summary: `**A/B Test Email Subject**\n\n1. Provide your email content\n2. AI generates 3 subject line variations (curiosity, urgency, benefit)\n3. All 3 emailed with AI recommendation on which to test first\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "RSS to Social Media": {
    nodes: [
      trigger("Schedule: Every hour", "schedule", {
        schedule: "Every hour",
        howItStarts:
          "Checks an RSS feed hourly for new articles to share on social media",
        setup: "Activate this workflow. Set your rss_feed_url in the API node",
        inputFields: [
          {
            name: "rss_feed_url",
            type: "string",
            example: "https://yoursite.com/feed",
          },
          { name: "social_manager_email", type: "string" },
        ],
      }),
      n("2", "hitApi", "Fetch RSS feed", 280, {
        hitApiDetails: {
          method: "GET",
          url: "{rss_feed_url}",
          headers: [{ key: "Accept", value: "application/rss+xml" }],
          body: [],
          variables: [
            { name: "items", customName: "feed_items", type: "array" },
            {
              name: "items[0].title",
              customName: "latest_title",
              type: "string",
            },
          ],
        },
      }),
      aiNode(
        "3",
        "AI summarize latest article",
        520,
        'Analyze this RSS feed data. Check if there\'s a new article. If so, summarize it for social media sharing. Respond JSON: {is_new_article: \\"true\\" | \\"false\\", article_title: string, social_summary: string, suggested_tweet: string, suggested_linkedin: string}',
        "Feed items: {feed_items}\\nLatest title: {latest_title}",
        [
          { field: "is_new_article", as: "is_new_article", type: "string" },
          { field: "article_title", as: "article_title", type: "string" },
          { field: "social_summary", as: "social_summary", type: "string" },
          { field: "suggested_tweet", as: "suggested_tweet", type: "string" },
          {
            field: "suggested_linkedin",
            as: "suggested_linkedin",
            type: "string",
          },
        ],
      ),
      n("4", "condition", "New article found?", 760, {
        conditionData: [
          {
            variable: "is_new_article",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "5",
        "Post to #content",
        1000,
        "📰 New article: *{article_title}*\\nTweet: {suggested_tweet}\\nLinkedIn: {suggested_linkedin}",
      ),
      emailNode(
        "6",
        "Email social media manager",
        1000,
        "{social_manager_email}",
        "New Article to Share: {article_title}",
        "<h2>{article_title}</h2><p>{social_summary}</p><h3>Suggested Tweet</h3><p>{suggested_tweet}</p><h3>Suggested LinkedIn</h3><p>{suggested_linkedin}</p>",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("4", "6")],
    summary: `**RSS to Social Media**\n\n1. Checks RSS feed every hour\n2. AI summarizes latest article for social sharing\n3. If new article → Slack post + email to social media manager\n\n**To activate:** Say "connect openai", "connect slack", "connect email"`,
  },

  "Newsletter Curator": {
    nodes: [
      trigger("Schedule: Every Monday at 8:00 AM", "schedule", {
        schedule: "Every Monday at 8:00 AM",
        howItStarts:
          "Fetches articles from configured sources weekly and curates a newsletter",
        setup:
          "Activate and configure your article source URLs in the API node. Say 'connect openai' and 'connect email'",
        inputFields: [
          {
            name: "source_urls",
            type: "string",
            example:
              "https://api.rss2json.com/v1/api.json?rss_url=YOUR_RSS_FEED",
          },
          { name: "newsletter_email", type: "string" },
        ],
      }),
      n("2", "hitApi", "Fetch articles from sources", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.rss2json.com/v1/api.json?rss_url={source_urls}&count=20&api_key={rss2json_key}",
          headers: [],
          body: [],
          variables: [
            { name: "items", customName: "raw_articles", type: "array" },
            { name: "feed.title", customName: "feed_title", type: "string" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI rank and summarize top 5",
        520,
        "From these articles, select the top 5 most interesting and relevant ones. Rank them by importance. For each, write a 2-sentence summary. Respond JSON: {curated_articles: string, article_count: number, newsletter_intro: string, newsletter_subject: string}",
        "Source: {feed_title}\\nArticles: {raw_articles}",
        [
          { field: "curated_articles", as: "curated_articles", type: "string" },
          { field: "article_count", as: "article_count", type: "number" },
          { field: "newsletter_intro", as: "newsletter_intro", type: "string" },
          {
            field: "newsletter_subject",
            as: "newsletter_subject",
            type: "string",
          },
        ],
      ),
      emailNode(
        "4",
        "Email curated newsletter draft",
        760,
        "{newsletter_email}",
        "{newsletter_subject}",
        "<h2>Weekly Newsletter Draft</h2><p>{newsletter_intro}</p><h3>Top {article_count} Articles This Week</h3><p>{curated_articles}</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4")],
    summary: `**Newsletter Curator**\n\n1. Runs every Monday at 8 AM\n2. Fetches articles from configured sources\n3. AI ranks and summarizes the top 5\n4. Curated newsletter draft emailed for review\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  // ===================================================================
  // CUSTOMER FEEDBACK & REVIEWS
  // ===================================================================

  "Testimonial Collector": {
    nodes: [
      trigger("Form: Testimonial submitted", "webhook_form", {
        source: "Outgrow Forms / Feedback Form",
        howItStarts:
          "A customer submits a testimonial or review via your feedback form",
        setup:
          "In your feedback form → Integrations → Webhooks → paste your workflow URL. The AI will read whatever data your form sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract testimonial data",
        280,
        "Read this feedback form submission and extract the customer name, testimonial text, and rating (1-5). Different forms send data in different formats — find the customer name, testimonial text, and rating regardless of field naming. Respond JSON: {customer_name: string, customer_email: string, testimonial_text: string, rating: number}",
        "{webhook_payload}",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "testimonial_text", as: "testimonial_text", type: "string" },
          { field: "rating", as: "rating", type: "number" },
        ],
      ),
      n("3", "condition", "High rating? (> 4)", 520, {
        conditionData: [
          {
            variable: "rating",
            value: "4",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "4",
        "Ask permission to share",
        760,
        "{customer_email}",
        "Can we share your testimonial, {customer_name}?",
        "<p>Hi {customer_name},</p><p>Thank you for your amazing feedback! We'd love to share your testimonial on our website and marketing materials:</p><blockquote>{testimonial_text}</blockquote><p>Would you be comfortable with that? Just reply to let us know!</p>",
      ),
      n(
        "5",
        "hitApi",
        "Log to Google Sheets",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Testimonials:append?valueInputOption=RAW",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "values",
                value:
                  '[["{customer_name}","{customer_email}","{testimonial_text}","{rating}","pending"]]',
              },
            ],
            variables: [
              {
                name: "updates.updatedRows",
                customName: "rows_added",
                type: "number",
              },
            ],
          },
        },
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("2", "5")],
    summary: `**Testimonial Collector**\n\n1. Feedback form submitted\n2. **AI reads the form** and extracts customer name, testimonial, rating\n3. Rating > 4 → email asking permission to share\n4. All testimonials logged to Google Sheets\n\n**To activate:** Say "connect openai", "connect email", "connect google sheets"`,
  },

  "Google Ads to CRM": {
    nodes: [
      trigger("Webhook: Google Ads conversion", "webhook_app", {
        source: "Google Ads",
        event: "conversion",
        howItStarts:
          "Google Ads sends a conversion webhook when a lead converts from an ad",
        setup:
          "In Google Ads → Tools → Conversions → Set up a webhook conversion action → paste your workflow URL. The AI will read whatever data Google Ads sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract lead from conversion",
        280,
        "Read this Google Ads conversion webhook data and extract the lead name, lead email, campaign name, ad group, keyword, and conversion value. Different Google Ads formats vary — find the lead name, lead email, campaign name, ad group, keyword, and conversion value regardless of field naming. Respond JSON: {lead_name: string, lead_email: string, campaign_name: string, ad_group: string, keyword: string, conversion_value: string}",
        "{webhook_payload}",
        [
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "campaign_name", as: "campaign_name", type: "string" },
          { field: "ad_group", as: "ad_group", type: "string" },
          { field: "keyword", as: "keyword", type: "string" },
          { field: "conversion_value", as: "conversion_value", type: "string" },
        ],
      ),
      n("3", "hitApi", "Create HubSpot contact", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"email":"{lead_email}","firstname":"{lead_name}","hs_lead_source":"Google Ads","hs_analytics_source_data_1":"{campaign_name}","hs_analytics_source_data_2":"{keyword}"}',
            },
          ],
          variables: [
            { name: "id", customName: "hubspot_contact_id", type: "string" },
          ],
        },
      }),
      slackNode(
        "4",
        "Notify sales",
        760,
        "🎯 Google Ads conversion!\\n{lead_name} ({lead_email})\\nCampaign: {campaign_name}\\nKeyword: {keyword}\\nValue: ${conversion_value}\\nHubSpot ID: {hubspot_contact_id}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4")],
    summary: `**Google Ads to CRM**\n\n1. Google Ads conversion webhook fires\n2. **AI reads the data** and extracts lead name, email, campaign, keyword, value\n3. Creates contact in HubSpot with ad attribution\n4. Sales team notified on Slack\n\n**To activate:** Say "connect openai", "connect hubspot", "connect slack"`,
  },

  // ===================================================================
  // DRIP & SEQUENCES
  // ===================================================================

  "Drip Campaign with Behavior": {
    nodes: [
      trigger("Webhook: User action received", "webhook_app", {
        source: "Your App / Segment / Mixpanel",
        howItStarts:
          "Your app sends a user behavior event (page visit, button click, feature usage, etc.)",
        setup:
          "In your app or analytics tool → Webhooks → paste your workflow URL. The AI will read whatever event data your app sends and classify the action type.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract user action",
        280,
        "Read this webhook payload and extract the user email, user name, and action type. Classify the action as one of: signup, purchase, browse, inactive. Different apps send data in different formats — find the user email, user name, and action type regardless of field naming. Respond JSON: {user_email: string, user_name: string, action_type: string}",
        "{webhook_payload}",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "action_type", as: "action_type", type: "string" },
        ],
      ),
      n("3", "condition", "Is it a purchase?", 520, {
        conditionData: [
          {
            variable: "action_type",
            value: "purchase",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "4",
        "Email: post-purchase upsell",
        760,
        "{user_email}",
        "Thanks for your purchase, {user_name}!",
        "<p>Hi {user_name},</p><p>Thanks for buying! Here are complementary products you might love...</p>",
      ),
      emailNode(
        "5",
        "Email: engagement nudge",
        760,
        "{user_email}",
        "We noticed you browsing, {user_name}",
        "<p>Hi {user_name},</p><p>Looks like you were checking us out. Here's a special offer to get started...</p>",
        460,
      ),
      n("6", "delayAction", "Wait 3 days", 1000, {
        delayAction: { time: 3, timeFormat: "days" },
      }),
      emailNode(
        "7",
        "Follow-up email",
        1200,
        "{user_email}",
        "Still thinking it over, {user_name}?",
        "<p>Hi {user_name},</p><p>Just checking in — do you have any questions? We're here to help.</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("3", "5"),
      e("4", "6"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**Drip Campaign with Behavior**\n\n1. App sends user action via webhook\n2. **AI reads the data** and extracts user email, name, action type\n3. Purchase → upsell email; Browse/other → engagement nudge\n4. Wait 3 days → follow-up email\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "Event Registration Sequence": {
    nodes: [
      trigger("Form: Event registration", "webhook_form", {
        source: "Outgrow Forms / Eventbrite / Outgrow Forms",
        howItStarts: "Someone registers for your event via a form",
        setup:
          "In your registration form → Integrations → Webhooks → paste your workflow URL. The AI will read whatever data your form sends and extract what it needs — no need to worry about field names.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract registration data",
        280,
        "Read this form/registration webhook data and extract the attendee name, attendee email, event name, and event date. Different forms send data in different formats — find the attendee name, attendee email, event name, and event date regardless of field naming. Respond JSON: {attendee_name: string, attendee_email: string, event_name: string, event_date: string}",
        "{webhook_payload}",
        [
          { field: "attendee_name", as: "attendee_name", type: "string" },
          { field: "attendee_email", as: "attendee_email", type: "string" },
          { field: "event_name", as: "event_name", type: "string" },
          { field: "event_date", as: "event_date", type: "string" },
        ],
      ),
      emailNode(
        "3",
        "Confirmation email",
        520,
        "{attendee_email}",
        "You're registered for {event_name}!",
        "<p>Hi {attendee_name},</p><p>You're confirmed for <strong>{event_name}</strong> on {event_date}.</p><p>We'll send you preparation details soon!</p>",
      ),
      n("4", "delayAction", "Wait 1 day", 720, {
        delayAction: { time: 1, timeFormat: "days" },
      }),
      emailNode(
        "5",
        "Prep info email",
        920,
        "{attendee_email}",
        "Prepare for {event_name}",
        "<p>Hi {attendee_name},</p><p>Your event is coming up on {event_date}. Here's everything you need to prepare...</p><p>Agenda, materials, and FAQs enclosed.</p>",
      ),
      n("6", "delayAction", "Wait until day before event", 1120, {
        delayAction: { time: 5, timeFormat: "days" },
      }),
      emailNode(
        "7",
        "Day-of reminder",
        1320,
        "{attendee_email}",
        "Reminder: {event_name} is TODAY!",
        "<p>Hi {attendee_name},</p><p><strong>{event_name}</strong> is happening today! Don't forget to join.</p><p>See you there!</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**Event Registration Sequence**\n\n1. Registration form submitted\n2. **AI reads the data** and extracts attendee name, email, event name, event date\n3. Immediate confirmation email\n4. Day 1: Preparation info\n5. Day before event: Reminder\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  // ===================================================================
  // OUTREACH & MARKETING
  // ===================================================================

  "Influencer Outreach Manager": {
    nodes: [
      trigger("Manual: Provide influencer topic", "manual", {
        howItStarts:
          "You provide a topic or niche to find influencers for outreach",
        setup:
          "Type the topic/niche and your email in chat (e.g., 'fitness influencers for protein brand'). Or POST via API",
        inputFields: [
          {
            name: "topic",
            type: "string",
            example: "fitness influencers for protein supplements",
          },
          { name: "brand_description", type: "string" },
          { name: "marketing_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI find relevant profiles + write outreach",
        280,
        "Based on this topic, suggest 5 relevant influencer profile types and write a personalized outreach email template for each type. Respond JSON: {influencer_profiles: string, outreach_template: string, subject_line: string, follow_up_template: string, profile_count: number}",
        "Topic: {topic}, Brand: {brand_description}",
        [
          {
            field: "influencer_profiles",
            as: "influencer_profiles",
            type: "string",
          },
          {
            field: "outreach_template",
            as: "outreach_template",
            type: "string",
          },
          { field: "subject_line", as: "subject_line", type: "string" },
          {
            field: "follow_up_template",
            as: "follow_up_template",
            type: "string",
          },
          { field: "profile_count", as: "profile_count", type: "number" },
        ],
      ),
      emailNode(
        "3",
        "Email drafts for review",
        520,
        "{marketing_email}",
        "Influencer Outreach Kit: {topic}",
        "<h2>Influencer Profiles</h2><p>{influencer_profiles}</p><h2>Outreach Template</h2><p>Subject: {subject_line}</p><p>{outreach_template}</p><h2>Follow-up Template</h2><p>{follow_up_template}</p>",
      ),
      slackNode(
        "4",
        "Notify #marketing",
        520,
        "📣 Influencer outreach kit ready for '{topic}' — {profile_count} profiles + email templates. Check your email!",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("2", "4")],
    summary: `**Influencer Outreach Manager**\n\n1. Provide a topic/niche\n2. AI suggests influencer profiles + writes outreach emails\n3. Outreach kit emailed for review + Slack notification\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  "Product Hunt Launch Automator": {
    nodes: [
      trigger("Manual: Provide product details", "manual", {
        howItStarts: "You provide product details for a Product Hunt launch",
        setup:
          "Type product name, tagline, description, and your email in chat. Or POST via API",
        inputFields: [
          { name: "product_name", type: "string" },
          { name: "tagline", type: "string" },
          { name: "product_description", type: "string" },
          { name: "founder_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI write PH post + tweets + emails",
        280,
        "Create a complete Product Hunt launch kit. Write the PH post (first comment), 5 launch-day tweets, and 3 email templates (to supporters, to press, to community). Respond JSON: {ph_first_comment: string, tweets_text: string, supporter_email: string, press_email: string, community_email: string, launch_checklist: string}",
        "Product: {product_name}, Tagline: {tagline}, Description: {product_description}",
        [
          { field: "ph_first_comment", as: "ph_first_comment", type: "string" },
          { field: "tweets_text", as: "tweets_text", type: "string" },
          { field: "supporter_email", as: "supporter_email", type: "string" },
          { field: "press_email", as: "press_email", type: "string" },
          { field: "community_email", as: "community_email", type: "string" },
          { field: "launch_checklist", as: "launch_checklist", type: "string" },
        ],
      ),
      emailNode(
        "3",
        "Email launch kit",
        520,
        "{founder_email}",
        "Product Hunt Launch Kit: {product_name}",
        "<h2>{product_name} — {tagline}</h2><h3>PH First Comment</h3><p>{ph_first_comment}</p><h3>Launch Tweets</h3><p>{tweets_text}</p><h3>Supporter Email</h3><p>{supporter_email}</p><h3>Press Email</h3><p>{press_email}</p><h3>Community Email</h3><p>{community_email}</p><h3>Checklist</h3><p>{launch_checklist}</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3")],
    summary: `**Product Hunt Launch Automator**\n\n1. Provide product details\n2. AI generates PH first comment, 5 tweets, 3 email templates, and a launch checklist\n3. Complete launch kit emailed to you\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "Email List Cleaner": {
    nodes: [
      trigger("Manual: Provide email list", "manual", {
        howItStarts: "You provide a list of emails to validate and clean",
        setup:
          "Paste your email list (one per line or comma-separated) in chat, or POST via API with {email_list: '...'}",
        inputFields: [
          { name: "email_list", type: "string" },
          { name: "report_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI validate format + check patterns",
        280,
        "Validate this email list. Check format validity, identify duplicates, flag suspicious patterns (disposable domains, role-based addresses, typos in common domains). Respond JSON: {valid_count: number, invalid_count: number, duplicate_count: number, clean_list: string, invalid_list: string, suspicious_list: string, summary: string}",
        "Emails: {email_list}",
        [
          { field: "valid_count", as: "valid_count", type: "number" },
          { field: "invalid_count", as: "invalid_count", type: "number" },
          { field: "duplicate_count", as: "duplicate_count", type: "number" },
          { field: "clean_list", as: "clean_list", type: "string" },
          { field: "invalid_list", as: "invalid_list", type: "string" },
          { field: "suspicious_list", as: "suspicious_list", type: "string" },
          { field: "summary", as: "clean_summary", type: "string" },
        ],
      ),
      emailNode(
        "3",
        "Email clean list report",
        520,
        "{report_email}",
        "Email List Cleaned: {valid_count} valid, {invalid_count} invalid",
        "<h2>Email List Report</h2><p>{clean_summary}</p><h3>Valid Emails ({valid_count})</h3><p>{clean_list}</p><h3>Invalid Emails ({invalid_count})</h3><p>{invalid_list}</p><h3>Suspicious ({duplicate_count} duplicates)</h3><p>{suspicious_list}</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3")],
    summary: `**Email List Cleaner**\n\n1. Provide your email list\n2. AI validates formats, finds duplicates, flags suspicious patterns\n3. Clean list report emailed to you\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  // ===================================================================
  // E-COMMERCE (EXTENDED)
  // ===================================================================

  "Out of Stock Reorder Alert": {
    nodes: [
      trigger("Schedule: Every day at 7:00 AM", "schedule", {
        schedule: "Every day at 7:00 AM",
        howItStarts:
          "Checks Shopify inventory levels daily for items below reorder threshold",
        setup:
          "Activate and say 'connect shopify' to set up your Shopify API token",
        inputFields: [],
      }),
      n("2", "hitApi", "Get Shopify inventory", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shop_domain}.myshopify.com/admin/api/2024-01/products.json?fields=id,title,variants&limit=100",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          ],
          variables: [
            { name: "products", customName: "all_products", type: "array" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI find items below threshold",
        520,
        'Analyze this product inventory. Find items with quantity below 10 (reorder threshold). For each low item, include the product name and current quantity. Respond JSON: {low_stock_items: string, low_count: number, has_low_stock: \\"true\\" | \\"false\\", most_critical: string}',
        "Products: {all_products}",
        [
          { field: "low_stock_items", as: "low_stock_items", type: "string" },
          { field: "low_count", as: "low_count", type: "number" },
          { field: "has_low_stock", as: "has_low_stock", type: "string" },
          { field: "most_critical", as: "most_critical", type: "string" },
        ],
      ),
      n("4", "condition", "Any items low?", 760, {
        conditionData: [
          {
            variable: "has_low_stock",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "5",
        "Email supplier reorder request",
        1000,
        "supplier@vendor.com",
        "Reorder Request: {low_count} items below threshold",
        "<h2>Reorder Needed</h2><p>{low_count} products are below stock threshold.</p><p>Most critical: {most_critical}</p><p>{low_stock_items}</p><p>Please confirm availability and lead time.</p>",
      ),
      slackNode(
        "6",
        "Alert #operations",
        1000,
        "📦 Reorder alert: {low_count} items below threshold!\\nMost critical: {most_critical}\\n\\n{low_stock_items}",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("4", "6")],
    summary: `**Out of Stock Reorder Alert**\n\n1. Daily Shopify inventory check\n2. AI finds items below reorder threshold (qty < 10)\n3. If low stock → emails supplier + alerts #operations on Slack\n\n**To activate:** Say "connect shopify", "connect openai", "connect email", "connect slack"`,
  },

  "Price Drop Customer Alert": {
    nodes: [
      trigger("Shopify: Product updated", "webhook_app", {
        source: "Shopify",
        event: "products/update",
        howItStarts:
          "A product is updated in your Shopify store (price, title, etc.)",
        setup:
          "In Shopify → Settings → Notifications → Webhooks → Create webhook → Event: 'Product update' → URL: paste your workflow URL. The AI will read whatever data Shopify sends and extract what it needs.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract product update",
        280,
        'Read this Shopify product update webhook and extract the product name, old price, new price, and product URL. Determine if the price decreased. Respond JSON: {product_name: string, old_price: string, new_price: string, product_url: string, price_decreased: \\"true\\" | \\"false\\", discount_pct: string}',
        "{webhook_payload}",
        [
          { field: "product_name", as: "product_name", type: "string" },
          { field: "old_price", as: "old_price", type: "string" },
          { field: "new_price", as: "new_price", type: "string" },
          { field: "product_url", as: "product_url", type: "string" },
          { field: "price_decreased", as: "price_decreased", type: "string" },
          { field: "discount_pct", as: "discount_pct", type: "string" },
        ],
      ),
      n("3", "condition", "Price decreased?", 520, {
        conditionData: [
          {
            variable: "price_decreased",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "4",
        "Email subscribed customers",
        760,
        "subscribers@company.com",
        "Price Drop: {product_name} now ${new_price}!",
        "<p>Great news! <strong>{product_name}</strong> just dropped from ${old_price} to <strong>${new_price}</strong> ({discount_pct}% off).</p><p><a href='{product_url}'>Shop now</a></p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4")],
    summary: `**Price Drop Customer Alert**\n\n1. Shopify product update webhook fires\n2. **AI reads the data** and extracts product name, old/new price, determines if price dropped\n3. If price decreased → emails subscribed customers with discount info\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "Returns Processing": {
    nodes: [
      trigger("Webhook: Return request received", "webhook_app", {
        source: "Shopify / Returns App",
        howItStarts: "A customer submits a return or exchange request",
        setup:
          "In your returns app or Shopify → Webhooks → Event: 'Return requested' → URL: your workflow URL. The AI will read whatever data your returns system sends and extract what it needs.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract return details",
        280,
        "Read this return request webhook data and extract the customer name, customer email, order number, return reason, product name, and whether they want a refund or exchange. Respond JSON: {customer_name: string, customer_email: string, order_number: string, return_reason: string, product_name: string, return_type: string}",
        "{webhook_payload}",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "order_number", as: "order_number", type: "string" },
          { field: "return_reason", as: "return_reason", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "return_type", as: "return_type", type: "string" },
        ],
      ),
      n("3", "condition", "Refund or exchange?", 520, {
        conditionData: [
          {
            variable: "return_type",
            value: "refund",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "4",
        "Email: refund confirmation",
        760,
        "{customer_email}",
        "Refund initiated for order #{order_number}",
        "<p>Hi {customer_name},</p><p>Your refund for <strong>{product_name}</strong> (order #{order_number}) has been initiated.</p><p>Reason: {return_reason}</p><p>Please allow 5-7 business days for the refund to appear.</p>",
      ),
      emailNode(
        "5",
        "Email: exchange confirmation",
        760,
        "{customer_email}",
        "Exchange initiated for order #{order_number}",
        "<p>Hi {customer_name},</p><p>Your exchange for <strong>{product_name}</strong> (order #{order_number}) is being processed.</p><p>We'll ship the replacement once we receive the original item.</p>",
        460,
      ),
      slackNode(
        "6",
        "Notify #warehouse",
        1000,
        "📦 Return: {return_type} for {product_name} (#{order_number})\\nCustomer: {customer_name}\\nReason: {return_reason}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5"), e("3", "6")],
    summary: `**Returns Processing**\n\n1. Return request received via webhook\n2. **AI reads the data** and extracts customer info, order, return type\n3. Refund → refund confirmation email; Exchange → exchange confirmation email\n4. Warehouse notified on Slack\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  "Multi-Marketplace Inventory Sync": {
    nodes: [
      trigger("Schedule: Every 2 hours", "schedule", {
        schedule: "Every 2 hours",
        howItStarts:
          "Syncs inventory levels from your primary marketplace to secondary ones",
        setup:
          "Activate and say 'connect shopify'. Configure your secondary marketplace API in the update node",
        inputFields: [],
      }),
      n("2", "hitApi", "Get inventory from primary (Shopify)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shop_domain}.myshopify.com/admin/api/2024-01/inventory_levels.json?limit=100",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          ],
          variables: [
            {
              name: "inventory_levels",
              customName: "primary_inventory",
              type: "array",
            },
          ],
        },
      }),
      aiNode(
        "3",
        "AI calculate allocations",
        520,
        "Analyze the primary inventory and calculate allocation for a secondary marketplace. Reserve 20% buffer for primary. Respond JSON: {allocation_data: string, items_to_sync: number, total_available: number, sync_summary: string}",
        "Primary inventory: {primary_inventory}",
        [
          { field: "allocation_data", as: "allocation_data", type: "string" },
          { field: "items_to_sync", as: "items_to_sync", type: "number" },
          { field: "total_available", as: "total_available", type: "number" },
          { field: "sync_summary", as: "sync_summary", type: "string" },
        ],
      ),
      n("4", "hitApi", "Update secondary marketplace", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.secondary-marketplace.com/v1/inventory/bulk-update",
          headers: [
            {
              key: "Authorization",
              value: "Bearer {secondary_marketplace_token}",
            },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "inventory", value: "{allocation_data}" }],
          variables: [
            {
              name: "updated_count",
              customName: "updated_count",
              type: "number",
            },
          ],
        },
      }),
      slackNode(
        "5",
        "Confirm sync to #operations",
        1000,
        "🔄 Inventory sync complete: {items_to_sync} items updated on secondary marketplace\\n{sync_summary}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5")],
    summary: `**Multi-Marketplace Inventory Sync**\n\n1. Every 2 hours, pulls inventory from Shopify\n2. AI calculates allocations (20% buffer for primary)\n3. Updates secondary marketplace\n4. Sync confirmation posted to Slack\n\n**To activate:** Say "connect shopify", "connect openai", "connect slack"`,
  },

  "Shipping Status Notifier": {
    nodes: [
      trigger("Webhook: Carrier tracking update", "webhook_app", {
        source: "ShipStation / AfterShip / Carrier",
        howItStarts: "Your shipping carrier sends a tracking status update",
        setup:
          "In your shipping tool (ShipStation, AfterShip) → Webhooks → Event: 'Tracking update' → URL: your workflow URL. The AI will read whatever data your carrier sends and extract what it needs.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract shipping status",
        280,
        'Read this carrier tracking webhook data and extract the customer email, customer name, order number, tracking number, current status (in_transit, out_for_delivery, delivered, exception), and estimated delivery date. Respond JSON: {customer_email: string, customer_name: string, order_number: string, tracking_number: string, status: string, estimated_delivery: string, is_delivered: \\"true\\" | \\"false\\"}',
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "order_number", as: "order_number", type: "string" },
          { field: "tracking_number", as: "tracking_number", type: "string" },
          { field: "status", as: "shipping_status", type: "string" },
          {
            field: "estimated_delivery",
            as: "estimated_delivery",
            type: "string",
          },
          { field: "is_delivered", as: "is_delivered", type: "string" },
        ],
      ),
      emailNode(
        "3",
        "Email customer status update",
        520,
        "{customer_email}",
        "Shipping Update: Order #{order_number}",
        "<p>Hi {customer_name},</p><p>Your order #{order_number} status: <strong>{shipping_status}</strong></p><p>Tracking: {tracking_number}</p><p>Estimated delivery: {estimated_delivery}</p>",
      ),
      n("4", "condition", "Is it delivered?", 760, {
        conditionData: [
          {
            variable: "is_delivered",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      n("5", "delayAction", "Wait 5 days", 1000, {
        delayAction: { time: 5, timeFormat: "days" },
      }),
      emailNode(
        "6",
        "Email review request",
        1200,
        "{customer_email}",
        "How was your order, {customer_name}?",
        "<p>Hi {customer_name},</p><p>Your order #{order_number} was delivered 5 days ago. We'd love to hear your feedback!</p><p>Please leave a review — it helps other customers make great choices.</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
    summary: `**Shipping Status Notifier**\n\n1. Carrier sends tracking update via webhook\n2. **AI reads the data** and extracts customer info, tracking, status\n3. Customer emailed with shipping update\n4. If delivered → wait 5 days → email review request\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "New Product Description Generator": {
    nodes: [
      trigger("Shopify: Product created", "webhook_app", {
        source: "Shopify",
        event: "products/create",
        howItStarts: "A new product is created in your Shopify store",
        setup:
          "In Shopify → Settings → Notifications → Webhooks → Create webhook → Event: 'Product creation' → URL: paste your workflow URL. The AI will read whatever data Shopify sends and extract what it needs.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract product details",
        280,
        "Read this Shopify product creation webhook and extract the product title, product type, vendor, tags, and any existing description. Respond JSON: {product_title: string, product_type: string, vendor: string, tags: string, raw_description: string}",
        "{webhook_payload}",
        [
          { field: "product_title", as: "product_title", type: "string" },
          { field: "product_type", as: "product_type", type: "string" },
          { field: "vendor", as: "vendor", type: "string" },
          { field: "tags", as: "tags", type: "string" },
          { field: "raw_description", as: "raw_description", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "AI generate SEO description + features",
        520,
        "Generate an SEO-optimized product description and a bullet-point feature list. Include relevant keywords naturally. Respond JSON: {seo_description: string, feature_list: string, meta_title: string, meta_description: string, suggested_keywords: string}",
        "Product: {product_title}, Type: {product_type}, Vendor: {vendor}, Tags: {tags}, Raw description: {raw_description}",
        [
          { field: "seo_description", as: "seo_description", type: "string" },
          { field: "feature_list", as: "feature_list", type: "string" },
          { field: "meta_title", as: "meta_title", type: "string" },
          { field: "meta_description", as: "meta_description", type: "string" },
          {
            field: "suggested_keywords",
            as: "suggested_keywords",
            type: "string",
          },
        ],
      ),
      emailNode(
        "4",
        "Email marketing team",
        760,
        "marketing@company.com",
        "New Product Description: {product_title}",
        "<h2>{product_title}</h2><h3>SEO Description</h3><p>{seo_description}</p><h3>Features</h3><p>{feature_list}</p><h3>Meta</h3><p>Title: {meta_title}</p><p>Description: {meta_description}</p><p>Keywords: {suggested_keywords}</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4")],
    summary: `**New Product Description Generator**\n\n1. New product created in Shopify\n2. **AI reads the data** and extracts product details\n3. AI generates SEO description, feature list, and meta tags\n4. Emailed to marketing team for review\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "Loyalty Points Calculator": {
    nodes: [
      trigger("Webhook: Order completed", "webhook_app", {
        source: "Shopify / WooCommerce",
        event: "orders/paid",
        howItStarts: "A customer completes an order and payment is confirmed",
        setup:
          "In Shopify → Settings → Notifications → Webhooks → Create webhook → Event: 'Order payment' → URL: paste your workflow URL. The AI will read whatever data your store sends.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract order value",
        280,
        "Read this order webhook data and extract the customer email, customer name, order total, and order number. Respond JSON: {customer_email: string, customer_name: string, order_total: number, order_number: string}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "order_total", as: "order_total", type: "number" },
          { field: "order_number", as: "order_number", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "AI calculate points",
        520,
        "Calculate loyalty points for this order. Rules: 1 point per $1 spent, bonus 50 points for orders over $100, double points for orders over $250. Respond JSON: {points_earned: number, bonus_applied: string, total_points_text: string, tier_status: string}",
        "Customer: {customer_name}, Order total: ${order_total}, Order: #{order_number}",
        [
          { field: "points_earned", as: "points_earned", type: "number" },
          { field: "bonus_applied", as: "bonus_applied", type: "string" },
          {
            field: "total_points_text",
            as: "total_points_text",
            type: "string",
          },
          { field: "tier_status", as: "tier_status", type: "string" },
        ],
      ),
      emailNode(
        "4",
        "Email customer points update",
        760,
        "{customer_email}",
        "You earned {points_earned} loyalty points!",
        "<p>Hi {customer_name},</p><p>Thanks for order #{order_number}!</p><p>You earned <strong>{points_earned} points</strong>. {bonus_applied}</p><p>{total_points_text}</p><p>Tier: {tier_status}</p>",
      ),
      n(
        "5",
        "hitApi",
        "Log to Google Sheets",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/LoyaltyPoints:append?valueInputOption=RAW",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "values",
                value:
                  '[["{customer_email}","{customer_name}","{order_number}","{order_total}","{points_earned}","{bonus_applied}","{tier_status}"]]',
              },
            ],
            variables: [
              {
                name: "updates.updatedRows",
                customName: "rows_added",
                type: "number",
              },
            ],
          },
        },
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5")],
    summary: `**Loyalty Points Calculator**\n\n1. Order completed via webhook\n2. **AI reads the data** and extracts customer info and order value\n3. AI calculates loyalty points (with bonus tiers)\n4. Customer emailed with points update\n5. Logged to Google Sheets\n\n**To activate:** Say "connect openai", "connect email", "connect google sheets"`,
  },

  "Affiliate Sales Tracker": {
    nodes: [
      trigger("Webhook: Sale with affiliate code", "webhook_app", {
        source: "Shopify / Your App",
        howItStarts:
          "A sale is completed that includes an affiliate/referral code",
        setup:
          "In your store or app → Webhooks → Event: 'Order created' → URL: your workflow URL. The AI will read whatever data your system sends and find the affiliate code.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract sale + affiliate code",
        280,
        "Read this order/sale webhook data and extract the order total, order number, customer email, and affiliate or referral code. Different systems embed affiliate codes in different places — find the affiliate code regardless of format. Respond JSON: {order_total: number, order_number: string, customer_email: string, affiliate_code: string, affiliate_email: string}",
        "{webhook_payload}",
        [
          { field: "order_total", as: "order_total", type: "number" },
          { field: "order_number", as: "order_number", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "affiliate_code", as: "affiliate_code", type: "string" },
          { field: "affiliate_email", as: "affiliate_email", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "AI calculate commission",
        520,
        'Calculate the affiliate commission. Rules: 10% commission on order total, cap at $100 per sale. Respond JSON: {commission_amount: number, commission_rate: string, is_capped: \\"true\\" | \\"false\\", payout_summary: string}',
        "Order total: ${order_total}, Affiliate code: {affiliate_code}, Order: #{order_number}",
        [
          {
            field: "commission_amount",
            as: "commission_amount",
            type: "number",
          },
          { field: "commission_rate", as: "commission_rate", type: "string" },
          { field: "is_capped", as: "is_capped", type: "string" },
          { field: "payout_summary", as: "payout_summary", type: "string" },
        ],
      ),
      emailNode(
        "4",
        "Email affiliate notification",
        760,
        "{affiliate_email}",
        "You earned ${commission_amount} commission!",
        "<p>Great news! A sale was made using your code <strong>{affiliate_code}</strong>.</p><p>Order #{order_number}: ${order_total}</p><p>Your commission: <strong>${commission_amount}</strong> ({commission_rate})</p><p>{payout_summary}</p>",
      ),
      n(
        "5",
        "hitApi",
        "Log to Google Sheets",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Affiliates:append?valueInputOption=RAW",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "values",
                value:
                  '[["{affiliate_code}","{affiliate_email}","{order_number}","{order_total}","{commission_amount}","{commission_rate}"]]',
              },
            ],
            variables: [
              {
                name: "updates.updatedRows",
                customName: "rows_added",
                type: "number",
              },
            ],
          },
        },
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5")],
    summary: `**Affiliate Sales Tracker**\n\n1. Sale with affiliate code via webhook\n2. **AI reads the data** and extracts order details + affiliate code\n3. AI calculates commission (10%, capped at $100)\n4. Affiliate emailed with earnings\n5. Logged to Google Sheets\n\n**To activate:** Say "connect openai", "connect email", "connect google sheets"`,
  },

  // ===================================================================
  // HR & RECRUITING (EXTENDED)
  // ===================================================================

  "AI CV Analyzer": {
    nodes: [
      trigger("Form: Job application received", "webhook_form", {
        source: "Outgrow Forms / Greenhouse / Application Form",
        howItStarts:
          "A candidate submits a job application with their CV/resume",
        setup:
          "In your application form or ATS → Webhooks → paste your workflow URL. The AI will read whatever data your form sends and extract the CV content and job requirements.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract application data",
        280,
        "Read this application webhook data and extract the candidate name, candidate email, CV/resume text, job title, and job requirements. Different forms and ATS systems send data in different formats — find the candidate name, candidate email, resume text, job title, and job requirements regardless of field naming. Respond JSON: {candidate_name: string, candidate_email: string, cv_text: string, job_title: string, job_requirements: string, recruiter_email: string}",
        "{webhook_payload}",
        [
          { field: "candidate_name", as: "candidate_name", type: "string" },
          { field: "candidate_email", as: "candidate_email", type: "string" },
          { field: "cv_text", as: "cv_text", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "job_requirements", as: "job_requirements", type: "string" },
          { field: "recruiter_email", as: "recruiter_email", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "AI analyze CV against requirements",
        520,
        "Analyze this CV against the job requirements. Score the candidate 0-100. Identify strengths, gaps, and provide a hiring recommendation. Respond JSON: {match_score: number, strengths: string, gaps: string, recommendation: string, experience_years: string, key_skills: string}",
        "Job: {job_title}\\nRequirements: {job_requirements}\\nCV: {cv_text}",
        [
          { field: "match_score", as: "match_score", type: "number" },
          { field: "strengths", as: "strengths", type: "string" },
          { field: "gaps", as: "gaps", type: "string" },
          { field: "recommendation", as: "recommendation", type: "string" },
          { field: "experience_years", as: "experience_years", type: "string" },
          { field: "key_skills", as: "key_skills", type: "string" },
        ],
      ),
      n("4", "condition", "Score > 75?", 760, {
        conditionData: [
          {
            variable: "match_score",
            value: "75",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "5",
        "Email recruiter: strong candidate",
        1000,
        "{recruiter_email}",
        "Strong Candidate: {candidate_name} ({match_score}/100) for {job_title}",
        "<h2>{candidate_name} — Score: {match_score}/100</h2><p>Recommendation: {recommendation}</p><h3>Strengths</h3><p>{strengths}</p><h3>Gaps</h3><p>{gaps}</p><p>Experience: {experience_years}</p><p>Key skills: {key_skills}</p><p>Contact: {candidate_email}</p>",
      ),
      emailNode(
        "6",
        "Email recruiter: review needed",
        1000,
        "{recruiter_email}",
        "Application Review: {candidate_name} ({match_score}/100) for {job_title}",
        "<p>{candidate_name} scored {match_score}/100 for {job_title}.</p><p>Recommendation: {recommendation}</p><p>Gaps: {gaps}</p><p>Contact: {candidate_email}</p>",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("4", "6")],
    summary: `**AI CV Analyzer**\n\n1. Application received via form\n2. **AI reads the data** and extracts candidate info, CV, job requirements\n3. AI scores CV 0-100 with strengths, gaps, recommendation\n4. Score > 75 → recruiter gets detailed strong-candidate email\n5. Score ≤ 75 → recruiter gets review-needed email\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "Automated Reference Check": {
    nodes: [
      trigger("Form: Candidate approved for references", "webhook_form", {
        source: "Internal Form / ATS",
        howItStarts:
          "A recruiter approves a candidate and provides reference contacts",
        setup:
          "In your form or ATS → Webhooks → paste your workflow URL. Include candidate name, reference emails, and job title. The AI will read whatever data your form sends.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract reference details",
        280,
        "Read this form/webhook data and extract the candidate name, job title, reference email addresses (comma-separated), and recruiter email. Respond JSON: {candidate_name: string, job_title: string, reference_emails: string, recruiter_email: string, reference_count: number}",
        "{webhook_payload}",
        [
          { field: "candidate_name", as: "candidate_name", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "reference_emails", as: "reference_emails", type: "string" },
          { field: "recruiter_email", as: "recruiter_email", type: "string" },
          { field: "reference_count", as: "reference_count", type: "number" },
        ],
      ),
      emailNode(
        "3",
        "Email references with questions",
        520,
        "{reference_emails}",
        "Reference Check: {candidate_name} for {job_title}",
        "<p>Hello,</p><p>{candidate_name} has listed you as a professional reference for the <strong>{job_title}</strong> position.</p><p>Could you please answer the following questions?</p><ol><li>How long and in what capacity did you work with {candidate_name}?</li><li>What are their greatest strengths?</li><li>What areas could they improve?</li><li>Would you recommend them for this role?</li></ol><p>Please reply to this email with your responses. Thank you!</p>",
      ),
      emailNode(
        "4",
        "Notify recruiter",
        520,
        "{recruiter_email}",
        "Reference check emails sent for {candidate_name}",
        "<p>Reference check emails have been sent to {reference_count} contacts for {candidate_name} ({job_title}).</p><p>References contacted: {reference_emails}</p><p>Responses will come via reply email.</p>",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("2", "4")],
    summary: `**Automated Reference Check**\n\n1. Candidate approved for reference check\n2. **AI reads the data** and extracts candidate info and reference emails\n3. Reference contacts emailed with structured questions\n4. Recruiter notified that emails were sent\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "New Hire IT Setup Checklist": {
    nodes: [
      trigger("Webhook: New employee added to HR", "webhook_app", {
        source: "BambooHR / Gusto / Rippling",
        event: "employee.created",
        howItStarts: "A new employee is added to your HR system",
        setup:
          "In your HR tool → Integrations → Webhooks → Event: 'New employee' → URL: your workflow URL. The AI will read whatever data your HR system sends and extract what it needs.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract employee details",
        280,
        "Read this HR system webhook data and extract the employee name, employee email, job title, department, start date, and manager name. Different HR systems send data in different formats — find these fields regardless of field naming. Respond JSON: {emp_name: string, emp_email: string, job_title: string, department: string, start_date: string, manager_name: string}",
        "{webhook_payload}",
        [
          { field: "emp_name", as: "emp_name", type: "string" },
          { field: "emp_email", as: "emp_email", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "start_date", as: "start_date", type: "string" },
          { field: "manager_name", as: "manager_name", type: "string" },
        ],
      ),
      emailNode(
        "3",
        "Email IT with setup list",
        520,
        "it@company.com",
        "IT Setup Needed: {emp_name} starting {start_date}",
        "<h2>New Hire IT Setup</h2><p><strong>{emp_name}</strong> — {job_title}, {department}</p><p>Start date: {start_date}</p><p>Manager: {manager_name}</p><h3>Checklist:</h3><ul><li>Create email account: {emp_email}</li><li>Provision laptop</li><li>Set up VPN access</li><li>Create Slack account</li><li>Add to {department} Google Drive</li><li>Set up 2FA</li><li>Schedule orientation IT session</li></ul>",
      ),
      emailNode(
        "4",
        "Email new hire welcome",
        520,
        "{emp_email}",
        "Welcome to the team, {emp_name}!",
        "<p>Hi {emp_name},</p><p>Welcome aboard as <strong>{job_title}</strong> in {department}!</p><p>Your start date is {start_date}. Your manager {manager_name} is looking forward to meeting you.</p><p>IT is setting up your accounts — you'll get access details before your first day.</p>",
        460,
      ),
      slackNode(
        "5",
        "Post to #onboarding",
        760,
        "👋 New hire: {emp_name} — {job_title} ({department})\\nStart date: {start_date}\\nManager: {manager_name}\\nIT setup in progress!",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("2", "4"), e("2", "5")],
    summary: `**New Hire IT Setup Checklist**\n\n1. New employee added to HR system\n2. **AI reads the data** and extracts employee name, email, title, department, start date, manager\n3. IT team emailed with full setup checklist\n4. New hire gets welcome email\n5. #onboarding channel notified on Slack\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  "Employee Birthday Alert": {
    nodes: [
      trigger("Schedule: Every day at 8:00 AM", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts: "Checks for employee birthdays daily",
        setup:
          "Activate and say 'connect openai'. Provide employee birthday data (from HR system or spreadsheet) in the AI node",
        inputFields: [
          { name: "employee_data", type: "string" },
          { name: "today_date", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI check upcoming birthdays",
        280,
        'Check this employee data for birthdays today. Respond JSON: {birthday_names: string, has_birthday_today: \\"true\\" | \\"false\\", birthday_count: number, celebration_message: string}',
        "Today: {today_date}\\nEmployees: {employee_data}",
        [
          { field: "birthday_names", as: "birthday_names", type: "string" },
          {
            field: "has_birthday_today",
            as: "has_birthday_today",
            type: "string",
          },
          { field: "birthday_count", as: "birthday_count", type: "number" },
          {
            field: "celebration_message",
            as: "celebration_message",
            type: "string",
          },
        ],
      ),
      n("3", "condition", "Any birthdays today?", 520, {
        conditionData: [
          {
            variable: "has_birthday_today",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode("4", "Celebrate in Slack", 760, "🎂 {celebration_message}"),
      emailNode(
        "5",
        "Email team",
        760,
        "team@company.com",
        "Birthday Celebration: {birthday_names}!",
        "<p>{celebration_message}</p><p>Let's make their day special!</p>",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5")],
    summary: `**Employee Birthday Alert**\n\n1. Runs every morning at 8 AM\n2. AI checks employee data for birthdays today\n3. If birthdays found → Slack celebration + email to team\n\n**To activate:** Say "connect openai", "connect slack", "connect email"`,
  },

  "Performance Review Reminder": {
    nodes: [
      trigger("Schedule: First Monday of every month", "schedule", {
        schedule: "First Monday of every month at 9:00 AM",
        howItStarts: "Monthly check for upcoming performance reviews",
        setup:
          "Activate and say 'connect openai'. Provide review schedule data in the AI node",
        inputFields: [
          { name: "review_data", type: "string" },
          { name: "current_month", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI check review dates",
        280,
        'Analyze the review schedule and find any performance reviews due this month. Respond JSON: {reviews_due: string, review_count: number, has_reviews_due: \\"true\\" | \\"false\\", manager_list: string}',
        "Current month: {current_month}\\nReview schedule: {review_data}",
        [
          { field: "reviews_due", as: "reviews_due", type: "string" },
          { field: "review_count", as: "review_count", type: "number" },
          { field: "has_reviews_due", as: "has_reviews_due", type: "string" },
          { field: "manager_list", as: "manager_list", type: "string" },
        ],
      ),
      n("3", "condition", "Reviews due this month?", 520, {
        conditionData: [
          {
            variable: "has_reviews_due",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "4",
        "Email managers",
        760,
        "managers@company.com",
        "Performance Reviews Due: {review_count} this month",
        "<h2>Performance Reviews Due This Month</h2><p>{review_count} reviews are scheduled.</p><p>{reviews_due}</p><p>Managers involved: {manager_list}</p><p>Please complete reviews by end of month.</p>",
      ),
      slackNode(
        "5",
        "Notify #hr",
        760,
        "📋 {review_count} performance reviews due this month\\n{reviews_due}\\nManagers: {manager_list}",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5")],
    summary: `**Performance Review Reminder**\n\n1. Monthly check for upcoming reviews\n2. AI identifies reviews due this month\n3. If reviews due → emails managers + alerts #hr on Slack\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  "Timesheet Approval Workflow": {
    nodes: [
      trigger("Form: Timesheet submitted", "webhook_form", {
        source: "Timesheet Form / Internal Tool",
        howItStarts: "An employee submits their weekly timesheet",
        setup:
          "In your timesheet form → Integrations → Webhooks → paste your workflow URL. The AI will read whatever data your form sends and extract hours, project, and employee info.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract hours + project",
        280,
        "Read this timesheet form data and extract the employee name, employee email, manager email, total hours, project name, and week dates. Different forms send data in different formats — find the employee name, employee email, manager email, total hours, project, and week dates regardless of field naming. Respond JSON: {employee_name: string, employee_email: string, manager_email: string, total_hours: number, project_name: string, week_dates: string}",
        "{webhook_payload}",
        [
          { field: "employee_name", as: "employee_name", type: "string" },
          { field: "employee_email", as: "employee_email", type: "string" },
          { field: "manager_email", as: "manager_email", type: "string" },
          { field: "total_hours", as: "total_hours", type: "number" },
          { field: "project_name", as: "project_name", type: "string" },
          { field: "week_dates", as: "week_dates", type: "string" },
        ],
      ),
      n("3", "condition", "Over 40 hours?", 520, {
        conditionData: [
          {
            variable: "total_hours",
            value: "40",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "4",
        "Email manager for approval (overtime)",
        760,
        "{manager_email}",
        "Overtime Approval Needed: {employee_name} ({total_hours}h)",
        "<p>{employee_name} submitted {total_hours} hours for week {week_dates}.</p><p>Project: {project_name}</p><p>This exceeds 40 hours and requires your approval.</p>",
      ),
      n(
        "5",
        "hitApi",
        "Log to Google Sheets",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Timesheets:append?valueInputOption=RAW",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "values",
                value:
                  '[["{employee_name}","{employee_email}","{project_name}","{total_hours}","{week_dates}"]]',
              },
            ],
            variables: [
              {
                name: "updates.updatedRows",
                customName: "rows_added",
                type: "number",
              },
            ],
          },
        },
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("2", "5")],
    summary: `**Timesheet Approval Workflow**\n\n1. Employee submits timesheet via form\n2. **AI reads the data** and extracts hours, project, employee info\n3. Over 40 hours → manager emailed for overtime approval\n4. All timesheets logged to Google Sheets\n\n**To activate:** Say "connect openai", "connect email", "connect google sheets"`,
  },

  // ===================================================================
  // FINANCE (EXTENDED)
  // ===================================================================

  "Recurring Invoice Generator": {
    nodes: [
      trigger("Schedule: First of every month at 9:00 AM", "schedule", {
        schedule: "First of every month at 9:00 AM",
        howItStarts: "Generates and sends recurring invoices monthly",
        setup:
          "Activate and say 'connect openai'. Provide subscription/client data in the AI node",
        inputFields: [
          { name: "subscription_data", type: "string" },
          { name: "company_name", type: "string" },
          { name: "billing_month", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI generate invoice from subscription data",
        280,
        "Generate invoice details from this subscription data. Include line items, subtotals, tax, and total. Respond JSON: {client_name: string, client_email: string, invoice_number: string, line_items: string, subtotal: string, tax_amount: string, total_amount: string, due_date: string}",
        "Company: {company_name}, Billing month: {billing_month}, Subscriptions: {subscription_data}",
        [
          { field: "client_name", as: "client_name", type: "string" },
          { field: "client_email", as: "client_email", type: "string" },
          { field: "invoice_number", as: "invoice_number", type: "string" },
          { field: "line_items", as: "line_items", type: "string" },
          { field: "subtotal", as: "subtotal", type: "string" },
          { field: "tax_amount", as: "tax_amount", type: "string" },
          { field: "total_amount", as: "total_amount", type: "string" },
          { field: "due_date", as: "due_date", type: "string" },
        ],
      ),
      emailNode(
        "3",
        "Email invoice to customer",
        520,
        "{client_email}",
        "Invoice #{invoice_number} from {company_name}",
        "<h2>Invoice #{invoice_number}</h2><p>From: {company_name}</p><p>To: {client_name}</p><h3>Items</h3><p>{line_items}</p><p>Subtotal: ${subtotal}</p><p>Tax: ${tax_amount}</p><p><strong>Total: ${total_amount}</strong></p><p>Due: {due_date}</p>",
      ),
      n(
        "4",
        "hitApi",
        "Log to Google Sheets",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Invoices:append?valueInputOption=RAW",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "values",
                value:
                  '[["{invoice_number}","{client_name}","{client_email}","{total_amount}","{due_date}","{billing_month}"]]',
              },
            ],
            variables: [
              {
                name: "updates.updatedRows",
                customName: "rows_added",
                type: "number",
              },
            ],
          },
        },
        460,
      ),
      slackNode(
        "5",
        "Notify #finance",
        760,
        "💰 Invoice #{invoice_number} sent to {client_name} — ${total_amount} due {due_date}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("2", "4"), e("2", "5")],
    summary: `**Recurring Invoice Generator**\n\n1. Runs first of every month\n2. AI generates invoices from subscription data\n3. Invoices emailed to customers\n4. Logged to Google Sheets\n5. #finance notified on Slack\n\n**To activate:** Say "connect openai", "connect email", "connect google sheets", "connect slack"`,
  },

  "Receipt Expense Categorizer": {
    nodes: [
      trigger("Email: Receipt received", "email", {
        howItStarts: "A receipt email arrives in your inbox",
        setup:
          "Forward receipt emails to your workflow inbox, or set up email rules to auto-forward receipts",
        inputFields: [
          { name: "sender_email", type: "string" },
          { name: "email_subject", type: "string" },
          { name: "email_body", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI extract vendor + amount + category",
        280,
        'Extract expense details from this receipt email. Determine the vendor name, total amount, date, and categorize the expense (travel, meals, software, office supplies, etc.). Respond JSON: {vendor_name: string, amount: number, expense_date: string, category: string, description: string, is_over_500: \\"true\\" | \\"false\\"}',
        "From: {sender_email}\\nSubject: {email_subject}\\nBody: {email_body}",
        [
          { field: "vendor_name", as: "vendor_name", type: "string" },
          { field: "amount", as: "amount", type: "number" },
          { field: "expense_date", as: "expense_date", type: "string" },
          { field: "category", as: "category", type: "string" },
          { field: "description", as: "description", type: "string" },
          { field: "is_over_500", as: "is_over_500", type: "string" },
        ],
      ),
      n("3", "hitApi", "Log to Google Sheets", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Expenses:append?valueInputOption=RAW",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{vendor_name}","{amount}","{expense_date}","{category}","{description}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_added",
              type: "number",
            },
          ],
        },
      }),
      n("4", "condition", "Amount > $500?", 760, {
        conditionData: [
          {
            variable: "is_over_500",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "5",
        "Email manager for review",
        1000,
        "manager@company.com",
        "Large Expense Alert: ${amount} at {vendor_name}",
        "<p>A receipt over $500 was automatically categorized.</p><p>Vendor: {vendor_name}</p><p>Amount: ${amount}</p><p>Category: {category}</p><p>Date: {expense_date}</p><p>Description: {description}</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5")],
    summary: `**Receipt Expense Categorizer**\n\n1. Receipt email arrives\n2. AI extracts vendor, amount, date, and categorizes the expense\n3. Logged to Google Sheets\n4. If amount > $500 → manager emailed for review\n\n**To activate:** Say "connect openai", "connect google sheets", "connect email"`,
  },

  "Bank Transaction Categorizer": {
    nodes: [
      trigger("Schedule: Every day at 8:00 AM", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts: "Pulls and categorizes yesterday's bank transactions",
        setup:
          "Activate and say 'connect plaid' or configure your banking API. Set your finance team email",
        inputFields: [{ name: "finance_email", type: "string" }],
      }),
      n("2", "hitApi", "Get transactions from bank API", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.plaid.com/transactions/get",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "client_id", value: "{plaid_client_id}" },
            { key: "secret", value: "{plaid_secret}" },
            { key: "access_token", value: "{plaid_access_token}" },
          ],
          variables: [
            { name: "transactions", customName: "transactions", type: "array" },
            {
              name: "total_transactions",
              customName: "transaction_count",
              type: "number",
            },
          ],
        },
      }),
      aiNode(
        "3",
        "AI categorize each transaction",
        520,
        "Categorize each transaction into business categories (payroll, rent, utilities, marketing, travel, software, supplies, etc.). Summarize totals by category. Respond JSON: {categorized_summary: string, total_spend: number, largest_category: string, unusual_transactions: string, daily_summary: string}",
        "Transactions: {transactions}",
        [
          {
            field: "categorized_summary",
            as: "categorized_summary",
            type: "string",
          },
          { field: "total_spend", as: "total_spend", type: "number" },
          { field: "largest_category", as: "largest_category", type: "string" },
          {
            field: "unusual_transactions",
            as: "unusual_transactions",
            type: "string",
          },
          { field: "daily_summary", as: "daily_summary", type: "string" },
        ],
      ),
      n("4", "hitApi", "Log to Google Sheets", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Transactions:append?valueInputOption=RAW",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{daily_summary}","{total_spend}","{largest_category}","{unusual_transactions}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_added",
              type: "number",
            },
          ],
        },
      }),
      emailNode(
        "5",
        "Email daily summary",
        760,
        "{finance_email}",
        "Daily Transaction Summary: ${total_spend} total",
        "<h2>Daily Transaction Report</h2><p>{daily_summary}</p><p>Total spend: <strong>${total_spend}</strong></p><p>Largest category: {largest_category}</p><h3>By Category</h3><p>{categorized_summary}</p><h3>Unusual Transactions</h3><p>{unusual_transactions}</p>",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5")],
    summary: `**Bank Transaction Categorizer**\n\n1. Daily pull of bank transactions\n2. AI categorizes each transaction into business categories\n3. Logged to Google Sheets\n4. Daily summary emailed to finance team\n\n**To activate:** Say "connect plaid" (or bank API), "connect openai", "connect google sheets", "connect email"`,
  },

  "Tax Document Organizer": {
    nodes: [
      trigger("Email: Tax document received", "email", {
        howItStarts: "A tax-related document email arrives",
        setup:
          "Forward tax document emails to your workflow inbox, or set up rules to auto-forward emails from known tax senders (IRS, accountant, payroll provider)",
        inputFields: [
          { name: "sender_email", type: "string" },
          { name: "email_subject", type: "string" },
          { name: "email_body", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI extract document type + details",
        280,
        "Analyze this tax document email. Extract the document type (W-2, 1099, receipt, K-1, etc.), tax year, sender, key amounts, and any action needed. Respond JSON: {doc_type: string, tax_year: string, sender_name: string, key_amounts: string, action_needed: string, filing_category: string}",
        "From: {sender_email}\\nSubject: {email_subject}\\nBody: {email_body}",
        [
          { field: "doc_type", as: "doc_type", type: "string" },
          { field: "tax_year", as: "tax_year", type: "string" },
          { field: "sender_name", as: "sender_name", type: "string" },
          { field: "key_amounts", as: "key_amounts", type: "string" },
          { field: "action_needed", as: "action_needed", type: "string" },
          { field: "filing_category", as: "filing_category", type: "string" },
        ],
      ),
      n("3", "hitApi", "Log to Google Sheets", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/TaxDocs:append?valueInputOption=RAW",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{doc_type}","{tax_year}","{sender_name}","{key_amounts}","{action_needed}","{filing_category}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_added",
              type: "number",
            },
          ],
        },
      }),
      emailNode(
        "4",
        "Email confirmation",
        520,
        "finance@company.com",
        "Tax Doc Filed: {doc_type} ({tax_year})",
        "<p>A tax document has been automatically categorized and logged.</p><p>Type: {doc_type}</p><p>Tax year: {tax_year}</p><p>From: {sender_name}</p><p>Key amounts: {key_amounts}</p><p>Action needed: {action_needed}</p><p>Category: {filing_category}</p>",
        460,
      ),
      slackNode(
        "5",
        "Notify #finance",
        760,
        "📄 Tax document received: {doc_type} ({tax_year}) from {sender_name}\\nAction: {action_needed}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("2", "4"), e("2", "5")],
    summary: `**Tax Document Organizer**\n\n1. Tax document email arrives\n2. AI extracts document type, tax year, amounts, and action needed\n3. Logged to Google Sheets\n4. Confirmation emailed to finance\n5. #finance notified on Slack\n\n**To activate:** Say "connect openai", "connect google sheets", "connect email", "connect slack"`,
  },

  "Payroll Change Alert": {
    nodes: [
      trigger("Webhook: HR system update", "webhook_app", {
        source: "BambooHR / Gusto / Rippling",
        howItStarts:
          "Your HR system sends an update when employee details change",
        setup:
          "In your HR tool → Integrations → Webhooks → Event: 'Employee updated' → URL: your workflow URL. The AI will read whatever data your HR system sends and detect salary or role changes.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract change details",
        280,
        'Read this HR system webhook data and extract the employee name, change type, old value, new value, effective date, and department. Determine if this is a salary or role change. Respond JSON: {employee_name: string, change_type: string, old_value: string, new_value: string, effective_date: string, department: string, is_salary_or_role_change: \\"true\\" | \\"false\\"}',
        "{webhook_payload}",
        [
          { field: "employee_name", as: "employee_name", type: "string" },
          { field: "change_type", as: "change_type", type: "string" },
          { field: "old_value", as: "old_value", type: "string" },
          { field: "new_value", as: "new_value", type: "string" },
          { field: "effective_date", as: "effective_date", type: "string" },
          { field: "department", as: "department", type: "string" },
          {
            field: "is_salary_or_role_change",
            as: "is_salary_or_role_change",
            type: "string",
          },
        ],
      ),
      n("3", "condition", "Salary or role change?", 520, {
        conditionData: [
          {
            variable: "is_salary_or_role_change",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "4",
        "Email HR director + finance",
        760,
        "hr-director@company.com",
        "Payroll Change: {employee_name} — {change_type}",
        "<h2>Employee Change Alert</h2><p>Employee: {employee_name} ({department})</p><p>Change: {change_type}</p><p>From: {old_value} → To: {new_value}</p><p>Effective: {effective_date}</p><p>Please update payroll records accordingly.</p>",
      ),
      n(
        "5",
        "hitApi",
        "Log to Google Sheets",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/PayrollChanges:append?valueInputOption=RAW",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "values",
                value:
                  '[["{employee_name}","{change_type}","{old_value}","{new_value}","{effective_date}","{department}"]]',
              },
            ],
            variables: [
              {
                name: "updates.updatedRows",
                customName: "rows_added",
                type: "number",
              },
            ],
          },
        },
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5")],
    summary: `**Payroll Change Alert**\n\n1. HR system sends employee update via webhook\n2. **AI reads the data** and extracts change type, old/new values, effective date\n3. If salary or role change → emails HR director + finance\n4. All changes logged to Google Sheets\n\n**To activate:** Say "connect openai", "connect email", "connect google sheets"`,
  },

  "Financial Report Generator": {
    nodes: [
      trigger("Schedule: Every Friday at 5:00 PM", "schedule", {
        schedule: "Every Friday at 5:00 PM",
        howItStarts: "Generates a weekly financial summary for leadership",
        setup:
          "Activate and say 'connect openai'. Configure your data sources (revenue, expenses, cash flow) in the AI node",
        inputFields: [
          { name: "revenue_data", type: "string" },
          { name: "expense_data", type: "string" },
          { name: "cash_flow_data", type: "string" },
          { name: "leadership_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI compile executive summary",
        280,
        "Compile a weekly financial executive summary from the provided data. Include revenue vs target, expense highlights, cash flow status, and key concerns. Respond JSON: {executive_summary: string, revenue_total: string, expense_total: string, net_income: string, cash_position: string, key_highlights: string, concerns: string, week_over_week_trend: string}",
        "Revenue: {revenue_data}, Expenses: {expense_data}, Cash flow: {cash_flow_data}",
        [
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "revenue_total", as: "revenue_total", type: "string" },
          { field: "expense_total", as: "expense_total", type: "string" },
          { field: "net_income", as: "net_income", type: "string" },
          { field: "cash_position", as: "cash_position", type: "string" },
          { field: "key_highlights", as: "key_highlights", type: "string" },
          { field: "concerns", as: "concerns", type: "string" },
          {
            field: "week_over_week_trend",
            as: "week_over_week_trend",
            type: "string",
          },
        ],
      ),
      emailNode(
        "3",
        "Email leadership",
        520,
        "{leadership_email}",
        "Weekly Financial Report: Revenue ${revenue_total}",
        "<h2>Weekly Financial Summary</h2><p>{executive_summary}</p><h3>Key Metrics</h3><p>Revenue: ${revenue_total}</p><p>Expenses: ${expense_total}</p><p>Net income: ${net_income}</p><p>Cash position: ${cash_position}</p><h3>Highlights</h3><p>{key_highlights}</p><h3>Concerns</h3><p>{concerns}</p><p>Week-over-week: {week_over_week_trend}</p>",
      ),
      slackNode(
        "4",
        "Post to #finance",
        520,
        "📊 Weekly Financial Report\\nRevenue: ${revenue_total} | Expenses: ${expense_total} | Net: ${net_income}\\n{week_over_week_trend}\\n\\nFull report emailed to leadership.",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("2", "4")],
    summary: `**Financial Report Generator**\n\n1. Runs every Friday at 5 PM\n2. AI compiles financial data into an executive summary\n3. Report emailed to leadership\n4. Summary posted to #finance on Slack\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  // ===================================================================
  // BATCH 4 — Templates 76-100
  // ===================================================================

  "Subscription Box Manager": {
    nodes: [
      trigger("Schedule: Monthly on the 1st", "schedule", {
        schedule: "Every month on the 1st at 9:00 AM",
        howItStarts:
          "Runs automatically on the first of every month to plan the next subscription box",
        setup:
          "Activate this workflow. Connect OpenAI for content generation, email for customer preview, and Slack for fulfillment coordination",
        inputFields: [
          { name: "box_theme", type: "string" },
          { name: "customer_list_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI generate next box contents",
        280,
        "Generate the contents for next month's subscription box. Include 4-6 items with descriptions and estimated costs. Respond JSON: {box_name: string, items_list: string, item_count: number, estimated_cost: number, theme_description: string}",
        "Theme: {box_theme}",
        [
          { field: "box_name", as: "box_name", type: "string" },
          { field: "items_list", as: "items_list", type: "string" },
          { field: "item_count", as: "item_count", type: "number" },
          { field: "estimated_cost", as: "estimated_cost", type: "number" },
          {
            field: "theme_description",
            as: "theme_description",
            type: "string",
          },
        ],
      ),
      emailNode(
        "3",
        "Email customers preview",
        520,
        "{customer_list_email}",
        "Sneak Peek: {box_name} — Next Month's Box!",
        "<h2>{box_name}</h2><p>{theme_description}</p><h3>What's inside:</h3><p>{items_list}</p><p>{item_count} items, curated just for you!</p>",
      ),
      slackNode(
        "4",
        "Notify #fulfillment",
        520,
        "📦 Next month's box planned: *{box_name}*\\n{item_count} items, est. cost: ${estimated_cost}\\n\\n{items_list}",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("2", "4")],
    summary: `**Subscription Box Manager**\n\n1. Runs monthly on the 1st\n2. AI generates next box contents (4-6 items with descriptions)\n3. Preview emailed to customers\n4. #fulfillment notified on Slack\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  "Cart Abandonment SMS": {
    nodes: [
      trigger("Shopify: Checkout created", "webhook_app", {
        source: "Shopify",
        event: "checkouts/create",
        howItStarts: "A customer starts checkout but hasn't completed payment",
        setup:
          "In Shopify → Settings → Notifications → Webhooks → Create webhook → Event: 'Checkout creation' → URL: paste your workflow URL",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract cart details",
        280,
        "Read this Shopify checkout webhook and extract the customer email, customer phone, cart items, and cart total. Different Shopify stores send data in different formats — find the customer email, phone, cart items, and total regardless of field naming. Respond JSON: {customer_email: string, customer_phone: string, cart_items: string, cart_total: string}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_phone", as: "customer_phone", type: "string" },
          { field: "cart_items", as: "cart_items", type: "string" },
          { field: "cart_total", as: "cart_total", type: "string" },
        ],
      ),
      n("3", "delayAction", "Wait 30 minutes", 520, {
        delayAction: { time: 30, timeFormat: "minutes" },
      }),
      n("4", "condition", "Still not purchased?", 760, {
        conditionData: [
          {
            variable: "customer_email",
            value: "",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      n("5", "hitApi", "Send SMS reminder via Twilio", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.twilio.com/2010-04-01/Accounts/{twilio_sid}/Messages.json",
          headers: [
            { key: "Authorization", value: "Basic {twilio_auth}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "To", value: "{customer_phone}" },
            { key: "From", value: "{twilio_number}" },
            {
              key: "Body",
              value:
                "Hey! You left {cart_items} in your cart (${cart_total}). Complete your order now and get free shipping!",
            },
          ],
          variables: [
            { name: "sid", customName: "sms_sid", type: "string" },
            { name: "status", customName: "sms_status", type: "string" },
          ],
        },
      }),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5")],
    summary: `**Cart Abandonment SMS**\n\n1. Shopify checkout webhook fires\n2. **AI reads the data** and extracts customer email, phone, cart items, total\n3. Wait 30 minutes\n4. If still not purchased → sends SMS reminder via Twilio\n\n**To activate:** Say "connect openai" and "connect twilio"`,
  },

  "Customer Win-Back Campaign": {
    nodes: [
      trigger("Schedule: Every Monday at 10:00 AM", "schedule", {
        schedule: "Every Monday at 10:00 AM",
        howItStarts:
          "Weekly scan to identify churned customers and launch win-back campaigns",
        setup:
          "Activate this workflow. Configure your customer data source. Say 'connect openai' and 'connect email'",
        inputFields: [{ name: "customer_data", type: "string" }],
      }),
      aiNode(
        "2",
        "AI identify churned customers",
        280,
        "Analyze this customer data and identify users who have not been active in 30+ days. Respond JSON: {churned_list: string, churned_count: number, top_churned_email: string, top_churned_name: string}",
        "Customer data: {customer_data}",
        [
          { field: "churned_list", as: "churned_list", type: "string" },
          { field: "churned_count", as: "churned_count", type: "number" },
          {
            field: "top_churned_email",
            as: "top_churned_email",
            type: "string",
          },
          { field: "top_churned_name", as: "top_churned_name", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "AI write personalized win-back email",
        520,
        "Write a personalized win-back email for a churned customer. Be empathetic, highlight what they're missing, offer an incentive. Respond JSON: {email_subject: string, email_body_html: string, incentive_offered: string}",
        "Customer: {top_churned_name}, Email: {top_churned_email}, Churned list summary: {churned_list}",
        [
          { field: "email_subject", as: "email_subject", type: "string" },
          { field: "email_body_html", as: "email_body_html", type: "string" },
          {
            field: "incentive_offered",
            as: "incentive_offered",
            type: "string",
          },
        ],
      ),
      emailNode(
        "4",
        "Email each churned customer",
        760,
        "{top_churned_email}",
        "{email_subject}",
        "{email_body_html}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4")],
    summary: `**Customer Win-Back Campaign**\n\n1. Weekly scan for churned customers (30+ days inactive)\n2. AI identifies churned users\n3. AI writes personalized win-back emails with incentives\n4. Emails sent to churned customers\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "Product Review Aggregator": {
    nodes: [
      trigger("Schedule: Every day at 7:00 AM", "schedule", {
        schedule: "Every day at 7:00 AM",
        howItStarts: "Daily check for new product reviews across review sites",
        setup:
          "Activate this workflow. Configure your review API endpoint and say 'connect openai'",
        inputFields: [
          { name: "product_name", type: "string" },
          { name: "team_email", type: "string" },
        ],
      }),
      n("2", "hitApi", "Check review sites", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.reviews.io/product/review?store={store_id}&sku={product_sku}&order=latest",
          headers: [{ key: "apikey", value: "{reviews_api_key}" }],
          variables: [
            { name: "reviews", customName: "new_reviews", type: "array" },
            {
              name: "stats.average_rating",
              customName: "avg_rating",
              type: "number",
            },
          ],
        },
      }),
      aiNode(
        "3",
        "AI summarize new reviews",
        520,
        'Summarize these product reviews. Identify positive themes, negative themes, and any urgent issues. Respond JSON: {review_summary: string, positive_themes: string, negative_themes: string, has_negative: \\"true\\" | \\"false\\", urgent_issues: string, review_count: number}',
        "Product: {product_name}, Reviews: {new_reviews}, Average rating: {avg_rating}",
        [
          { field: "review_summary", as: "review_summary", type: "string" },
          { field: "positive_themes", as: "positive_themes", type: "string" },
          { field: "negative_themes", as: "negative_themes", type: "string" },
          { field: "has_negative", as: "has_negative", type: "string" },
          { field: "urgent_issues", as: "urgent_issues", type: "string" },
          { field: "review_count", as: "review_count", type: "number" },
        ],
      ),
      n("4", "condition", "Any negative reviews?", 760, {
        conditionData: [
          {
            variable: "has_negative",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "5",
        "Alert: negative reviews found",
        1000,
        "⚠️ Negative reviews for {product_name}!\\n{negative_themes}\\nUrgent: {urgent_issues}",
      ),
      emailNode(
        "6",
        "Email weekly digest",
        1000,
        "{team_email}",
        "Weekly Review Digest: {product_name}",
        "<h2>Review Summary ({review_count} new reviews)</h2><p>{review_summary}</p><h3>Positive</h3><p>{positive_themes}</p><h3>Negative</h3><p>{negative_themes}</p>",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("3", "6")],
    summary: `**Product Review Aggregator**\n\n1. Daily check of review sites\n2. AI summarizes new reviews (positive/negative themes)\n3. Negative reviews → Slack alert\n4. Weekly digest emailed to team\n\n**To activate:** Say "connect openai", "connect slack", "connect email"`,
  },

  "Order Fraud Detector": {
    nodes: [
      trigger("Shopify: New order placed", "webhook_app", {
        source: "Shopify",
        event: "orders/create",
        howItStarts: "A new order comes in from your Shopify store",
        setup:
          "In Shopify → Settings → Notifications → Webhooks → Create webhook → Event: 'Order creation' → URL: paste your workflow URL",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI analyze order for fraud",
        280,
        'Analyze this order for fraud signals. Check amount, location, velocity, and payment method. Respond JSON: {order_number: string, customer_email: string, order_amount: string, shipping_country: string, fraud_risk: \\"high\\" | \\"medium\\" | \\"low\\", fraud_signals: string, recommendation: string}',
        "{webhook_payload}",
        [
          { field: "order_number", as: "order_number", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "order_amount", as: "order_amount", type: "string" },
          { field: "shipping_country", as: "shipping_country", type: "string" },
          { field: "fraud_risk", as: "fraud_risk", type: "string" },
          { field: "fraud_signals", as: "fraud_signals", type: "string" },
          { field: "recommendation", as: "recommendation", type: "string" },
        ],
      ),
      n("3", "condition", "High fraud risk?", 520, {
        conditionData: [
          {
            variable: "fraud_risk",
            value: "high",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "4",
        "Alert #fraud-prevention",
        760,
        "🚨 High fraud risk! Order #{order_number}\\nAmount: ${order_amount} from {shipping_country}\\nCustomer: {customer_email}\\nSignals: {fraud_signals}\\nRecommendation: {recommendation}",
      ),
      emailNode(
        "5",
        "Email fraud team",
        760,
        "fraud@company.com",
        "FRAUD ALERT: Order #{order_number} — ${order_amount}",
        "<p><strong>High Fraud Risk Detected</strong></p><p>Order: #{order_number}</p><p>Amount: ${order_amount}</p><p>Customer: {customer_email}</p><p>Country: {shipping_country}</p><p>Fraud signals: {fraud_signals}</p><p>Recommendation: {recommendation}</p>",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5")],
    summary: `**Order Fraud Detector**\n\n1. New Shopify order webhook fires\n2. **AI reads the order** and analyzes for fraud (amount, location, velocity)\n3. High risk → Slack alert + email to fraud team\n\n**To activate:** Say "connect openai", "connect slack", "connect email"`,
  },

  "Training Completion Tracker": {
    nodes: [
      trigger("Webhook: LMS course event", "webhook_app", {
        source: "LMS (Teachable, Thinkific, etc.)",
        event: "course.completed",
        howItStarts:
          "Your LMS sends a notification when a learner completes a course",
        setup:
          "In your LMS → Integrations → Webhooks → Event: 'Course completed' → URL: paste your workflow URL. The AI will read whatever data your LMS sends and extract what it needs.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract training data",
        280,
        'Read this LMS webhook data and extract the learner name, learner email, course name, completion status, and score. Different LMS systems send data in different formats — find them regardless of field naming. Respond JSON: {learner_name: string, learner_email: string, course_name: string, completed: \\"true\\" | \\"false\\", score: number}',
        "{webhook_payload}",
        [
          { field: "learner_name", as: "learner_name", type: "string" },
          { field: "learner_email", as: "learner_email", type: "string" },
          { field: "course_name", as: "course_name", type: "string" },
          { field: "completed", as: "completed", type: "string" },
          { field: "score", as: "score", type: "number" },
        ],
      ),
      n("3", "condition", "Course completed?", 520, {
        conditionData: [
          {
            variable: "completed",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "4",
        "Email certificate",
        760,
        "{learner_email}",
        "Congratulations! You completed {course_name}",
        "<p>Hi {learner_name},</p><p>You have successfully completed <strong>{course_name}</strong> with a score of {score}%.</p><p>Your certificate of completion is attached.</p>",
      ),
      slackNode(
        "5",
        "Celebrate in #team",
        760,
        "🎉 {learner_name} just completed *{course_name}* with a score of {score}%! Congrats!",
        460,
      ),
      n("6", "hitApi", "Log to Google Sheets", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Training:append?valueInputOption=RAW",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{learner_name}","{learner_email}","{course_name}","{completed}","{score}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_added",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5"), e("4", "6")],
    summary: `**Training Completion Tracker**\n\n1. LMS sends webhook when learner completes a course\n2. **AI reads the data** and extracts learner name, email, course, completion status, score\n3. Completed → certificate email + Slack celebration + logged to Sheets\n\n**To activate:** Say "connect openai", "connect email", "connect slack", "connect google sheets"`,
  },

  "Internal Job Posting": {
    nodes: [
      trigger("Manual: Provide role details", "manual", {
        howItStarts: "You provide the role details in the chat",
        setup:
          "Type the role details: title, department, requirements, salary range. Or POST via API",
        inputFields: [
          { name: "role_title", type: "string" },
          { name: "department", type: "string" },
          { name: "requirements", type: "string" },
          { name: "salary_range", type: "string" },
          { name: "hr_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI write internal + external job post",
        280,
        "Write two versions of a job posting: one for internal employees (casual, highlight growth) and one for external candidates (professional, highlight company culture). Respond JSON: {internal_post: string, external_post: string, key_selling_points: string, suggested_channels: string}",
        "Role: {role_title}, Department: {department}, Requirements: {requirements}, Salary: {salary_range}",
        [
          { field: "internal_post", as: "internal_post", type: "string" },
          { field: "external_post", as: "external_post", type: "string" },
          {
            field: "key_selling_points",
            as: "key_selling_points",
            type: "string",
          },
          {
            field: "suggested_channels",
            as: "suggested_channels",
            type: "string",
          },
        ],
      ),
      emailNode(
        "3",
        "Email HR with both versions",
        520,
        "{hr_email}",
        "Job Posting Ready: {role_title}",
        "<h2>Internal Version</h2><p>{internal_post}</p><h2>External Version</h2><p>{external_post}</p><h3>Key Selling Points</h3><p>{key_selling_points}</p><h3>Suggested Channels</h3><p>{suggested_channels}</p>",
      ),
      slackNode(
        "4",
        "Post to #jobs",
        520,
        "💼 New opening: *{role_title}* ({department})\\n{key_selling_points}\\nSalary: {salary_range}\\n\\nRefer someone awesome!",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("2", "4")],
    summary: `**Internal Job Posting**\n\n1. Provide role details\n2. AI writes internal + external job posts\n3. Both versions emailed to HR\n4. Internal posting shared in #jobs on Slack\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  "Benefits Enrollment Reminder": {
    nodes: [
      trigger("Schedule: Yearly on November 1st", "schedule", {
        schedule: "Yearly on November 1st at 9:00 AM",
        howItStarts: "Kicks off annual benefits enrollment reminder campaign",
        setup:
          "Activate this workflow. Configure employee data source. Say 'connect openai' and 'connect email'",
        inputFields: [{ name: "employee_data", type: "string" }],
      }),
      aiNode(
        "2",
        "AI check enrollment status",
        280,
        "Analyze employee data and identify who has NOT enrolled in benefits for the upcoming year. Respond JSON: {not_enrolled_list: string, not_enrolled_count: number, top_not_enrolled_email: string, top_not_enrolled_name: string, enrollment_deadline: string}",
        "Employee data: {employee_data}",
        [
          {
            field: "not_enrolled_list",
            as: "not_enrolled_list",
            type: "string",
          },
          {
            field: "not_enrolled_count",
            as: "not_enrolled_count",
            type: "number",
          },
          {
            field: "top_not_enrolled_email",
            as: "top_not_enrolled_email",
            type: "string",
          },
          {
            field: "top_not_enrolled_name",
            as: "top_not_enrolled_name",
            type: "string",
          },
          {
            field: "enrollment_deadline",
            as: "enrollment_deadline",
            type: "string",
          },
        ],
      ),
      n("3", "condition", "Anyone not enrolled?", 520, {
        conditionData: [
          {
            variable: "not_enrolled_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "4",
        "Email reminder",
        760,
        "{top_not_enrolled_email}",
        "Action Required: Benefits Enrollment Deadline Approaching",
        "<p>Hi {top_not_enrolled_name},</p><p>Our records show you have not yet enrolled in benefits for the upcoming year.</p><p>Enrollment deadline: {enrollment_deadline}</p><p>Please complete your enrollment as soon as possible.</p>",
      ),
      n("5", "delayAction", "Wait 7 days", 1000, {
        delayAction: { time: 7, timeFormat: "days" },
      }),
      emailNode(
        "6",
        "Email final reminder",
        1240,
        "{top_not_enrolled_email}",
        "FINAL REMINDER: Benefits Enrollment Closes Soon",
        "<p>Hi {top_not_enrolled_name},</p><p>This is your final reminder. Benefits enrollment closes on {enrollment_deadline}.</p><p>If you do not enroll, you may lose coverage. Please act now.</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
    summary: `**Benefits Enrollment Reminder**\n\n1. Runs yearly during enrollment period\n2. AI checks who hasn't enrolled yet\n3. Not enrolled → reminder email\n4. 7-day wait → final reminder email\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "Recruitment Dashboard Reporter": {
    nodes: [
      trigger("Schedule: Every Monday at 9:00 AM", "schedule", {
        schedule: "Every Monday at 9:00 AM",
        howItStarts: "Compiles weekly hiring metrics every Monday",
        setup: "Activate this workflow. Configure your ATS data source.",
        inputFields: [
          { name: "hiring_data", type: "string" },
          { name: "hiring_managers_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI compile hiring metrics",
        280,
        "Compile a weekly hiring dashboard. Include open roles, candidates in pipeline, interviews scheduled, offers extended, and time-to-hire. Respond JSON: {dashboard_summary: string, open_roles: number, candidates_in_pipeline: number, interviews_this_week: number, offers_extended: number, avg_time_to_hire: number}",
        "Hiring data: {hiring_data}",
        [
          {
            field: "dashboard_summary",
            as: "dashboard_summary",
            type: "string",
          },
          { field: "open_roles", as: "open_roles", type: "number" },
          {
            field: "candidates_in_pipeline",
            as: "candidates_in_pipeline",
            type: "number",
          },
          {
            field: "interviews_this_week",
            as: "interviews_this_week",
            type: "number",
          },
          { field: "offers_extended", as: "offers_extended", type: "number" },
          { field: "avg_time_to_hire", as: "avg_time_to_hire", type: "number" },
        ],
      ),
      emailNode(
        "3",
        "Email hiring managers",
        520,
        "{hiring_managers_email}",
        "Weekly Hiring Dashboard",
        "<h2>Recruitment Dashboard</h2><p>{dashboard_summary}</p><p>Open roles: {open_roles} | Pipeline: {candidates_in_pipeline} | Interviews: {interviews_this_week} | Offers: {offers_extended} | Avg TTH: {avg_time_to_hire} days</p>",
      ),
      slackNode(
        "4",
        "Post to #recruiting",
        520,
        "📊 Weekly Hiring Report:\\nOpen: {open_roles} | Pipeline: {candidates_in_pipeline} | Interviews: {interviews_this_week} | Offers: {offers_extended} | TTH: {avg_time_to_hire}d",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("2", "4")],
    summary: `**Recruitment Dashboard Reporter**\n\n1. Runs every Monday at 9 AM\n2. AI compiles hiring metrics\n3. Dashboard emailed to hiring managers + posted to #recruiting\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  "Accounts Receivable Aging": {
    nodes: [
      trigger("Schedule: Every Monday at 8:00 AM", "schedule", {
        schedule: "Every Monday at 8:00 AM",
        howItStarts:
          "Weekly check of outstanding invoices and their aging buckets",
        setup:
          "Activate and say 'connect stripe' or configure your invoicing API",
        inputFields: [{ name: "finance_email", type: "string" }],
      }),
      n("2", "hitApi", "Get outstanding invoices", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.stripe.com/v1/invoices?status=open&limit=100",
          headers: [{ key: "Authorization", value: "Bearer {stripe_key}" }],
          variables: [
            { name: "data", customName: "open_invoices", type: "array" },
            { name: "has_more", customName: "has_more", type: "boolean" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI categorize by age",
        520,
        'Categorize these invoices by aging: 0-30, 31-60, 61-90, 90+ days. Respond JSON: {aging_summary: string, bucket_30: number, bucket_60: number, bucket_90: number, bucket_90_plus: number, has_90_plus: \\"true\\" | \\"false\\", total_outstanding: number}',
        "Invoices: {open_invoices}",
        [
          { field: "aging_summary", as: "aging_summary", type: "string" },
          { field: "bucket_30", as: "bucket_30", type: "number" },
          { field: "bucket_60", as: "bucket_60", type: "number" },
          { field: "bucket_90", as: "bucket_90", type: "number" },
          { field: "bucket_90_plus", as: "bucket_90_plus", type: "number" },
          { field: "has_90_plus", as: "has_90_plus", type: "string" },
          {
            field: "total_outstanding",
            as: "total_outstanding",
            type: "number",
          },
        ],
      ),
      emailNode(
        "4",
        "Email finance team",
        760,
        "{finance_email}",
        "AR Aging Report: ${total_outstanding} Outstanding",
        "<h2>Accounts Receivable Aging</h2><p>{aging_summary}</p><p>0-30: ${bucket_30} | 31-60: ${bucket_60} | 61-90: ${bucket_90} | 90+: ${bucket_90_plus}</p><p>Total: ${total_outstanding}</p>",
      ),
      n("5", "condition", "Any 90+ day invoices?", 1000, {
        conditionData: [
          {
            variable: "has_90_plus",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "6",
        "Urgent: 90+ day invoices!",
        1240,
        "🚨 URGENT: ${bucket_90_plus} in invoices over 90 days!\\n{aging_summary}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5"), e("5", "6")],
    summary: `**Accounts Receivable Aging**\n\n1. Weekly pull of outstanding invoices\n2. AI categorizes by age (30/60/90/90+ days)\n3. Report emailed to finance\n4. 90+ day invoices → urgent Slack alert\n\n**To activate:** Say "connect stripe", "connect openai", "connect email", "connect slack"`,
  },

  "Multi-Currency Logger": {
    nodes: [
      trigger("Webhook: Payment received", "webhook_app", {
        source: "Stripe / PayPal",
        event: "payment.received",
        howItStarts: "A payment is received in any currency",
        setup:
          "In your payment processor → Webhooks → Events: payment received → URL: paste your workflow URL",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract + convert currency",
        280,
        "Read this payment webhook data and extract the payment amount, original currency, customer email, and payment ID. Convert the amount to USD. Respond JSON: {payment_amount: number, original_currency: string, usd_amount: number, customer_email: string, payment_id: string, exchange_rate: number}",
        "{webhook_payload}",
        [
          { field: "payment_amount", as: "payment_amount", type: "number" },
          {
            field: "original_currency",
            as: "original_currency",
            type: "string",
          },
          { field: "usd_amount", as: "usd_amount", type: "number" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "payment_id", as: "payment_id", type: "string" },
          { field: "exchange_rate", as: "exchange_rate", type: "number" },
        ],
      ),
      n("3", "hitApi", "Log to Google Sheets", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Payments:append?valueInputOption=RAW",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{payment_id}","{customer_email}","{payment_amount}","{original_currency}","{usd_amount}","{exchange_rate}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_added",
              type: "number",
            },
          ],
        },
      }),
      emailNode(
        "4",
        "Email daily summary",
        520,
        "finance@company.com",
        "Payment: {payment_amount} {original_currency} (${usd_amount} USD)",
        "<p>Payment ID: {payment_id}</p><p>Customer: {customer_email}</p><p>Amount: {payment_amount} {original_currency} = ${usd_amount} USD (rate: {exchange_rate})</p>",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("2", "4")],
    summary: `**Multi-Currency Logger**\n\n1. Payment webhook fires\n2. **AI reads the data**, extracts amount/currency, converts to USD\n3. Logged to Google Sheets\n4. Finance team emailed\n\n**To activate:** Say "connect openai", "connect google sheets", "connect email"`,
  },

  "Compliance Document Checker": {
    nodes: [
      trigger("Form: Document uploaded", "webhook_form", {
        source: "Internal Form / Outgrow Forms",
        howItStarts: "Someone uploads a document for compliance review",
        setup:
          "Create a document submission form → Webhooks → paste your workflow URL.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI analyze document for compliance",
        280,
        'Read this form submission and extract the document text, submitter name, submitter email, and document type. Then analyze for compliance issues. Respond JSON: {document_text: string, submitter_name: string, submitter_email: string, document_type: string, issues_found: \\"true\\" | \\"false\\", issue_list: string, issue_count: number, compliance_score: number}',
        "{webhook_payload}",
        [
          { field: "document_text", as: "document_text", type: "string" },
          { field: "submitter_name", as: "submitter_name", type: "string" },
          { field: "submitter_email", as: "submitter_email", type: "string" },
          { field: "document_type", as: "document_type", type: "string" },
          { field: "issues_found", as: "issues_found", type: "string" },
          { field: "issue_list", as: "issue_list", type: "string" },
          { field: "issue_count", as: "issue_count", type: "number" },
          { field: "compliance_score", as: "compliance_score", type: "number" },
        ],
      ),
      n("3", "condition", "Issues found?", 520, {
        conditionData: [
          {
            variable: "issues_found",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "4",
        "Email compliance team",
        760,
        "compliance@company.com",
        "Compliance Issues: {document_type} from {submitter_name}",
        "<p><strong>{issue_count} issues found</strong> (Score: {compliance_score}/100)</p><p>Submitted by: {submitter_name} ({submitter_email})</p><h3>Issues</h3><p>{issue_list}</p>",
      ),
      slackNode(
        "5",
        "Alert #compliance",
        760,
        "⚠️ Compliance issues in {document_type} from {submitter_name}\\n{issue_count} issues (score: {compliance_score}/100)\\n{issue_list}",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5")],
    summary: `**Compliance Document Checker**\n\n1. Document submitted via form\n2. **AI reads the data** and analyzes for compliance issues\n3. Issues found → email compliance team + Slack alert\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  "Meeting Minutes to Tasks": {
    nodes: [
      trigger("Manual: Provide meeting notes", "manual", {
        howItStarts: "You paste meeting notes in the chat",
        setup: "Paste meeting notes with attendee emails. Or POST via API",
        inputFields: [
          { name: "meeting_notes", type: "string" },
          { name: "attendees_emails", type: "string" },
          { name: "slack_channel", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI extract action items",
        280,
        "Extract action items from these meeting notes. For each, identify the owner, deadline, and priority. Respond JSON: {meeting_summary: string, task_list: string, task_count: number, high_priority_count: number}",
        "Meeting notes: {meeting_notes}",
        [
          { field: "meeting_summary", as: "meeting_summary", type: "string" },
          { field: "task_list", as: "task_list", type: "string" },
          { field: "task_count", as: "task_count", type: "number" },
          {
            field: "high_priority_count",
            as: "high_priority_count",
            type: "number",
          },
        ],
      ),
      emailNode(
        "3",
        "Email attendees",
        520,
        "{attendees_emails}",
        "Meeting Summary & Action Items ({task_count} tasks)",
        "<h2>Meeting Summary</h2><p>{meeting_summary}</p><h3>Action Items ({task_count})</h3><p>{task_list}</p>",
      ),
      slackNode(
        "4",
        "Post task list to channel",
        520,
        "📋 *Meeting Summary*\\n{meeting_summary}\\n\\n✅ *{task_count} Action Items* ({high_priority_count} high priority):\\n{task_list}",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("2", "4")],
    summary: `**Meeting Minutes to Tasks**\n\n1. Paste meeting notes\n2. AI extracts action items with owners, deadlines, priorities\n3. Summary emailed to attendees + task list posted to Slack\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  "Content Calendar Planner": {
    nodes: [
      trigger("Manual: Provide topic and month", "manual", {
        howItStarts:
          "You provide a topic and target month for content planning",
        setup: "Type the topic and month in chat or POST via API",
        inputFields: [
          { name: "topic", type: "string" },
          { name: "target_month", type: "string" },
          { name: "content_team_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI generate 30 days of content ideas",
        280,
        "Generate a 30-day content calendar. Include blog posts, social media, videos, and newsletters. Respond JSON: {calendar_html: string, total_pieces: number, blog_count: number, social_count: number, video_count: number, top_themes: string}",
        "Topic: {topic}, Month: {target_month}",
        [
          { field: "calendar_html", as: "calendar_html", type: "string" },
          { field: "total_pieces", as: "total_pieces", type: "number" },
          { field: "blog_count", as: "blog_count", type: "number" },
          { field: "social_count", as: "social_count", type: "number" },
          { field: "video_count", as: "video_count", type: "number" },
          { field: "top_themes", as: "top_themes", type: "string" },
        ],
      ),
      emailNode(
        "3",
        "Email content team",
        520,
        "{content_team_email}",
        "Content Calendar: {topic} — {target_month}",
        "<h2>30-Day Content Calendar</h2><p>{total_pieces} pieces: {blog_count} blogs, {social_count} social, {video_count} videos</p><h3>Themes</h3><p>{top_themes}</p>{calendar_html}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3")],
    summary: `**Content Calendar Planner**\n\n1. Provide a topic and target month\n2. AI generates 30 days of content ideas\n3. Full calendar emailed to content team\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "PR Crisis Monitor": {
    nodes: [
      trigger("Schedule: Every hour", "schedule", {
        schedule: "Every hour",
        howItStarts:
          "Scans news and social media for negative brand mentions every hour",
        setup:
          "Activate this workflow. Say 'connect serpapi' and 'connect openai'",
        inputFields: [
          { name: "brand_name", type: "string" },
          { name: "pr_team_email", type: "string" },
        ],
      }),
      n("2", "hitApi", "Search news and social", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search?q={brand_name}&tbm=nws&tbs=qdr:h&num=10",
          headers: [],
          params: [{ key: "api_key", value: "{serpapi_key}" }],
          variables: [
            {
              name: "news_results",
              customName: "news_mentions",
              type: "array",
            },
          ],
        },
      }),
      aiNode(
        "3",
        "AI detect negative mentions",
        520,
        'Analyze these news mentions for negative sentiment. Assess if this is a crisis. Respond JSON: {negative_count: number, crisis_level: \\"none\\" | \\"low\\" | \\"medium\\" | \\"high\\", crisis_summary: string, recommended_response: string, is_crisis: \\"true\\" | \\"false\\"}',
        "Brand: {brand_name}, News mentions: {news_mentions}",
        [
          { field: "negative_count", as: "negative_count", type: "number" },
          { field: "crisis_level", as: "crisis_level", type: "string" },
          { field: "crisis_summary", as: "crisis_summary", type: "string" },
          {
            field: "recommended_response",
            as: "recommended_response",
            type: "string",
          },
          { field: "is_crisis", as: "is_crisis", type: "string" },
        ],
      ),
      n("4", "condition", "Crisis detected?", 760, {
        conditionData: [
          {
            variable: "is_crisis",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "5",
        "Urgent: #comms alert",
        1000,
        "🚨 *PR CRISIS DETECTED* for {brand_name}!\\nLevel: {crisis_level}\\n{crisis_summary}\\nRecommended response: {recommended_response}",
      ),
      emailNode(
        "6",
        "Email PR team",
        1000,
        "{pr_team_email}",
        "URGENT: PR Crisis — {brand_name}",
        "<h2>Crisis Alert: {crisis_level}</h2><p>{crisis_summary}</p><p><strong>Recommended:</strong> {recommended_response}</p><p>{negative_count} negative mentions in the last hour.</p>",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("4", "6")],
    summary: `**PR Crisis Monitor**\n\n1. Scans news hourly for brand mentions\n2. AI analyzes sentiment and detects crisis level\n3. Crisis detected → urgent Slack alert + email to PR team\n\n**To activate:** Say "connect serpapi", "connect openai", "connect slack", "connect email"`,
  },

  "Supplier Price Comparison": {
    nodes: [
      trigger("Manual: Provide product to compare", "manual", {
        howItStarts:
          "You provide a product name to compare prices across suppliers",
        setup: "Type the product name and details in chat or POST via API",
        inputFields: [
          { name: "product_name", type: "string" },
          { name: "product_specs", type: "string" },
          { name: "procurement_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI compare prices across suppliers",
        280,
        "Compare prices for this product across common suppliers. Consider price, shipping, lead time, and MOQ. Respond JSON: {comparison_table: string, best_value_supplier: string, best_price: string, fastest_delivery: string, recommendation: string}",
        "Product: {product_name}, Specs: {product_specs}",
        [
          { field: "comparison_table", as: "comparison_table", type: "string" },
          {
            field: "best_value_supplier",
            as: "best_value_supplier",
            type: "string",
          },
          { field: "best_price", as: "best_price", type: "string" },
          { field: "fastest_delivery", as: "fastest_delivery", type: "string" },
          { field: "recommendation", as: "recommendation", type: "string" },
        ],
      ),
      emailNode(
        "3",
        "Email procurement",
        520,
        "{procurement_email}",
        "Price Comparison: {product_name}",
        "<h2>Supplier Price Comparison</h2>{comparison_table}<h3>Recommendation</h3><p>{recommendation}</p><p>Best value: {best_value_supplier} at {best_price}</p><p>Fastest: {fastest_delivery}</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3")],
    summary: `**Supplier Price Comparison**\n\n1. Provide a product to compare\n2. AI compares prices across suppliers\n3. Recommendation emailed to procurement\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "Office Supply Auto-Reorder": {
    nodes: [
      trigger("Schedule: Every Monday at 8:00 AM", "schedule", {
        schedule: "Every Monday at 8:00 AM",
        howItStarts: "Weekly check of office supply levels",
        setup: "Activate this workflow. Configure your inventory data source.",
        inputFields: [
          { name: "supply_data", type: "string" },
          { name: "supplier_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI check supply levels",
        280,
        'Check these office supply levels and identify low or out-of-stock items. Create a reorder list. Respond JSON: {low_items: string, low_count: number, reorder_list: string, estimated_cost: number, any_low: \\"true\\" | \\"false\\"}',
        "Supply inventory: {supply_data}",
        [
          { field: "low_items", as: "low_items", type: "string" },
          { field: "low_count", as: "low_count", type: "number" },
          { field: "reorder_list", as: "reorder_list", type: "string" },
          { field: "estimated_cost", as: "estimated_cost", type: "number" },
          { field: "any_low", as: "any_low", type: "string" },
        ],
      ),
      n("3", "condition", "Any supplies low?", 520, {
        conditionData: [
          {
            variable: "any_low",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "4",
        "Email supplier with order",
        760,
        "{supplier_email}",
        "Office Supply Reorder: {low_count} items",
        "<h2>Reorder Request</h2><p>{low_count} items need restocking.</p><p>{reorder_list}</p><p>Estimated cost: ${estimated_cost}</p>",
      ),
      slackNode(
        "5",
        "Notify #office",
        760,
        "📎 Office supplies reorder placed!\\n{low_count} items low: {low_items}\\nCost: ${estimated_cost}",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5")],
    summary: `**Office Supply Auto-Reorder**\n\n1. Weekly check of office supply levels\n2. AI identifies low items and creates reorder list\n3. Low supplies → email supplier + notify #office on Slack\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  "Customer Health Score": {
    nodes: [
      trigger("Schedule: Every Monday at 9:00 AM", "schedule", {
        schedule: "Every Monday at 9:00 AM",
        howItStarts: "Weekly analysis of customer usage and engagement data",
        setup:
          "Activate this workflow. Configure your customer analytics data source.",
        inputFields: [
          { name: "customer_data", type: "string" },
          { name: "account_manager_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI analyze usage/engagement data",
        280,
        'Analyze customer usage and engagement. Calculate health scores (0-100). Respond JSON: {health_summary: string, at_risk_customers: string, at_risk_count: number, avg_health_score: number, lowest_score: number, lowest_customer: string, lowest_customer_email: string, has_at_risk: \\"true\\" | \\"false\\"}',
        "Customer data: {customer_data}",
        [
          { field: "health_summary", as: "health_summary", type: "string" },
          {
            field: "at_risk_customers",
            as: "at_risk_customers",
            type: "string",
          },
          { field: "at_risk_count", as: "at_risk_count", type: "number" },
          { field: "avg_health_score", as: "avg_health_score", type: "number" },
          { field: "lowest_score", as: "lowest_score", type: "number" },
          { field: "lowest_customer", as: "lowest_customer", type: "string" },
          {
            field: "lowest_customer_email",
            as: "lowest_customer_email",
            type: "string",
          },
          { field: "has_at_risk", as: "has_at_risk", type: "string" },
        ],
      ),
      n("3", "condition", "Any at-risk? (score < 30)", 520, {
        conditionData: [
          {
            variable: "has_at_risk",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "4",
        "Alert CS team",
        760,
        "⚠️ {at_risk_count} at-risk customers (score < 30)!\\nLowest: {lowest_customer} ({lowest_score})\\n{at_risk_customers}",
      ),
      emailNode(
        "5",
        "Email account manager",
        760,
        "{account_manager_email}",
        "At-Risk Customers: {at_risk_count} need attention",
        "<h2>Customer Health Alert</h2><p>{at_risk_count} customers below 30.</p><p>Lowest: {lowest_customer} ({lowest_score})</p><p>{at_risk_customers}</p><p>{health_summary}</p>",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5")],
    summary: `**Customer Health Score**\n\n1. Weekly analysis of customer usage/engagement\n2. AI calculates health scores (0-100)\n3. Score < 30 → Slack alert + email to account manager\n\n**To activate:** Say "connect openai", "connect slack", "connect email"`,
  },

  "Automated Standup Collector": {
    nodes: [
      trigger("Schedule: Every weekday at 9:00 AM", "schedule", {
        schedule: "Every weekday at 9:00 AM",
        howItStarts:
          "Sends standup prompts to team every morning and compiles responses",
        setup: "Activate this workflow. Configure team and manager emails.",
        inputFields: [
          { name: "team_emails", type: "string" },
          { name: "manager_email", type: "string" },
        ],
      }),
      emailNode(
        "2",
        "Email team standup prompt",
        280,
        "{team_emails}",
        "Daily Standup — What's your update?",
        "<p>Reply with your standup:</p><ul><li>What did you do yesterday?</li><li>What's next?</li><li>Any blockers?</li></ul>",
      ),
      n("3", "delayAction", "Wait 2 hours for responses", 520, {
        delayAction: { time: 2, timeFormat: "hours" },
      }),
      aiNode(
        "4",
        "AI compile responses",
        760,
        "Compile standup responses into a team digest. Group by person, highlight blockers. Respond JSON: {standup_digest: string, response_count: number, blockers_found: string, blocker_count: number}",
        "Team emails: {team_emails}, Responses collected over 2 hours",
        [
          { field: "standup_digest", as: "standup_digest", type: "string" },
          { field: "response_count", as: "response_count", type: "number" },
          { field: "blockers_found", as: "blockers_found", type: "string" },
          { field: "blocker_count", as: "blocker_count", type: "number" },
        ],
      ),
      emailNode(
        "5",
        "Email manager digest",
        1000,
        "{manager_email}",
        "Daily Standup Digest ({response_count} responses)",
        "<h2>Team Standup</h2><p>{standup_digest}</p><h3>Blockers ({blocker_count})</h3><p>{blockers_found}</p>",
      ),
      slackNode(
        "6",
        "Post to #standup",
        1000,
        "📋 *Daily Standup* ({response_count} responses)\\n{standup_digest}\\n\\nBlockers: {blockers_found}",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("4", "6")],
    summary: `**Automated Standup Collector**\n\n1. Daily at 9 AM → emails team standup prompt\n2. Waits 2 hours for responses\n3. AI compiles into digest, highlights blockers\n4. Digest emailed to manager + posted to Slack\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  "Document Version Controller": {
    nodes: [
      trigger("Webhook: File changed", "webhook_app", {
        source: "Google Drive / Dropbox / SharePoint",
        event: "file.modified",
        howItStarts: "A file is modified in your cloud storage",
        setup:
          "In your cloud storage → Webhooks → Event: 'File modified' → URL: paste your workflow URL.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract file info + diff summary",
        280,
        "Read this file change webhook and extract the file name, path, modifier, date, and change summary. Respond JSON: {file_name: string, file_path: string, modified_by: string, mod_date: string, change_summary: string, change_type: string}",
        "{webhook_payload}",
        [
          { field: "file_name", as: "file_name", type: "string" },
          { field: "file_path", as: "file_path", type: "string" },
          { field: "modified_by", as: "modified_by", type: "string" },
          { field: "mod_date", as: "mod_date", type: "string" },
          { field: "change_summary", as: "change_summary", type: "string" },
          { field: "change_type", as: "change_type", type: "string" },
        ],
      ),
      slackNode(
        "3",
        "Notify team",
        520,
        "📄 File updated: *{file_name}*\\nBy: {modified_by} on {mod_date}\\nChange: {change_summary}",
      ),
      n(
        "4",
        "hitApi",
        "Log to Google Sheets",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/VersionLog:append?valueInputOption=RAW",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "values",
                value:
                  '[["{mod_date}","{file_name}","{modified_by}","{change_type}","{change_summary}"]]',
              },
            ],
            variables: [
              {
                name: "updates.updatedRows",
                customName: "rows_added",
                type: "number",
              },
            ],
          },
        },
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("2", "4")],
    summary: `**Document Version Controller**\n\n1. File modified in cloud storage\n2. **AI reads the data** and extracts file info + change summary\n3. Team notified on Slack + logged to Sheets\n\n**To activate:** Say "connect openai", "connect slack", "connect google sheets"`,
  },

  "API Rate Limit Monitor": {
    nodes: [
      trigger("Schedule: Every 5 minutes", "schedule", {
        schedule: "Every 5 minutes",
        howItStarts: "Checks your API usage stats every 5 minutes",
        setup: "Activate and configure your API endpoint to check usage stats",
        inputFields: [{ name: "api_name", type: "string" }],
      }),
      n("2", "hitApi", "Check API usage stats", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.your-service.com/v1/usage/current",
          headers: [{ key: "Authorization", value: "Bearer {api_key}" }],
          variables: [
            { name: "rate_limit", customName: "rate_limit", type: "number" },
            { name: "remaining", customName: "remaining", type: "number" },
            { name: "reset_at", customName: "reset_at", type: "string" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI assess remaining quota",
        520,
        'Assess this API rate limit status. Calculate percentage remaining. Respond JSON: {pct_remaining: number, is_critical: \\"true\\" | \\"false\\", usage_summary: string, recommendation: string}',
        "API: {api_name}, Rate limit: {rate_limit}, Remaining: {remaining}, Resets: {reset_at}",
        [
          { field: "pct_remaining", as: "pct_remaining", type: "number" },
          { field: "is_critical", as: "is_critical", type: "string" },
          { field: "usage_summary", as: "usage_summary", type: "string" },
          { field: "recommendation", as: "recommendation", type: "string" },
        ],
      ),
      n("4", "condition", "Below 20% remaining?", 760, {
        conditionData: [
          {
            variable: "pct_remaining",
            value: "20",
            comparison_operator: "LESS THAN",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "5",
        "Warn devs",
        1000,
        "⚠️ API Rate Limit: {api_name}\\n{usage_summary}\\n{pct_remaining}% remaining (resets: {reset_at})\\n{recommendation}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5")],
    summary: `**API Rate Limit Monitor**\n\n1. Checks API usage every 5 minutes\n2. AI assesses remaining quota\n3. Below 20% → warns devs on Slack\n\n**To activate:** Say "connect openai" and "connect slack"`,
  },

  "Social Proof Aggregator": {
    nodes: [
      trigger("Schedule: Every day at 8:00 AM", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts: "Daily scan of review sites for the best testimonials",
        setup: "Activate this workflow. Configure your review API.",
        inputFields: [
          { name: "product_name", type: "string" },
          { name: "marketing_email", type: "string" },
        ],
      }),
      n("2", "hitApi", "Check review sites", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.reviews.io/product/review?store={store_id}&sku={product_sku}&order=latest&per_page=20",
          headers: [{ key: "apikey", value: "{reviews_api_key}" }],
          variables: [
            { name: "reviews", customName: "recent_reviews", type: "array" },
            {
              name: "stats.average_rating",
              customName: "avg_rating",
              type: "number",
            },
          ],
        },
      }),
      aiNode(
        "3",
        "AI extract best testimonials",
        520,
        "Select the 5 best testimonials for marketing. Prioritize specific results and emotional impact. Respond JSON: {curated_list: string, testimonial_count: number, best_quote: string, avg_sentiment: number}",
        "Product: {product_name}, Reviews: {recent_reviews}, Avg rating: {avg_rating}",
        [
          { field: "curated_list", as: "curated_list", type: "string" },
          {
            field: "testimonial_count",
            as: "testimonial_count",
            type: "number",
          },
          { field: "best_quote", as: "best_quote", type: "string" },
          { field: "avg_sentiment", as: "avg_sentiment", type: "number" },
        ],
      ),
      emailNode(
        "4",
        "Email marketing",
        760,
        "{marketing_email}",
        "Today's Best Testimonials: {product_name}",
        "<h2>Curated Social Proof</h2><p>{curated_list}</p><h3>Best Quote</h3><blockquote>{best_quote}</blockquote><p>Sentiment: {avg_sentiment}/100</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4")],
    summary: `**Social Proof Aggregator**\n\n1. Daily scan of review sites\n2. AI curates the 5 best testimonials\n3. Curated list emailed to marketing\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "Competitive Pricing Alert": {
    nodes: [
      trigger("Schedule: Every day at 6:00 AM", "schedule", {
        schedule: "Every day at 6:00 AM",
        howItStarts: "Daily scrape of competitor pricing pages",
        setup: "Activate and configure competitor URLs.",
        inputFields: [
          { name: "competitor_url", type: "string" },
          { name: "our_product", type: "string" },
          { name: "our_price", type: "string" },
          { name: "pricing_team_email", type: "string" },
        ],
      }),
      n("2", "hitApi", "Scrape competitor prices", 280, {
        hitApiDetails: {
          method: "GET",
          url: 'https://api.scrapingbee.com/v1/?api_key={scrapingbee_key}&url={competitor_url}&extract_rules={"prices":{"selector":".price","type":"list"}}',
          headers: [],
          variables: [
            { name: "prices", customName: "competitor_prices", type: "string" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI compare prices",
        520,
        'Compare competitor prices against ours. Respond JSON: {cheapest_competitor_price: string, price_gap: string, competitor_cheaper: \\"true\\" | \\"false\\", analysis: string, recommendation: string}',
        "Our product: {our_product}, Our price: {our_price}, Competitor prices: {competitor_prices}",
        [
          {
            field: "cheapest_competitor_price",
            as: "cheapest_competitor_price",
            type: "string",
          },
          { field: "price_gap", as: "price_gap", type: "string" },
          {
            field: "competitor_cheaper",
            as: "competitor_cheaper",
            type: "string",
          },
          { field: "analysis", as: "analysis", type: "string" },
          { field: "recommendation", as: "recommendation", type: "string" },
        ],
      ),
      n("4", "condition", "Competitor cheaper?", 760, {
        conditionData: [
          {
            variable: "competitor_cheaper",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "5",
        "Email pricing team",
        1000,
        "{pricing_team_email}",
        "Pricing Alert: Competitor undercuts {our_product}",
        "<p>Our price: {our_price} | Competitor: {cheapest_competitor_price} (gap: {price_gap})</p><p>{analysis}</p><p>Recommendation: {recommendation}</p>",
      ),
      slackNode(
        "6",
        "Alert #pricing",
        1000,
        "💲 Pricing alert: {our_product}\\nCompetitor: {cheapest_competitor_price} vs ours: {our_price}\\nGap: {price_gap}\\n{recommendation}",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("4", "6")],
    summary: `**Competitive Pricing Alert**\n\n1. Daily scrape of competitor pricing\n2. AI compares to your price\n3. Competitor cheaper → email pricing team + Slack alert\n\n**To activate:** Say "connect scrapingbee", "connect openai", "connect email", "connect slack"`,
  },

  "Data Privacy Request Handler": {
    nodes: [
      trigger("Email: GDPR/CCPA request received", "email", {
        howItStarts:
          "A customer sends a data privacy request (GDPR, CCPA, etc.)",
        setup:
          "Forward privacy-related emails to your workflow inbox or set up auto-forwarding rules",
        inputFields: [
          { name: "sender_email", type: "string" },
          { name: "email_subject", type: "string" },
          { name: "email_body", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI classify request type",
        280,
        'Classify this data privacy request. Determine type, regulation, and urgency. Respond JSON: {request_type: string, regulation: string, customer_name: string, is_deletion: \\"true\\" | \\"false\\", urgency: string, classification_summary: string}',
        "From: {sender_email}\\nSubject: {email_subject}\\nBody: {email_body}",
        [
          { field: "request_type", as: "request_type", type: "string" },
          { field: "regulation", as: "regulation", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "is_deletion", as: "is_deletion", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          {
            field: "classification_summary",
            as: "classification_summary",
            type: "string",
          },
        ],
      ),
      n("3", "condition", "Deletion request?", 520, {
        conditionData: [
          {
            variable: "is_deletion",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "4",
        "Email data team",
        760,
        "data-team@company.com",
        "Data Deletion Request: {customer_name} ({regulation})",
        "<p><strong>Deletion Request</strong></p><p>Customer: {customer_name} ({sender_email})</p><p>Regulation: {regulation} | Urgency: {urgency}</p><p>{classification_summary}</p>",
      ),
      slackNode(
        "5",
        "Alert #compliance",
        760,
        "🔒 Data deletion request from {customer_name}\\nRegulation: {regulation} | Urgency: {urgency}\\n{classification_summary}",
        460,
      ),
      emailNode(
        "6",
        "Acknowledge to customer",
        1000,
        "{sender_email}",
        "We received your data privacy request",
        "<p>Hi {customer_name},</p><p>We received your {request_type} request under {regulation} and are processing it.</p><p>Reference: {email_subject}</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5"), e("2", "6")],
    summary: `**Data Privacy Request Handler**\n\n1. Privacy request email arrives\n2. AI classifies request type, regulation, urgency\n3. Deletion → email data team + Slack #compliance\n4. Customer gets acknowledgment\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  "Workflow Performance Optimizer": {
    nodes: [
      trigger("Schedule: Every Monday at 8:00 AM", "schedule", {
        schedule: "Every Monday at 8:00 AM",
        howItStarts:
          "Weekly analysis of workflow execution data to find optimization opportunities",
        setup: "Activate this workflow. Configure your analytics data source.",
        inputFields: [
          { name: "workflow_data", type: "string" },
          { name: "admin_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI analyze workflow execution data",
        280,
        "Analyze workflow execution data. Identify slow/failing workflows and optimization opportunities. Respond JSON: {total_workflows: number, slow_workflows: string, failing_workflows: string, optimization_recommendations: string, top_issue: string, avg_execution_time: number, success_rate: number}",
        "Workflow data: {workflow_data}",
        [
          { field: "total_workflows", as: "total_workflows", type: "number" },
          { field: "slow_workflows", as: "slow_workflows", type: "string" },
          {
            field: "failing_workflows",
            as: "failing_workflows",
            type: "string",
          },
          {
            field: "optimization_recommendations",
            as: "optimization_recommendations",
            type: "string",
          },
          { field: "top_issue", as: "top_issue", type: "string" },
          {
            field: "avg_execution_time",
            as: "avg_execution_time",
            type: "number",
          },
          { field: "success_rate", as: "success_rate", type: "number" },
        ],
      ),
      emailNode(
        "3",
        "Email recommendations",
        520,
        "{admin_email}",
        "Weekly Workflow Performance Report",
        "<h2>Performance Summary</h2><p>Total: {total_workflows} | Avg: {avg_execution_time}s | Success: {success_rate}%</p><h3>Slow</h3><p>{slow_workflows}</p><h3>Failing</h3><p>{failing_workflows}</p><h3>Recommendations</h3><p>{optimization_recommendations}</p><p><strong>Top issue:</strong> {top_issue}</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3")],
    summary: `**Workflow Performance Optimizer**\n\n1. Weekly analysis of all workflow execution data\n2. AI identifies slow/failing workflows and suggests optimizations\n3. Recommendations emailed to admin\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  // ===================================================================
  // DEVOPS & ENGINEERING (Batch 3)
  // ===================================================================

  "GitHub Issue to Jira": {
    nodes: [
      trigger("GitHub: New issue created", "webhook_app", {
        source: "GitHub",
        event: "issues.opened",
        howItStarts: "Someone opens a new issue in your GitHub repository",
        setup:
          "In GitHub → repo Settings → Webhooks → Add webhook → URL: your workflow URL → Events: 'Issues'. The AI will read whatever data GitHub sends and extract what it needs.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the GitHub issue",
        280,
        "Read this GitHub issue webhook data and extract the issue title, issue body, author username, labels, and repository name. Different GitHub webhook formats vary — find the issue title, issue body, author, labels, and repo name regardless of field naming. Respond JSON: {issue_title: string, issue_body: string, issue_author: string, issue_labels: string, repo_name: string}",
        "{webhook_payload}",
        [
          { field: "issue_title", as: "issue_title", type: "string" },
          { field: "issue_body", as: "issue_body", type: "string" },
          { field: "issue_author", as: "issue_author", type: "string" },
          { field: "issue_labels", as: "issue_labels", type: "string" },
          { field: "repo_name", as: "repo_name", type: "string" },
        ],
      ),
      n("3", "hitApi", "Create Jira ticket", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/issue",
          headers: [
            { key: "Authorization", value: "Basic {jira_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "fields",
              value:
                '{"project":{"key":"{jira_project_key}"},"summary":"{issue_title}","description":{"type":"doc","version":1,"content":[{"type":"paragraph","content":[{"type":"text","text":"{issue_body}"}]}]},"issuetype":{"name":"Task"}}',
            },
          ],
          variables: [
            { name: "key", customName: "jira_ticket_key", type: "string" },
            { name: "id", customName: "jira_ticket_id", type: "string" },
          ],
        },
      }),
      slackNode(
        "4",
        "Notify #engineering",
        760,
        "🎫 GitHub issue → Jira: {issue_title}\\nBy: {issue_author} | Labels: {issue_labels}\\nJira: {jira_ticket_key} | Repo: {repo_name}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4")],
    summary: `**GitHub Issue to Jira**\n\n1. GitHub issue created via webhook\n2. **AI reads the data** and extracts issue title, body, author, labels, repo\n3. Creates a Jira ticket automatically\n4. Notifies #engineering on Slack\n\n**To activate:** Say "connect openai", "connect jira", "connect slack"`,
  },

  "Security Alert Response": {
    nodes: [
      trigger("Webhook: Security monitoring alert", "webhook_app", {
        source: "Snyk / Dependabot / SonarQube",
        howItStarts:
          "Your security monitoring tool detects a vulnerability or threat",
        setup:
          "In your security tool (Snyk, Dependabot, SonarQube) → Integrations → Webhooks → paste your workflow URL. The AI will read whatever data your tool sends and extract what it needs.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract + classify severity",
        280,
        'Read this security alert webhook data and extract the alert title, description, affected component, and severity. Then classify the severity as critical, high, medium, or low. Respond JSON: {alert_title: string, alert_description: string, affected_component: string, severity: string, is_critical: \\"true\\" | \\"false\\", recommended_action: string}',
        "{webhook_payload}",
        [
          { field: "alert_title", as: "alert_title", type: "string" },
          {
            field: "alert_description",
            as: "alert_description",
            type: "string",
          },
          {
            field: "affected_component",
            as: "affected_component",
            type: "string",
          },
          { field: "severity", as: "severity", type: "string" },
          { field: "is_critical", as: "is_critical", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
        ],
      ),
      n("3", "condition", "Is it critical?", 520, {
        conditionData: [
          {
            variable: "is_critical",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "4",
        "Alert #security (critical)",
        760,
        "🚨 *CRITICAL SECURITY ALERT*\\n{alert_title}\\nComponent: {affected_component}\\nSeverity: {severity}\\n{alert_description}\\nAction: {recommended_action}",
      ),
      emailNode(
        "5",
        "Email security team",
        760,
        "security@company.com",
        "CRITICAL: {alert_title} — {affected_component}",
        "<p><strong>Critical Security Alert</strong></p><p>{alert_description}</p><p>Component: {affected_component}</p><p>Recommended action: {recommended_action}</p>",
        460,
      ),
      n("6", "hitApi", "Create incident ticket", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/issue",
          headers: [
            { key: "Authorization", value: "Basic {jira_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "fields",
              value:
                '{"project":{"key":"{jira_project_key}"},"summary":"[SECURITY] {alert_title}","description":{"type":"doc","version":1,"content":[{"type":"paragraph","content":[{"type":"text","text":"{alert_description} — Component: {affected_component} — Action: {recommended_action}"}]}]},"issuetype":{"name":"Bug"},"priority":{"name":"Highest"}}',
            },
          ],
          variables: [
            { name: "key", customName: "incident_ticket_key", type: "string" },
          ],
        },
      }),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5"), e("4", "6")],
    summary: `**Security Alert Response**\n\n1. Security monitoring tool sends alert via webhook\n2. **AI reads the data** and extracts alert details + classifies severity\n3. Critical → #security Slack alert + email security team + incident ticket\n4. Non-critical → logged for review\n\n**To activate:** Say "connect openai", "connect slack", "connect email", "connect jira"`,
  },

  "SSL Certificate Monitor": {
    nodes: [
      trigger("Schedule: Every Monday at 9:00 AM", "schedule", {
        schedule: "Every Monday at 9:00 AM",
        howItStarts: "Checks your SSL certificate expiry status weekly",
        setup: "Activate this workflow and update the domain in the API node",
        inputFields: [
          { name: "domain", type: "string", example: "yourdomain.com" },
        ],
      }),
      n("2", "hitApi", "Check SSL certificate expiry", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://ssl-checker.io/api/v1/check/{domain}",
          headers: [],
          body: [],
          variables: [
            {
              name: "result.days_left",
              customName: "days_left",
              type: "number",
            },
            {
              name: "result.issuer",
              customName: "cert_issuer",
              type: "string",
            },
            {
              name: "result.valid_till",
              customName: "expiry_date",
              type: "string",
            },
          ],
        },
      }),
      aiNode(
        "3",
        "AI assess certificate status",
        520,
        'Assess this SSL certificate status. Determine if action is needed. Respond JSON: {status_summary: string, is_expiring_soon: \\"true\\" | \\"false\\", urgency: string, recommendation: string}',
        "Domain: {domain}, Days left: {days_left}, Issuer: {cert_issuer}, Expires: {expiry_date}",
        [
          { field: "status_summary", as: "status_summary", type: "string" },
          { field: "is_expiring_soon", as: "is_expiring_soon", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "recommendation", as: "recommendation", type: "string" },
        ],
      ),
      n("4", "condition", "Expiring within 30 days?", 760, {
        conditionData: [
          {
            variable: "is_expiring_soon",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "5",
        "Alert #devops",
        1000,
        "🔒 SSL Certificate Expiring!\\nDomain: {domain}\\nDays left: {days_left}\\nExpires: {expiry_date}\\n{recommendation}",
      ),
      emailNode(
        "6",
        "Email ops team",
        1000,
        "ops@company.com",
        "SSL Certificate Expiring: {domain} ({days_left} days left)",
        "<p><strong>SSL Certificate Alert</strong></p><p>Domain: {domain}</p><p>Days remaining: {days_left}</p><p>Expires: {expiry_date}</p><p>Issuer: {cert_issuer}</p><p>Recommendation: {recommendation}</p>",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("4", "6")],
    summary: `**SSL Certificate Monitor**\n\n1. Weekly check of SSL certificate expiry\n2. AI assesses status and urgency\n3. Expiring within 30 days → Slack alert + email ops team\n\n**To activate:** Say "connect openai", "connect slack", "connect email"`,
  },

  "API Health Check Dashboard": {
    nodes: [
      trigger("Schedule: Every 5 minutes", "schedule", {
        schedule: "Every 5 minutes, 24/7",
        howItStarts:
          "Pings your API endpoints every 5 minutes to monitor health",
        setup:
          "Activate this workflow and update the endpoint URLs in the API node",
        inputFields: [],
      }),
      n("2", "hitApi", "Ping API endpoints", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://your-api.com/health",
          headers: [{ key: "Authorization", value: "Bearer {api_key}" }],
          body: [],
          variables: [
            { name: "status", customName: "http_status", type: "number" },
            {
              name: "response_time_ms",
              customName: "response_time_ms",
              type: "number",
            },
          ],
        },
      }),
      aiNode(
        "3",
        "AI analyze response times",
        520,
        'Analyze this API health check result. Determine if any endpoints are slow (>500ms) or down. Respond JSON: {health_status: string, is_degraded: \\"true\\" | \\"false\\", response_summary: string, recommendation: string}',
        "HTTP Status: {http_status}, Response time: {response_time_ms}ms",
        [
          { field: "health_status", as: "health_status", type: "string" },
          { field: "is_degraded", as: "is_degraded", type: "string" },
          { field: "response_summary", as: "response_summary", type: "string" },
          { field: "recommendation", as: "recommendation", type: "string" },
        ],
      ),
      n("4", "condition", "Any slow or down?", 760, {
        conditionData: [
          {
            variable: "is_degraded",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "5",
        "Alert #ops",
        1000,
        "⚠️ API Health Degraded!\\nStatus: {health_status}\\nResponse time: {response_time_ms}ms\\n{response_summary}\\nAction: {recommendation}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5")],
    summary: `**API Health Check Dashboard**\n\n1. Pings your API every 5 minutes\n2. AI analyzes response times and status\n3. If slow or down → Slack alert with recommendation\n\n**To activate:** Update the endpoint URL, then say "connect openai" and "connect slack"`,
  },

  "Release Notes Generator": {
    nodes: [
      trigger("GitHub: New release published", "webhook_app", {
        source: "GitHub",
        event: "release.published",
        howItStarts: "A new release is published in your GitHub repository",
        setup:
          "In GitHub → repo Settings → Webhooks → Add webhook → URL: your workflow URL → Events: 'Releases'. The AI will read whatever data GitHub sends and extract what it needs.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract commits and changes",
        280,
        "Read this GitHub release webhook data and extract the release tag, release title, commit log, and repository name. Different GitHub webhook formats vary — find the release tag, title, commit details, and repo name regardless of field naming. Respond JSON: {release_tag: string, release_title: string, commit_log: string, repo_name: string}",
        "{webhook_payload}",
        [
          { field: "release_tag", as: "release_tag", type: "string" },
          { field: "release_title", as: "release_title", type: "string" },
          { field: "commit_log", as: "commit_log", type: "string" },
          { field: "repo_name", as: "repo_name", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "AI write release notes",
        520,
        "Write professional release notes from these commits. Group by: features, bug fixes, improvements. Make them user-friendly. Respond JSON: {release_notes_html: string, highlight: string, breaking_changes: string}",
        "Release: {release_tag} ({release_title})\\nRepo: {repo_name}\\nCommits: {commit_log}",
        [
          {
            field: "release_notes_html",
            as: "release_notes_html",
            type: "string",
          },
          { field: "highlight", as: "highlight", type: "string" },
          { field: "breaking_changes", as: "breaking_changes", type: "string" },
        ],
      ),
      emailNode(
        "4",
        "Email release notes to team",
        760,
        "team@company.com",
        "Release {release_tag}: {release_title}",
        "<h2>Release {release_tag} — {release_title}</h2><p><strong>Highlight:</strong> {highlight}</p>{release_notes_html}<p><strong>Breaking changes:</strong> {breaking_changes}</p>",
      ),
      slackNode(
        "5",
        "Post to #releases",
        760,
        "🚀 *Release {release_tag}* — {release_title}\\n{highlight}\\nBreaking changes: {breaking_changes}",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5")],
    summary: `**Release Notes Generator**\n\n1. GitHub release published via webhook\n2. **AI reads the data** and extracts release tag, title, commits\n3. AI writes user-friendly release notes (features, fixes, improvements)\n4. Emailed to team + posted to #releases on Slack\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  "Dependency Update Checker": {
    nodes: [
      trigger("Schedule: Every Monday at 8:00 AM", "schedule", {
        schedule: "Every Monday at 8:00 AM",
        howItStarts: "Checks for outdated npm/pip dependencies weekly",
        setup:
          "Activate this workflow and configure your package registry API endpoint in the next node",
        inputFields: [
          { name: "project_name", type: "string" },
          { name: "package_json_url", type: "string" },
        ],
      }),
      n("2", "hitApi", "Check outdated packages", 280, {
        hitApiDetails: {
          method: "GET",
          url: "{package_json_url}",
          headers: [{ key: "Authorization", value: "Bearer {repo_token}" }],
          body: [],
          variables: [
            {
              name: "dependencies",
              customName: "current_deps",
              type: "object",
            },
            { name: "devDependencies", customName: "dev_deps", type: "object" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI assess update risk",
        520,
        "Analyze these dependencies and identify which are outdated or have known vulnerabilities. Assess update risk for each. Respond JSON: {outdated_count: number, critical_updates: string, safe_updates: string, risk_assessment: string, update_recommendations: string}",
        "Project: {project_name}\\nDependencies: {current_deps}\\nDev dependencies: {dev_deps}",
        [
          { field: "outdated_count", as: "outdated_count", type: "number" },
          { field: "critical_updates", as: "critical_updates", type: "string" },
          { field: "safe_updates", as: "safe_updates", type: "string" },
          { field: "risk_assessment", as: "risk_assessment", type: "string" },
          {
            field: "update_recommendations",
            as: "update_recommendations",
            type: "string",
          },
        ],
      ),
      emailNode(
        "4",
        "Email dev team with recommendations",
        760,
        "dev-team@company.com",
        "Dependency Update Report: {project_name} — {outdated_count} outdated",
        "<h2>Dependency Update Report: {project_name}</h2><p><strong>Outdated packages:</strong> {outdated_count}</p><h3>Critical Updates</h3><p>{critical_updates}</p><h3>Safe Updates</h3><p>{safe_updates}</p><h3>Risk Assessment</h3><p>{risk_assessment}</p><h3>Recommendations</h3><p>{update_recommendations}</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4")],
    summary: `**Dependency Update Checker**\n\n1. Weekly check for outdated npm/pip packages\n2. AI assesses update risk and prioritizes\n3. Emails dev team with categorized recommendations\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "Infrastructure Cost Monitor": {
    nodes: [
      trigger("Schedule: Every day at 8:00 AM", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts: "Checks cloud infrastructure costs daily",
        setup:
          "Activate this workflow and say 'connect aws' to set up your AWS Cost Explorer API access",
        inputFields: [],
      }),
      n("2", "hitApi", "Get cloud costs from AWS", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://ce.us-east-1.amazonaws.com/",
          headers: [
            { key: "Authorization", value: "Bearer {aws_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "TimePeriod",
              value: '{"Start":"{yesterday_date}","End":"{today_date}"}',
            },
            { key: "Granularity", value: "DAILY" },
            { key: "Metrics", value: '["UnblendedCost"]' },
          ],
          variables: [
            {
              name: "ResultsByTime[0].Total.UnblendedCost.Amount",
              customName: "daily_cost",
              type: "string",
            },
            {
              name: "ResultsByTime[0].Total.UnblendedCost.Unit",
              customName: "currency",
              type: "string",
            },
          ],
        },
      }),
      aiNode(
        "3",
        "AI analyze cost trends",
        520,
        'Analyze today\'s cloud infrastructure cost. Compare to a typical daily average. Determine if there\'s a spending spike (>20% above normal). Respond JSON: {cost_summary: string, is_spike: \\"true\\" | \\"false\\", spike_percentage: number, cost_breakdown: string, recommendation: string}',
        "Daily cost: {daily_cost} {currency}",
        [
          { field: "cost_summary", as: "cost_summary", type: "string" },
          { field: "is_spike", as: "is_spike", type: "string" },
          { field: "spike_percentage", as: "spike_percentage", type: "number" },
          { field: "cost_breakdown", as: "cost_breakdown", type: "string" },
          { field: "recommendation", as: "recommendation", type: "string" },
        ],
      ),
      n("4", "condition", "Cost spike > 20%?", 760, {
        conditionData: [
          {
            variable: "is_spike",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "5",
        "Alert #infrastructure",
        1000,
        "💸 Cost Spike Alert!\\n{cost_summary}\\nSpike: {spike_percentage}% above normal\\n{cost_breakdown}\\nAction: {recommendation}",
      ),
      emailNode(
        "6",
        "Email finance team",
        1000,
        "finance@company.com",
        "Cloud Cost Spike: {spike_percentage}% above normal",
        "<p><strong>Cost Spike Detected</strong></p><p>{cost_summary}</p><p>Spike: {spike_percentage}% above normal</p><p>{cost_breakdown}</p><p>Recommendation: {recommendation}</p>",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("4", "6")],
    summary: `**Infrastructure Cost Monitor**\n\n1. Daily check of cloud infrastructure costs\n2. AI analyzes trends and detects spending spikes\n3. Spike > 20% → Slack alert + email finance team\n\n**To activate:** Say "connect aws", "connect openai", "connect slack", "connect email"`,
  },

  "Multi-Channel Support Inbox": {
    nodes: [
      trigger("Email: Support request received", "email", {
        howItStarts: "A customer sends a support email from any channel",
        setup:
          "Forward support emails to your workflow inbox, or connect your helpdesk (Zendesk, Freshdesk) webhook",
        inputFields: [
          { name: "sender_email", type: "string" },
          { name: "email_subject", type: "string" },
          { name: "email_body", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI classify channel + urgency + category",
        280,
        'Classify this support request. Determine the channel it came from, urgency level, and category. Respond JSON: {channel: string, urgency: string, category: string, is_urgent: \\"true\\" | \\"false\\", summary: string, suggested_response: string}',
        "From: {sender_email}\\nSubject: {email_subject}\\nBody: {email_body}",
        [
          { field: "channel", as: "channel", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "category", as: "category", type: "string" },
          { field: "is_urgent", as: "is_urgent", type: "string" },
          { field: "summary", as: "ticket_summary", type: "string" },
          {
            field: "suggested_response",
            as: "suggested_response",
            type: "string",
          },
        ],
      ),
      n("3", "condition", "Is it urgent?", 520, {
        conditionData: [
          {
            variable: "is_urgent",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "4",
        "Urgent → alert #support",
        760,
        "🚨 Urgent support ticket!\\nFrom: {sender_email}\\nChannel: {channel} | Category: {category}\\n{ticket_summary}\\n\\nSuggested: {suggested_response}",
      ),
      n("5", "hitApi", "Create helpdesk ticket", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.your-helpdesk.com/v2/tickets",
          headers: [
            { key: "Authorization", value: "Bearer {helpdesk_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "subject", value: "{email_subject}" },
            { key: "description", value: "{email_body}" },
            { key: "priority", value: "{urgency}" },
            { key: "category", value: "{category}" },
            { key: "requester_email", value: "{sender_email}" },
          ],
          variables: [{ name: "id", customName: "ticket_id", type: "string" }],
        },
      }),
      emailNode(
        "6",
        "Send acknowledgment to customer",
        1240,
        "{sender_email}",
        "Re: {email_subject} — We've received your request",
        "<p>Hi,</p><p>We've received your support request and created ticket #{ticket_id}. Our team will get back to you shortly.</p><p>Summary: {ticket_summary}</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("2", "5"), e("5", "6")],
    summary: `**Multi-Channel Support Inbox**\n\n1. Support email arrives\n2. AI classifies channel, urgency, and category\n3. Urgent → immediate Slack alert\n4. Ticket created in helpdesk\n5. Customer receives acknowledgment email\n\n**To activate:** Say "connect openai", "connect slack", "connect email"`,
  },

  "CSAT Survey After Ticket": {
    nodes: [
      trigger("Webhook: Support ticket closed", "webhook_app", {
        source: "Zendesk / Freshdesk / Intercom",
        event: "ticket.closed",
        howItStarts: "A support ticket is marked as closed in your helpdesk",
        setup:
          "In your helpdesk → Webhooks → Event: 'Ticket closed' → paste your workflow URL. The AI will read whatever data your helpdesk sends and extract what it needs.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the ticket data",
        180,
        "Read this helpdesk webhook data and extract the customer email, customer name, ticket subject, and resolution summary. Different helpdesk systems send data in different formats — find the customer email, customer name, ticket subject, and resolution summary regardless of field naming. Respond JSON: {customer_email: string, customer_name: string, ticket_subject: string, resolution_summary: string}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "ticket_subject", as: "ticket_subject", type: "string" },
          {
            field: "resolution_summary",
            as: "resolution_summary",
            type: "string",
          },
        ],
      ),
      n("3", "delayAction", "Wait 1 day", 380, {
        delayAction: { time: 1, timeFormat: "days" },
      }),
      emailNode(
        "4",
        "Send CSAT survey email",
        580,
        "{customer_email}",
        "How did we do? — Re: {ticket_subject}",
        '<p>Hi {customer_name},</p><p>Your ticket regarding "{ticket_subject}" was recently resolved.</p><p>How would you rate your experience? Reply with a number 1-5 (1=poor, 5=excellent).</p>',
      ),
      aiNode(
        "5",
        "AI classify CSAT response",
        780,
        'Classify this CSAT survey response. Extract the score and sentiment. Respond JSON: {csat_score: number, sentiment: string, is_low_score: \\"true\\" | \\"false\\", feedback_summary: string}',
        "Customer: {customer_name}, Original ticket: {ticket_subject}, Survey response: {email_body}",
        [
          { field: "csat_score", as: "csat_score", type: "number" },
          { field: "sentiment", as: "sentiment", type: "string" },
          { field: "is_low_score", as: "is_low_score", type: "string" },
          { field: "feedback_summary", as: "feedback_summary", type: "string" },
        ],
      ),
      n("6", "condition", "Score < 3?", 980, {
        conditionData: [
          {
            variable: "is_low_score",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "7",
        "Alert CS manager",
        1220,
        "⚠️ Low CSAT score ({csat_score}/5) from {customer_name}\\nTicket: {ticket_subject}\\nSentiment: {sentiment}\\nFeedback: {feedback_summary}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**CSAT Survey After Ticket**\n\n1. Support ticket closed via webhook\n2. **AI reads the data** and extracts customer info and ticket details\n3. 1-day delay → CSAT survey email sent\n4. AI classifies the response score\n5. Score < 3 → CS manager alerted on Slack\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  "Knowledge Base Suggester": {
    nodes: [
      trigger("Webhook: New support ticket created", "webhook_app", {
        source: "Zendesk / Freshdesk / Intercom",
        event: "ticket.created",
        howItStarts: "A new support ticket is created in your helpdesk",
        setup:
          "In your helpdesk → Webhooks → Event: 'Ticket created' → paste your workflow URL. The AI will read whatever data your helpdesk sends and extract what it needs.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI analyze question",
        280,
        "Read this support ticket webhook data and extract the ticket subject, ticket body, customer email, and agent email. Different helpdesk systems send data in different formats — find the ticket subject, ticket body, customer email, and assigned agent email regardless of field naming. Respond JSON: {ticket_subject: string, ticket_body: string, customer_email: string, agent_email: string, core_question: string}",
        "{webhook_payload}",
        [
          { field: "ticket_subject", as: "ticket_subject", type: "string" },
          { field: "ticket_body", as: "ticket_body", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "agent_email", as: "agent_email", type: "string" },
          { field: "core_question", as: "core_question", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "AI find matching KB articles",
        520,
        "Based on this support question, find the most relevant knowledge base articles. Suggest the top 3 articles with links and explain why each is relevant. Respond JSON: {matched_articles: string, match_confidence: number, suggested_reply_snippet: string}",
        "Customer question: {core_question}\\nTicket subject: {ticket_subject}\\nTicket body: {ticket_body}",
        [
          { field: "matched_articles", as: "matched_articles", type: "string" },
          { field: "match_confidence", as: "match_confidence", type: "number" },
          {
            field: "suggested_reply_snippet",
            as: "suggested_reply_snippet",
            type: "string",
          },
        ],
      ),
      emailNode(
        "4",
        "Email agent with suggestions",
        760,
        "{agent_email}",
        "KB Suggestions for: {ticket_subject}",
        "<h2>Suggested Knowledge Base Articles</h2><p>Customer question: {core_question}</p><h3>Matching Articles</h3><p>{matched_articles}</p><h3>Suggested Reply Snippet</h3><p>{suggested_reply_snippet}</p><p>Confidence: {match_confidence}%</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4")],
    summary: `**Knowledge Base Suggester**\n\n1. New support ticket created via webhook\n2. **AI reads the data** and extracts ticket details + identifies the core question\n3. AI finds matching KB articles and suggests reply snippets\n4. Suggestions emailed to assigned agent\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "Escalation Auto-Reassign": {
    nodes: [
      trigger("Schedule: Every hour", "schedule", {
        schedule: "Every hour",
        howItStarts:
          "Checks for stale support tickets that have breached SLA every hour",
        setup:
          "Activate this workflow and configure your helpdesk API endpoint in the next node",
        inputFields: [],
      }),
      n("2", "hitApi", "Get stale tickets", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.your-helpdesk.com/v2/tickets?status=open&sort_by=created_at&order=asc",
          headers: [
            { key: "Authorization", value: "Bearer {helpdesk_api_key}" },
          ],
          variables: [
            { name: "tickets", customName: "open_tickets", type: "array" },
            { name: "count", customName: "ticket_count", type: "number" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI identify breached SLA",
        520,
        'Analyze these open tickets and identify any that have breached their SLA (response time > 4 hours or resolution time > 24 hours). Respond JSON: {stale_count: number, has_stale: \\"true\\" | \\"false\\", stale_tickets_summary: string, recommended_assignees: string}',
        "Open tickets: {open_tickets}, Total: {ticket_count}",
        [
          { field: "stale_count", as: "stale_count", type: "number" },
          { field: "has_stale", as: "has_stale", type: "string" },
          {
            field: "stale_tickets_summary",
            as: "stale_tickets_summary",
            type: "string",
          },
          {
            field: "recommended_assignees",
            as: "recommended_assignees",
            type: "string",
          },
        ],
      ),
      n("4", "condition", "Any stale tickets?", 760, {
        conditionData: [
          {
            variable: "has_stale",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      n("5", "hitApi", "Reassign stale tickets", 1000, {
        hitApiDetails: {
          method: "PUT",
          url: "https://api.your-helpdesk.com/v2/tickets/bulk-update",
          headers: [
            { key: "Authorization", value: "Bearer {helpdesk_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "action", value: "reassign" },
            { key: "tickets", value: "{stale_tickets_summary}" },
          ],
          variables: [
            {
              name: "updated_count",
              customName: "reassigned_count",
              type: "number",
            },
          ],
        },
      }),
      slackNode(
        "6",
        "Notify new owners",
        1240,
        "🔄 SLA Breach — {stale_count} tickets auto-reassigned\\n{stale_tickets_summary}\\nRecommended assignees: {recommended_assignees}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
    summary: `**Escalation Auto-Reassign**\n\n1. Hourly check for stale/breached SLA tickets\n2. AI identifies tickets past SLA thresholds\n3. If any stale → auto-reassigns tickets\n4. New owners notified on Slack\n\n**To activate:** Say "connect openai" and "connect slack"`,
  },

  "Customer Feedback to Feature Request": {
    nodes: [
      trigger("Form: Feedback submitted", "webhook_form", {
        source: "Outgrow Forms / Feedback Widget",
        howItStarts: "A customer submits feedback via your feedback form",
        setup:
          "In your feedback form → Integrations → Webhooks → paste your workflow URL. The AI will read whatever data your form sends and extract what it needs.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract + classify feedback",
        280,
        'Read this feedback form submission and extract the feedback text, customer email, and customer name. Then classify: is this a feature request, bug report, general feedback, or complaint? Respond JSON: {feedback_text: string, customer_email: string, customer_name: string, classification: string, is_feature_request: \\"true\\" | \\"false\\", feature_title: string, feature_description: string}',
        "{webhook_payload}",
        [
          { field: "feedback_text", as: "feedback_text", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "classification", as: "classification", type: "string" },
          {
            field: "is_feature_request",
            as: "is_feature_request",
            type: "string",
          },
          { field: "feature_title", as: "feature_title", type: "string" },
          {
            field: "feature_description",
            as: "feature_description",
            type: "string",
          },
        ],
      ),
      n("3", "condition", "Is it a feature request?", 520, {
        conditionData: [
          {
            variable: "is_feature_request",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      n("4", "hitApi", "Create product board item", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.productboard.com/features",
          headers: [
            { key: "Authorization", value: "Bearer {productboard_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "{feature_title}" },
            {
              key: "description",
              value:
                "{feature_description}\\n\\nRequested by: {customer_name} ({customer_email})\\nOriginal feedback: {feedback_text}",
            },
          ],
          variables: [{ name: "id", customName: "feature_id", type: "string" }],
        },
      }),
      slackNode(
        "5",
        "Notify #product",
        1000,
        "💡 New feature request from {customer_name}!\\n*{feature_title}*\\n{feature_description}\\nProductBoard: {feature_id}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5")],
    summary: `**Customer Feedback to Feature Request**\n\n1. Feedback submitted via form\n2. **AI reads the data** and classifies it (feature request, bug, feedback, complaint)\n3. Feature requests → automatically created in ProductBoard\n4. #product notified on Slack\n\n**To activate:** Say "connect openai", "connect productboard", "connect slack"`,
  },

  "Support to Bug Report": {
    nodes: [
      trigger("Webhook: Ticket tagged as bug", "webhook_app", {
        source: "Zendesk / Freshdesk / Intercom",
        event: "ticket.tagged",
        howItStarts: "A support ticket is tagged as a bug in your helpdesk",
        setup:
          "In your helpdesk → Webhooks → Event: 'Ticket tagged' (filter: tag = 'bug') → paste your workflow URL. The AI will read whatever data your helpdesk sends and extract what it needs.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract bug details",
        280,
        "Read this support ticket webhook data and extract the bug description, steps to reproduce, expected behavior, actual behavior, customer email, and ticket ID. Different helpdesk systems send data in different formats — find these details regardless of field naming. Respond JSON: {bug_title: string, bug_description: string, steps_to_reproduce: string, expected_behavior: string, actual_behavior: string, customer_email: string, ticket_id: string}",
        "{webhook_payload}",
        [
          { field: "bug_title", as: "bug_title", type: "string" },
          { field: "bug_description", as: "bug_description", type: "string" },
          {
            field: "steps_to_reproduce",
            as: "steps_to_reproduce",
            type: "string",
          },
          {
            field: "expected_behavior",
            as: "expected_behavior",
            type: "string",
          },
          { field: "actual_behavior", as: "actual_behavior", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "ticket_id", as: "ticket_id", type: "string" },
        ],
      ),
      n("3", "hitApi", "Create Jira bug", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/issue",
          headers: [
            { key: "Authorization", value: "Basic {jira_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "fields",
              value:
                '{"project":{"key":"{jira_project_key}"},"summary":"[BUG] {bug_title}","description":{"type":"doc","version":1,"content":[{"type":"paragraph","content":[{"type":"text","text":"Description: {bug_description}\\nSteps: {steps_to_reproduce}\\nExpected: {expected_behavior}\\nActual: {actual_behavior}\\nSupport ticket: {ticket_id}"}]}]},"issuetype":{"name":"Bug"}}',
            },
          ],
          variables: [
            { name: "key", customName: "jira_bug_key", type: "string" },
          ],
        },
      }),
      slackNode(
        "4",
        "Notify #engineering",
        760,
        "🐛 New bug from support!\\n*{bug_title}*\\n{bug_description}\\nJira: {jira_bug_key} | Support ticket: {ticket_id}",
      ),
      emailNode(
        "5",
        "Acknowledge to customer",
        760,
        "{customer_email}",
        "Bug report received — {bug_title}",
        "<p>Hi,</p><p>Thank you for reporting this issue. Our engineering team is now investigating.</p><p>Bug: {bug_title}</p><p>We'll keep you updated on the fix.</p>",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5")],
    summary: `**Support to Bug Report**\n\n1. Support ticket tagged as bug via webhook\n2. **AI reads the data** and extracts bug details, steps to reproduce, etc.\n3. Jira bug created automatically\n4. #engineering notified on Slack\n5. Customer receives acknowledgment email\n\n**To activate:** Say "connect openai", "connect jira", "connect slack", "connect email"`,
  },

  "VIP Customer Alert": {
    nodes: [
      trigger("Webhook: Customer event received", "webhook_app", {
        source: "Your App / CRM / Helpdesk",
        howItStarts:
          "Your app or CRM sends a customer event (support request, login, purchase)",
        setup:
          "In your app/CRM → Webhooks → paste your workflow URL for customer events. The AI will read whatever data your system sends and extract what it needs.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract customer + check VIP",
        280,
        'Read this customer event webhook data and extract the customer name, email, event type, and any VIP/tier/plan indicator. Determine if this is a VIP customer (enterprise plan, high-value account, or explicitly marked VIP). Respond JSON: {customer_name: string, customer_email: string, event_type: string, account_tier: string, is_vip: \\"true\\" | \\"false\\", event_summary: string}',
        "{webhook_payload}",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "event_type", as: "event_type", type: "string" },
          { field: "account_tier", as: "account_tier", type: "string" },
          { field: "is_vip", as: "is_vip", type: "string" },
          { field: "event_summary", as: "event_summary", type: "string" },
        ],
      ),
      n("3", "condition", "Is VIP customer?", 520, {
        conditionData: [
          {
            variable: "is_vip",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "4",
        "Alert #vip-support",
        760,
        "⭐ VIP Customer Alert!\\n{customer_name} ({account_tier})\\nEvent: {event_type}\\n{event_summary}",
      ),
      emailNode(
        "5",
        "Email account manager",
        760,
        "account-managers@company.com",
        "VIP Alert: {customer_name} — {event_type}",
        "<p><strong>VIP Customer Activity</strong></p><p>Customer: {customer_name} ({customer_email})</p><p>Account tier: {account_tier}</p><p>Event: {event_type}</p><p>{event_summary}</p>",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5")],
    summary: `**VIP Customer Alert**\n\n1. Customer event arrives via webhook\n2. **AI reads the data** and extracts customer info + determines VIP status\n3. VIP customer → #vip-support Slack alert + email account manager\n\n**To activate:** Say "connect openai", "connect slack", "connect email"`,
  },

  "Airtable Notion Two-Way Sync": {
    nodes: [
      trigger("Webhook: Airtable record updated", "webhook_app", {
        source: "Airtable",
        event: "record.updated",
        howItStarts: "A record is created or updated in your Airtable base",
        setup:
          "In Airtable → Automations → Trigger: 'When record matches conditions' → Action: 'Send webhook' → paste your workflow URL. The AI will read whatever data Airtable sends and extract what it needs.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract Airtable fields",
        280,
        "Read this Airtable webhook data and extract the record ID, record name/title, status, description, and any other key fields. Different Airtable bases send data in different formats — find the record ID, record name, status, and description regardless of field naming. Respond JSON: {record_id: string, record_name: string, record_status: string, record_description: string, record_fields_json: string}",
        "{webhook_payload}",
        [
          { field: "record_id", as: "record_id", type: "string" },
          { field: "record_name", as: "record_name", type: "string" },
          { field: "record_status", as: "record_status", type: "string" },
          {
            field: "record_description",
            as: "record_description",
            type: "string",
          },
          {
            field: "record_fields_json",
            as: "record_fields_json",
            type: "string",
          },
        ],
      ),
      n("3", "hitApi", "Update Notion page", 520, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.notion.com/v1/pages/{notion_page_id}",
          headers: [
            { key: "Authorization", value: "Bearer {notion_token}" },
            { key: "Content-Type", value: "application/json" },
            { key: "Notion-Version", value: "2022-06-28" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"Name":{"title":[{"text":{"content":"{record_name}"}}]},"Status":{"select":{"name":"{record_status}"}},"Description":{"rich_text":[{"text":{"content":"{record_description}"}}]}}',
            },
          ],
          variables: [
            {
              name: "id",
              customName: "notion_page_id_updated",
              type: "string",
            },
          ],
        },
      }),
      slackNode(
        "4",
        "Confirm sync",
        760,
        "🔄 Airtable → Notion synced!\\nRecord: {record_name}\\nStatus: {record_status}\\nNotion page: {notion_page_id_updated}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4")],
    summary: `**Airtable Notion Two-Way Sync**\n\n1. Airtable record updated via webhook\n2. **AI reads the data** and extracts record fields\n3. Updates corresponding Notion page\n4. Sync confirmed on Slack\n\n**To activate:** Say "connect openai", "connect notion", "connect slack"`,
  },

  "Google Sheets Email Report": {
    nodes: [
      trigger("Schedule: Every day at 8:00 AM", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts:
          "Reads data from Google Sheets and sends a summary report daily",
        setup:
          "Activate this workflow and configure your spreadsheet ID and range in the API node",
        inputFields: [
          { name: "spreadsheet_id", type: "string" },
          { name: "report_email", type: "string" },
        ],
      }),
      n("2", "hitApi", "Read Google Sheets data", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Sheet1!A1:Z100",
          headers: [{ key: "Authorization", value: "Bearer {google_token}" }],
          body: [],
          variables: [
            { name: "values", customName: "sheet_data", type: "array" },
            { name: "range", customName: "data_range", type: "string" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI summarize into report",
        520,
        "Analyze this spreadsheet data and create a daily summary report. Highlight key metrics, trends, and anomalies. Respond JSON: {report_title: string, executive_summary: string, key_metrics: string, trends: string, anomalies: string}",
        "Spreadsheet data: {sheet_data}\\nRange: {data_range}",
        [
          { field: "report_title", as: "report_title", type: "string" },
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "key_metrics", as: "key_metrics", type: "string" },
          { field: "trends", as: "trends", type: "string" },
          { field: "anomalies", as: "anomalies", type: "string" },
        ],
      ),
      emailNode(
        "4",
        "Email daily report",
        760,
        "{report_email}",
        "{report_title}",
        "<h2>{report_title}</h2><h3>Executive Summary</h3><p>{executive_summary}</p><h3>Key Metrics</h3><p>{key_metrics}</p><h3>Trends</h3><p>{trends}</p><h3>Anomalies</h3><p>{anomalies}</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4")],
    summary: `**Google Sheets Email Report**\n\n1. Daily read of Google Sheets data\n2. AI summarizes into an executive report with metrics, trends, and anomalies\n3. Emailed to recipients\n\n**To activate:** Say "connect google sheets", "connect openai", "connect email"`,
  },

  "Webhook Transform Forward": {
    nodes: [
      trigger("Webhook: Any app event", "webhook_app", {
        source: "Any App",
        howItStarts:
          "Any external app sends an event to your webhook URL for transformation and forwarding",
        setup:
          "Copy your workflow webhook URL and paste it in the source app. Configure the destination URL in the API node.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract + transform data",
        280,
        "Read this incoming webhook payload and transform it into the required format for the destination API. Extract all relevant fields and restructure them. Respond JSON: {transformed_payload: string, source_app: string, event_type: string, field_count: number}",
        "{webhook_payload}",
        [
          {
            field: "transformed_payload",
            as: "transformed_payload",
            type: "string",
          },
          { field: "source_app", as: "source_app", type: "string" },
          { field: "event_type", as: "event_type", type: "string" },
          { field: "field_count", as: "field_count", type: "number" },
        ],
      ),
      n("3", "hitApi", "Forward to destination", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://destination-api.com/webhook",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "Authorization", value: "Bearer {destination_api_key}" },
          ],
          body: [{ key: "data", value: "{transformed_payload}" }],
          variables: [
            { name: "status", customName: "forward_status", type: "string" },
            { name: "id", customName: "forward_id", type: "string" },
          ],
        },
      }),
      slackNode(
        "4",
        "Log to Slack",
        760,
        "🔀 Webhook forwarded!\\nSource: {source_app} ({event_type})\\nFields: {field_count}\\nDestination status: {forward_status}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4")],
    summary: `**Webhook Transform Forward**\n\n1. Any app sends data via webhook\n2. **AI reads the data** and transforms it to the destination format\n3. Forwards to destination API\n4. Logged to Slack for visibility\n\n**To activate:** Say "connect openai" and "connect slack"`,
  },

  "Data Deduplication Pipeline": {
    nodes: [
      trigger("Manual: Submit dataset for deduplication", "manual", {
        howItStarts:
          "You provide a dataset (CSV text, JSON, or list) for deduplication",
        setup:
          "Paste your dataset in the chat or POST via API with {dataset: '...', requester_email: '...'}",
        inputFields: [
          { name: "dataset", type: "string" },
          { name: "requester_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI identify duplicates",
        280,
        "Analyze this dataset and identify duplicate or near-duplicate entries. Group duplicates together and recommend which to keep. Respond JSON: {total_records: number, duplicate_count: number, unique_count: number, duplicate_groups: string, clean_dataset_summary: string, dedup_recommendations: string}",
        "Dataset: {dataset}",
        [
          { field: "total_records", as: "total_records", type: "number" },
          { field: "duplicate_count", as: "duplicate_count", type: "number" },
          { field: "unique_count", as: "unique_count", type: "number" },
          { field: "duplicate_groups", as: "duplicate_groups", type: "string" },
          {
            field: "clean_dataset_summary",
            as: "clean_dataset_summary",
            type: "string",
          },
          {
            field: "dedup_recommendations",
            as: "dedup_recommendations",
            type: "string",
          },
        ],
      ),
      emailNode(
        "3",
        "Email clean dataset report",
        520,
        "{requester_email}",
        "Deduplication Report: {duplicate_count} duplicates found in {total_records} records",
        "<h2>Data Deduplication Report</h2><p>Total records: {total_records}</p><p>Duplicates found: {duplicate_count}</p><p>Unique records: {unique_count}</p><h3>Duplicate Groups</h3><p>{duplicate_groups}</p><h3>Recommendations</h3><p>{dedup_recommendations}</p><h3>Clean Dataset Summary</h3><p>{clean_dataset_summary}</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3")],
    summary: `**Data Deduplication Pipeline**\n\n1. Submit a dataset (CSV, JSON, or list)\n2. AI identifies duplicates and near-duplicates\n3. Clean dataset report emailed with recommendations\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "ETL Pipeline": {
    nodes: [
      trigger("Schedule: Every day at 2:00 AM", "schedule", {
        schedule: "Every day at 2:00 AM",
        howItStarts: "Runs a daily ETL (Extract, Transform, Load) pipeline",
        setup:
          "Activate this workflow and configure the source and destination API endpoints in the nodes",
        inputFields: [{ name: "etl_report_email", type: "string" }],
      }),
      n("2", "hitApi", "Extract data from source", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://source-api.com/v1/data/export?date={yesterday_date}",
          headers: [{ key: "Authorization", value: "Bearer {source_api_key}" }],
          body: [],
          variables: [
            { name: "data", customName: "raw_data", type: "array" },
            { name: "count", customName: "record_count", type: "number" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI transform data",
        520,
        "Transform this raw data for the destination system. Clean, normalize, and restructure the data. Remove invalid entries. Respond JSON: {transformed_data: string, records_processed: number, records_cleaned: number, records_dropped: number, transform_summary: string}",
        "Raw data ({record_count} records): {raw_data}",
        [
          { field: "transformed_data", as: "transformed_data", type: "string" },
          {
            field: "records_processed",
            as: "records_processed",
            type: "number",
          },
          { field: "records_cleaned", as: "records_cleaned", type: "number" },
          { field: "records_dropped", as: "records_dropped", type: "number" },
          {
            field: "transform_summary",
            as: "transform_summary",
            type: "string",
          },
        ],
      ),
      n("4", "hitApi", "Load to destination", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://destination-api.com/v1/data/import",
          headers: [
            { key: "Authorization", value: "Bearer {destination_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "data", value: "{transformed_data}" }],
          variables: [
            {
              name: "imported_count",
              customName: "imported_count",
              type: "number",
            },
            { name: "status", customName: "import_status", type: "string" },
          ],
        },
      }),
      emailNode(
        "5",
        "Email completion report",
        1000,
        "{etl_report_email}",
        "ETL Pipeline Complete: {records_processed} records processed",
        "<h2>ETL Pipeline Report</h2><p>Extracted: {record_count} records</p><p>Processed: {records_processed}</p><p>Cleaned: {records_cleaned}</p><p>Dropped: {records_dropped}</p><p>Imported: {imported_count}</p><p>Status: {import_status}</p><h3>Summary</h3><p>{transform_summary}</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5")],
    summary: `**ETL Pipeline**\n\n1. Daily extraction from source API\n2. AI transforms, cleans, and normalizes data\n3. Loads into destination system\n4. Completion report emailed\n\n**To activate:** Say "connect openai" and "connect email"`,
  },

  "Daily Briefing Email": {
    nodes: [
      trigger("Schedule: Every day at 7:00 AM", "schedule", {
        schedule: "Every day at 7:00 AM",
        howItStarts:
          "Compiles your daily briefing from weather, calendar, and news every morning",
        setup:
          "Activate this workflow and configure your location, calendar API, and preferred news topics",
        inputFields: [
          { name: "location", type: "string", example: "New York" },
          { name: "user_email", type: "string" },
          {
            name: "news_topics",
            type: "string",
            example: "technology, business",
          },
        ],
      }),
      n("2", "hitApi", "Get weather + calendar + news", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.openweathermap.org/data/2.5/weather?q={location}&appid={openweather_key}&units=imperial",
          headers: [],
          body: [],
          variables: [
            { name: "main.temp", customName: "temperature", type: "number" },
            {
              name: "weather[0].description",
              customName: "weather_description",
              type: "string",
            },
            { name: "name", customName: "city_name", type: "string" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI compile briefing",
        520,
        "Compile a morning briefing email. Include weather summary, suggested outfit, and a motivational start to the day. Respond JSON: {briefing_html: string, weather_summary: string, top_headline: string, day_tip: string}",
        "Location: {city_name}, Temperature: {temperature}F, Weather: {weather_description}, News topics: {news_topics}",
        [
          { field: "briefing_html", as: "briefing_html", type: "string" },
          { field: "weather_summary", as: "weather_summary", type: "string" },
          { field: "top_headline", as: "top_headline", type: "string" },
          { field: "day_tip", as: "day_tip", type: "string" },
        ],
      ),
      emailNode(
        "4",
        "Email daily briefing",
        760,
        "{user_email}",
        "Good Morning! {weather_summary} — {top_headline}",
        "{briefing_html}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4")],
    summary: `**Daily Briefing Email**\n\n1. Runs every morning at 7 AM\n2. Fetches weather data for your location\n3. AI compiles a personalized morning briefing\n4. Emailed to you with weather, headlines, and a day tip\n\n**To activate:** Say "connect openweathermap", "connect openai", "connect email"`,
  },

  "Read Later AI Digest": {
    nodes: [
      trigger("Schedule: Every Sunday at 9:00 AM", "schedule", {
        schedule: "Every Sunday at 9:00 AM",
        howItStarts: "Compiles a weekly digest of your saved articles",
        setup:
          "Activate this workflow and connect your read-later service (Pocket, Instapaper) API",
        inputFields: [{ name: "user_email", type: "string" }],
      }),
      n("2", "hitApi", "Get saved articles from Pocket", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://getpocket.com/v3/get",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "consumer_key", value: "{pocket_consumer_key}" },
            { key: "access_token", value: "{pocket_access_token}" },
            { key: "count", value: "10" },
            { key: "sort", value: "newest" },
            { key: "detailType", value: "complete" },
          ],
          variables: [
            { name: "list", customName: "saved_articles", type: "object" },
            { name: "status", customName: "pocket_status", type: "number" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI summarize top 5 articles",
        520,
        "From these saved articles, pick the top 5 most interesting and summarize each in 2-3 sentences. Respond JSON: {digest_html: string, article_count: number, top_topics: string, reading_time_estimate: string}",
        "Saved articles: {saved_articles}",
        [
          { field: "digest_html", as: "digest_html", type: "string" },
          { field: "article_count", as: "article_count", type: "number" },
          { field: "top_topics", as: "top_topics", type: "string" },
          {
            field: "reading_time_estimate",
            as: "reading_time_estimate",
            type: "string",
          },
        ],
      ),
      emailNode(
        "4",
        "Email weekly digest",
        760,
        "{user_email}",
        "Your Weekly Read-Later Digest — {article_count} articles on {top_topics}",
        "<h2>Weekly Article Digest</h2><p>Top topics: {top_topics}</p><p>Estimated reading time: {reading_time_estimate}</p>{digest_html}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4")],
    summary: `**Read Later AI Digest**\n\n1. Weekly pull of saved articles from Pocket\n2. AI picks top 5 and summarizes each\n3. Digest emailed every Sunday morning\n\n**To activate:** Say "connect pocket", "connect openai", "connect email"`,
  },

  "Smart Bookmark Organizer": {
    nodes: [
      trigger("Form: New bookmark submitted", "webhook_form", {
        source: "Bookmarklet / Browser Extension / Form",
        howItStarts: "You save a new bookmark via form or browser extension",
        setup:
          "Set up a bookmarklet or form that POSTs to your workflow URL when you save a URL. The AI will read whatever data your form sends and extract what it needs.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract URL + content",
        280,
        "Read this bookmark submission and extract the URL and any title or description. Respond JSON: {bookmark_url: string, bookmark_title: string, bookmark_description: string}",
        "{webhook_payload}",
        [
          { field: "bookmark_url", as: "bookmark_url", type: "string" },
          { field: "bookmark_title", as: "bookmark_title", type: "string" },
          {
            field: "bookmark_description",
            as: "bookmark_description",
            type: "string",
          },
        ],
      ),
      aiNode(
        "3",
        "AI categorize + tag",
        520,
        "Based on this URL and its content, categorize it and assign relevant tags. Categories: [tech, business, design, science, health, finance, entertainment, education, other]. Respond JSON: {category: string, tags: string, summary: string, read_time_estimate: string}",
        "URL: {bookmark_url}\\nTitle: {bookmark_title}\\nDescription: {bookmark_description}",
        [
          { field: "category", as: "category", type: "string" },
          { field: "tags", as: "tags", type: "string" },
          { field: "summary", as: "bookmark_summary", type: "string" },
          {
            field: "read_time_estimate",
            as: "read_time_estimate",
            type: "string",
          },
        ],
      ),
      n("4", "hitApi", "Log to Google Sheets", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Bookmarks:append?valueInputOption=RAW",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{bookmark_url}","{bookmark_title}","{category}","{tags}","{bookmark_summary}","{read_time_estimate}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_added",
              type: "number",
            },
          ],
        },
      }),
      emailNode(
        "5",
        "Email confirmation",
        760,
        "{user_email}",
        "Bookmark saved: {bookmark_title}",
        '<p>Saved: <a href="{bookmark_url}">{bookmark_title}</a></p><p>Category: {category}</p><p>Tags: {tags}</p><p>Summary: {bookmark_summary}</p><p>Read time: {read_time_estimate}</p>',
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("3", "5")],
    summary: `**Smart Bookmark Organizer**\n\n1. Save a bookmark via form/extension\n2. **AI reads the data** and extracts URL + content\n3. AI categorizes and tags the bookmark\n4. Logged to Google Sheets + confirmation emailed\n\n**To activate:** Say "connect openai", "connect google sheets", "connect email"`,
  },

  "Weekly Goal Tracker": {
    nodes: [
      trigger("Schedule: Every Monday at 8:00 AM", "schedule", {
        schedule: "Every Monday at 8:00 AM",
        howItStarts:
          "Weekly check of your goals and progress every Monday morning",
        setup:
          "Activate this workflow and configure your goals spreadsheet or data source",
        inputFields: [
          { name: "goals_data", type: "string" },
          { name: "user_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI check goals vs progress",
        280,
        "Analyze the current goals and progress data. For each goal, assess completion percentage and whether it's on track. Provide an accountability summary. Respond JSON: {goals_summary: string, on_track_count: number, behind_count: number, completed_count: number, accountability_report: string, weekly_focus: string}",
        "Goals and progress: {goals_data}",
        [
          { field: "goals_summary", as: "goals_summary", type: "string" },
          { field: "on_track_count", as: "on_track_count", type: "number" },
          { field: "behind_count", as: "behind_count", type: "number" },
          { field: "completed_count", as: "completed_count", type: "number" },
          {
            field: "accountability_report",
            as: "accountability_report",
            type: "string",
          },
          { field: "weekly_focus", as: "weekly_focus", type: "string" },
        ],
      ),
      emailNode(
        "3",
        "Email accountability report",
        520,
        "{user_email}",
        "Weekly Goals Report: {completed_count} done, {behind_count} behind",
        "<h2>Weekly Goal Tracker</h2><h3>Status</h3><p>On track: {on_track_count} | Behind: {behind_count} | Completed: {completed_count}</p><h3>Details</h3><p>{goals_summary}</p><h3>Accountability</h3><p>{accountability_report}</p><h3>This Week's Focus</h3><p>{weekly_focus}</p>",
      ),
      slackNode(
        "4",
        "Post reminder",
        520,
        "🎯 Weekly goal check-in!\\nCompleted: {completed_count} | On track: {on_track_count} | Behind: {behind_count}\\nFocus this week: {weekly_focus}",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("2", "4")],
    summary: `**Weekly Goal Tracker**\n\n1. Runs every Monday at 8 AM\n2. AI reviews goals vs progress and assesses completion\n3. Accountability report emailed + Slack reminder\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  "Habit Tracker with Streaks": {
    nodes: [
      trigger("Form: Daily habit check-in", "webhook_form", {
        source: "Daily Check-in Form / Slack Bot",
        howItStarts: "You submit your daily habit check-in via form",
        setup:
          "Create a daily check-in form (Outgrow Forms, Outgrow Forms) → Webhooks → paste your workflow URL. The AI will read whatever data your form sends and extract what it needs.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract + calculate streak",
        280,
        'Read this daily habit check-in form submission and extract the habits completed, date, and any notes. Calculate the current streak count based on consecutive days. Respond JSON: {habits_completed: string, check_in_date: string, streak_count: number, is_milestone: \\"true\\" | \\"false\\", milestone_type: string, notes: string, encouragement: string}',
        "{webhook_payload}",
        [
          { field: "habits_completed", as: "habits_completed", type: "string" },
          { field: "check_in_date", as: "check_in_date", type: "string" },
          { field: "streak_count", as: "streak_count", type: "number" },
          { field: "is_milestone", as: "is_milestone", type: "string" },
          { field: "milestone_type", as: "milestone_type", type: "string" },
          { field: "notes", as: "notes", type: "string" },
          { field: "encouragement", as: "encouragement", type: "string" },
        ],
      ),
      n("3", "condition", "Streak milestone?", 520, {
        conditionData: [
          {
            variable: "is_milestone",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "4",
        "Celebrate milestone!",
        760,
        "🔥 Streak Milestone! {streak_count} days in a row!\\n{milestone_type}\\nHabits: {habits_completed}\\n{encouragement}",
      ),
      emailNode(
        "5",
        "Email weekly summary",
        760,
        "{user_email}",
        "Habit Tracker: {streak_count}-day streak! {encouragement}",
        "<h2>Habit Tracker Update</h2><p>Date: {check_in_date}</p><p>Current streak: {streak_count} days</p><p>Habits completed: {habits_completed}</p><p>Notes: {notes}</p><p>{encouragement}</p>",
        460,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("2", "5")],
    summary: `**Habit Tracker with Streaks**\n\n1. Daily habit check-in via form\n2. **AI reads the data** and extracts habits + calculates streak\n3. Milestone reached → Slack celebration\n4. Weekly summary emailed\n\n**To activate:** Say "connect openai", "connect slack", "connect email"`,
  },

  "Recipe Meal Planner": {
    nodes: [
      trigger("Manual: Submit meal plan preferences", "manual", {
        howItStarts:
          "You provide your dietary preferences and needs for the week",
        setup:
          "Type your preferences in chat: dietary restrictions, cuisine preferences, number of people, budget. Or POST via API",
        inputFields: [
          {
            name: "dietary_needs",
            type: "string",
            example: "vegetarian, gluten-free",
          },
          {
            name: "cuisine_preferences",
            type: "string",
            example: "Mediterranean, Asian",
          },
          { name: "servings", type: "number", example: "4" },
          { name: "budget", type: "string", example: "$100/week" },
          { name: "user_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI generate weekly meal plan + grocery list",
        280,
        "Generate a weekly meal plan (7 dinners) based on the dietary needs and preferences. Include a consolidated grocery list with estimated quantities. Respond JSON: {meal_plan_html: string, grocery_list: string, estimated_cost: string, nutritional_highlights: string, prep_tips: string, meal_count: number}",
        "Dietary needs: {dietary_needs}, Cuisine: {cuisine_preferences}, Servings: {servings}, Budget: {budget}",
        [
          { field: "meal_plan_html", as: "meal_plan_html", type: "string" },
          { field: "grocery_list", as: "grocery_list", type: "string" },
          { field: "estimated_cost", as: "estimated_cost", type: "string" },
          {
            field: "nutritional_highlights",
            as: "nutritional_highlights",
            type: "string",
          },
          { field: "prep_tips", as: "prep_tips", type: "string" },
          { field: "meal_count", as: "meal_count", type: "number" },
        ],
      ),
      emailNode(
        "3",
        "Email meal plan",
        520,
        "{user_email}",
        "Your Weekly Meal Plan ({meal_count} meals, est. {estimated_cost})",
        "<h2>Weekly Meal Plan</h2><p>Diet: {dietary_needs} | Cuisine: {cuisine_preferences} | Servings: {servings}</p>{meal_plan_html}<h3>Grocery List</h3><p>{grocery_list}</p><p>Estimated cost: {estimated_cost}</p><h3>Nutritional Highlights</h3><p>{nutritional_highlights}</p><h3>Prep Tips</h3><p>{prep_tips}</p>",
      ),
    ],
    edges: [e("1", "2"), e("2", "3")],
    summary: `**Recipe Meal Planner**\n\n1. Provide dietary preferences, cuisine, servings, budget\n2. AI generates a 7-day meal plan + consolidated grocery list\n3. Plan emailed with nutritional highlights and prep tips\n\n**To activate:** Say "connect openai" and "connect email"`,
  },
};
